/**
 * The playing screen.
 *
 * React owns the canvas element and the panels around it. It does not own a single
 * frame of the world: the Game class runs its own loop and pushes only the handful
 * of values the interface actually shows.
 */

import {
  ContainerId,
  EQUIPMENT_SLOT_NAMES,
  TOOLBELT_SLOTS,
  INVENTORY_SIZE,
  MAX_RUN_ENERGY,
  Role,
  atLeast,
  toRole,
  type ItemStack,
  type RoleValue,
  SKILL_NAMES,
  combatLevel as combatLevelOf,
  Skill,
  BANK_SIZE,
} from '@inverse/common';
import {
  AskKind,
  BONUS_FIELDS,
  ChatScope,
  type ChatScopeValue,
  TradeAct,
  PartyAct,
  type PartyWire,
  ExchangeAct,
  type ExchangeWire,
  type GuildWire,
  type TradeStatePacket,
  type BonusesPacket,
  FriendAct,
  type DropTablePacket,
  type GatheringPacket,
  type DialoguePacket,
  type ProgressPacket,
  type ModelWire,
  type RecipeWire,
  type QuestWire,
  type WorldProblem,
  type WorldCheckPacket,
  type FriendsPacket,
  type SpawnWire,
  type ZoneWire,
  type ItemDefinitionWire,
  type NpcDefinitionWire,
  type ObjectDefinitionWire,
  type ShopStatePacket,
  type ShopStockPacket,
  RejectReason,
  type TimeOfDayPacket,
  EntityKind,
  type DevotionPacket,
  type SpellbookWire,
  type CollectionWire,
  type SpellWire,
} from '@inverse/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyUpdate, updateWaiting, watchForUpdates } from '../net/updates';
import { isSmallScreen, usingTouch, type GameSettings } from '../game/camera';
import { Game } from '../game/game';
import type { ContextMenu } from '../game/menu';
import { api, ApiError, storedToken } from '../net/api';
import { Connection } from '../net/connection';
import { socketUrl } from '../net/paths';
import { ContextMenuView } from './ContextMenuView';
import { provideTilePicking } from './editor/tile-pick';
import { FaithPanel, SpellsPanel } from './Spellbook';
import { ReportForm } from './ReportForm';
import { Equipment } from './Equipment';
import { Inventory } from './Inventory';
import { Fighting, Journal, Skills, TabStrip } from './Panels';
import { FaithMeter, HealthMeter, ManaMeter, RunMeter } from './RunMeter';
import { playableArea } from './playableArea';
import { TAB_LABELS, type TabName } from './tabs';
import { AdminWindow } from './Admin';
import { EditorToolbar } from './editor/Toolbar';
import { EditorPalette } from './editor/Palette';
import { EditorDetails } from './editor/Details';
import { EditorKeys } from './editor/Keys';
import { readItemDetail, readItemList, type ItemDetail, type ListedItem } from './editor/items';
import type { SavedQuest } from './editor/quests';
import {
  readCharacterDetail,
  readCharacterList,
  type CharacterDetail,
  type ListedCharacter,
} from './editor/characters';
import { useDraggableWindow } from './draggable';
import { Conversation } from './editor/Conversation';
import { Appearance } from './Appearance';
import { QuestTracker } from './QuestTracker';
import { EditorFind } from './editor/Find';
import { Minimap } from './Minimap';
import { WorldMap } from './WorldMap';
import { Friends } from './Friends';
import type { SavedGroup } from './editor/Groups';
import type { Wanted } from './editor/find';
import { Trained, type Earned } from './Trained';
import { Requests, type Ask } from './Requests';
import { QuestNotice, type QuestNoticeEntry } from './QuestNotice';
import { loadMarks, saveMarks, type MapMarker, type Waypoint } from './map-marks';
import { useAltHeld } from './alt-mode';
import { LoosePanel } from './LoosePanel';
import { ItemForm } from './editor/Items';
import { itemFrom, problemWith, recipeFrom, usesFrom, type ItemDraft } from './editor/items';
import { SkillGuide } from './SkillGuide';
import { Lookup } from './Lookup';
import { guideFor } from './skill-guide';
import { clipsLoaded } from '../game/avatars';
import type { PoseRow } from './editor/Poses';
import { wantedItems, wantsOf } from './wants';
import { questTarget } from './editor/marks';
import type { DialogueLine } from './editor/Dialogue';
import { EditorMake } from './editor/Make';
import type { SavedTrigger, TriggerArea } from './editor/Triggers';
import { EditorMap } from './editor/Map';
import { Places } from './editor/Places';
import { placesFrom, type Place } from './editor/places-list';
import { EditorOutliner } from './editor/Outliner';
import { EditorTools } from './editor/Tools';
import { EditorWindow, type WindowRect } from './editor/Window';
import { clearOf, loadLayout, saveLayout, unrolled, type WindowName } from './editor/windows';
import { phaseNow, pivotOf, turnAround, turnedBy, variationOf } from '@inverse/common';
import {
  MODES,
  NOTHING_SELECTED,
  TRANSFORM_TOOLS,
  TERRAIN_ACTIONS,
  areaBetween,
  loadEditorSettings,
  loadPieces,
  pieceFrom,
  saveEditorSettings,
  savePieces,
  areaHolds,
  brushArea,
  dragMeans,
  objectPicks,
  selectionOf,
  wantsWholeArea,
  buildingKind,
  terrainCommand,
  wallSide,
  zonesOn,
  type EditorMode,
  type Facts,
  type ObjectPick,
  type Pick,
  type Piece,
  type Placeable,
  type SelectFilter,
  type Selection,
  tilesOf,
  DRAGGED,
  activePick,
  withPicks,
  howFrom,
  type SpawnKind,
  type TerrainAction,
  type TileArea,
  type TransformTool,
} from './editor/model';
import { FloatingPanel } from './FloatingPanel';
import { AllSettings, QuickSettings } from './Settings';
import { SystemMenu } from './SystemMenu';
import { Crafting } from './Crafting';
import {
  defaultLabel,
  loadDefaults,
  recipesAt,
  saveDefaults,
  type StationDefaults,
} from './crafting';
import { Tooltip, type TooltipContent } from './Tooltip';
import { itemTooltip } from './item-tooltip';
import {
  actionFor,
  keyOf,
  loadKeys,
  placeOf,
  readable,
  saveKeys,
  slotOf,
  type KeyBindings,
} from './keys';
import { Bank, Shop } from './Trade';
import { TradeWindow } from './TradeWindow';
import { Dialogue } from './Dialogue';
import { TouchButtons } from './TouchButtons';
import { WorldFrame } from './WorldFrame';
import { DialogueNearby } from './editor/DialogueNearby';
import { EditorQuests } from './editor/Quests';
import type { LocalRegion } from '../game/world-state';
import { sounds } from '../game/sound';
import { XpTracker, type Tracked } from './XpTracker';
import { Leaderboards } from './Leaderboards';
import { CollectionLog } from './CollectionLog';
import { Party } from './Party';
import { Guild } from './Guild';
import { Exchange } from './Exchange';
import type { DropTest } from './editor/Drops';
import { readEdits, type EditSummary } from './editor/edits';

/** Everything at zero, which is what an empty set of equipment adds up to. */
function emptyBonuses(): BonusesPacket {
  const bonuses = {} as BonusesPacket;
  for (const field of BONUS_FIELDS) bonuses[field] = 0;
  return bonuses;
}

/** The tabs across the top of the log, in the order they are read. */

/** What the world says when it says no, which is heard as well as read. */
const REFUSAL =
  /^(you (cannot|can't|can not|do not have|don't have|need|are not|have no|must)|nothing can|there is no room|that is not)/i;

const CHAT_TABS = [
  { name: 'all', label: 'All' },
  { name: 'system', label: 'System' },
  { name: 'nearby', label: 'Nearby' },
  { name: 'world', label: 'World' },
  { name: 'guild', label: 'Guild' },
  { name: 'private', label: 'Private' },
] as const;

/**
 * A class per scope, so the All tab can be read without reading it.
 *
 * Colour is doing the work a prefix would otherwise do. A line that says where
 * it came from in words costs eight characters of every line; a line that says
 * it in colour costs nothing and is faster to skim.
 */
const SCOPE_CLASS: Readonly<Record<number, string>> = {
  [ChatScope.System]: 'said-system',
  [ChatScope.Nearby]: 'said-nearby',
  [ChatScope.World]: 'said-world',
  [ChatScope.Guild]: 'said-guild',
  [ChatScope.Private]: 'said-private',
};

/** Which tab a line belongs on. */
const SCOPE_TAB: Readonly<Record<number, string>> = {
  [ChatScope.System]: 'system',
  [ChatScope.Nearby]: 'nearby',
  [ChatScope.World]: 'world',
  [ChatScope.Guild]: 'guild',
  [ChatScope.Private]: 'private',
};

interface ChatLine {
  id: number;
  who: string | null;
  text: string;
  /**
   * Where it was said, which decides both which tab it belongs on and what
   * colour it is drawn in.
   *
   * Carried on the line rather than worked out from who said it, because the
   * same person speaks in several of these and the world speaks in one of its
   * own.
   */
  scope: ChatScopeValue;
  /** Whose conversation, for a private line: the other person, never yourself. */
  with?: string;
  /**
   * Something the line itself does when it is pressed.
   *
   * A trade request arrives as a line in the log as well as a popup, because the
   * popup fades and the log does not: somebody who looked away for five seconds
   * should still be able to answer. Lines that do something are drawn in a
   * colour of their own so it is clear they are not ordinary chatter.
   */
  act?: { label: string; run: () => void };
}

/**
 * What was dragged out of the content browser, when that is where it came from.
 *
 * The browser writes the entry into the drag itself rather than relying on what
 * the palette is holding, because a drag and the render that follows choosing
 * something do not happen in a fixed order, and a drop that lands first would
 * otherwise put down whatever was chosen before it.
 */
/** The smallest rectangle holding a set of tiles, for anything that wants one. */
function areaAround(tiles: readonly { tx: number; tz: number }[]): TileArea | null {
  if (tiles.length === 0) return null;

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;

  for (const tile of tiles) {
    minX = Math.min(minX, tile.tx);
    maxX = Math.max(maxX, tile.tx);
    minZ = Math.min(minZ, tile.tz);
    maxZ = Math.max(maxZ, tile.tz);
  }

  return { tx: minX, tz: minZ, width: maxX - minX + 1, height: maxZ - minZ + 1 };
}

/**
 * Everything inside a loop drawn on the ground.
 *
 * A lasso is not a brush: what it means is the shape it encloses, so the ends of
 * the trail are joined up and the inside is filled in. Worked out by flooding
 * the outside of the bounding box and keeping whatever the flood could not
 * reach, which handles a shape drawn round a corner or into itself without any
 * winding arithmetic.
 */
function insideLoop(trail: readonly { tx: number; tz: number }[]): { tx: number; tz: number }[] {
  if (trail.length < 3) return [...trail];

  const edge = new Set(trail.map((tile) => `${tile.tx},${tile.tz}`));

  // The line back to where it started, so a loop somebody did not quite close is
  // still a loop.
  const first = trail[0];
  const last = trail[trail.length - 1];
  if (first && last) {
    for (const tile of tilesBetween(last, first)) edge.add(`${tile.tx},${tile.tz}`);
  }

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;

  for (const key of edge) {
    const [tx = 0, tz = 0] = key.split(',').map(Number);
    minX = Math.min(minX, tx);
    maxX = Math.max(maxX, tx);
    minZ = Math.min(minZ, tz);
    maxZ = Math.max(maxZ, tz);
  }

  // A ring of one tile round the box, so the flood always has somewhere outside
  // to start from even when the loop runs along the edge of its own bounds.
  const fromX = minX - 1;
  const fromZ = minZ - 1;
  const toX = maxX + 1;
  const toZ = maxZ + 1;

  const outside = new Set<string>();
  const queue: { tx: number; tz: number }[] = [{ tx: fromX, tz: fromZ }];
  outside.add(`${fromX},${fromZ}`);

  while (queue.length > 0) {
    const at = queue.pop();
    if (!at) break;

    for (const [dx, dz] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const tx = at.tx + dx;
      const tz = at.tz + dz;
      if (tx < fromX || tz < fromZ || tx > toX || tz > toZ) continue;

      const key = `${tx},${tz}`;
      if (outside.has(key) || edge.has(key)) continue;

      outside.add(key);
      queue.push({ tx, tz });
    }
  }

  const held: { tx: number; tz: number }[] = [];
  for (let tz = minZ; tz <= maxZ; tz++) {
    for (let tx = minX; tx <= maxX; tx++) {
      if (outside.has(`${tx},${tz}`)) continue;
      held.push({ tx, tz });
    }
  }

  return held;
}

/**
 * Every tile on the straight line between two, not counting the first.
 *
 * The same walk the game uses to fill in what a quick drag skipped, written here
 * as well because closing a loop needs it and the two are not the same module.
 */
function tilesBetween(
  from: { tx: number; tz: number },
  to: { tx: number; tz: number },
): { tx: number; tz: number }[] {
  const tiles: { tx: number; tz: number }[] = [];

  let x = from.tx;
  let z = from.tz;
  const stepX = to.tx > from.tx ? 1 : -1;
  const stepZ = to.tz > from.tz ? 1 : -1;
  const spanX = Math.abs(to.tx - from.tx);
  const spanZ = Math.abs(to.tz - from.tz);

  let error = spanX - spanZ;
  while (x !== to.tx || z !== to.tz) {
    const doubled = error * 2;
    if (doubled > -spanZ) {
      error -= spanZ;
      x += stepX;
    }
    if (doubled < spanX) {
      error += spanX;
      z += stepZ;
    }

    tiles.push({ tx: x, tz: z });
  }

  return tiles;
}

/**
 * A number that marks everything one gesture sends as belonging together.
 *
 * A lasso across forty tiles is one act, however many commands it takes to say
 * so, and taking it back should be one press rather than forty. The server folds
 * consecutive edits carrying the same mark into a single entry in the history.
 */
let lastStroke = 0;

function strokeMark(): number {
  lastStroke = (lastStroke % 65_000) + 1;
  return lastStroke;
}

/**
 * A scattered set of tiles as the fewest rectangles that cover it.
 *
 * Only rows, which is enough: a hand drawn trail is mostly short horizontal
 * runs, and turning two hundred commands into twenty is the whole point.
 */
function runsOf(
  tiles: readonly { tx: number; tz: number }[],
): { tx: number; tz: number; width: number }[] {
  const rows = new Map<number, number[]>();
  for (const tile of tiles) {
    const row = rows.get(tile.tz) ?? [];
    row.push(tile.tx);
    rows.set(tile.tz, row);
  }

  const runs: { tx: number; tz: number; width: number }[] = [];
  for (const [tz, columns] of rows) {
    columns.sort((a, b) => a - b);

    let from = columns[0];
    let last = columns[0];
    if (from === undefined || last === undefined) continue;

    for (const tx of columns.slice(1)) {
      if (tx === last + 1) {
        last = tx;
        continue;
      }

      runs.push({ tx: from, tz, width: last - from + 1 });
      from = tx;
      last = tx;
    }

    runs.push({ tx: from, tz, width: last - from + 1 });
  }

  return runs;
}

function draggedPlaceable(data: DataTransfer): Placeable | null {
  try {
    const written = data.getData('application/inverse-placeable');
    if (!written) return null;

    const parsed: unknown = JSON.parse(written);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Placeable;
  } catch {
    return null;
  }
}

export function GameView({
  token,
  characterId,
  settings,
  onSettings,
  onLeave,
  onSessionExpired,
}: {
  token: string;
  characterId: string;
  settings: GameSettings;
  onSettings: (settings: GameSettings) => void;
  onLeave: () => void;
  onSessionExpired: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** The arrow at the edge of the screen, moved by hand rather than by state. */
  const pointerRef = useRef<HTMLDivElement>(null);
  const lastPickRef = useRef<{ tx: number; tz: number } | null>(null);
  /**
   * The stroke being painted right now, while the pointer is still down.
   *
   * One mark for the whole of it, so undo takes back the stroke rather than the
   * last tile of it, and a note of what has already been worked so a brush that
   * doubles back does not raise the same corner twice.
   */
  const strokeRef = useRef<{ mark: number; done: Set<string> } | null>(null);
  /** Where a freehand stroke began and where it has got to, for a ramp. */
  const wayRef = useRef<{
    from: { tx: number; tz: number };
    to: { tx: number; tz: number };
  } | null>(null);
  const dropRef = useRef<
    ((screenX: number, screenY: number, dropped: Placeable | null) => void) | null
  >(null);
  const gameRef = useRef<Game | null>(null);
  const connectionRef = useRef<Connection | null>(null);
  const chatIdRef = useRef(0);

  const [status, setStatus] = useState<'connecting' | 'playing' | 'lost'>('connecting');
  const [problem, setProblem] = useState<string | null>(null);
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [energy, setEnergy] = useState(MAX_RUN_ENERGY);

  /** How much of a beating the character can still take. */
  const [health, setHealth] = useState({ current: 0, max: 0 });

  /** How they fight, which decides the bonus and where the experience goes. */
  const [stance, setStance] = useState(0);

  /** Which of the three ways of fighting what they are holding puts them in. */
  const [fightingWay, setFightingWay] = useState(0);

  /** Whether being hit is answered without being told to. */
  const [retaliate, setRetaliate] = useState(true);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [latency, setLatency] = useState(0);
  const [restartIn, setRestartIn] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  /** Set once nothing has been said for a while, so the log stops taking attention. */
  const [chatIdle, setChatIdle] = useState(false);
  const [allSettingsOpen, setAllSettingsOpen] = useState(false);
  /** The collection log as the world last sent it, and whether it is open. */
  const [collection, setCollection] = useState<CollectionWire>({ pages: [], have: {} });
  const [collectionOpen, setCollectionOpen] = useState(false);
  /** Who is looking at the boards, while they are open, so their own row stands out. */
  const [boardsFor, setBoardsFor] = useState<string | null>(null);

  /** What the server says this account may do. Player until told otherwise. */
  const [role, setRole] = useState<RoleValue>(Role.Player);
  const [online, setOnline] = useState<readonly string[]>([]);
  const [adminOpen, setAdminOpen] = useState(false);
  /** Which section the tools open on, when the way in named one. */
  const [adminSection, setAdminSection] = useState<'players' | 'server' | 'look'>('players');
  /**
   * How the editor was set up last time, which is where it starts this time.
   *
   * Read once as the first state is worked out rather than held in a ref, since
   * a ref read while rendering is a ref read at the wrong moment.
   */
  const [remembered] = useState(loadEditorSettings);

  /** What the editor is doing, and what it has hold of. */
  const [mode, setMode] = useState<EditorMode>('play');
  const [tool, setTool] = useState<TransformTool>(remembered.tool);
  const [chosen, setChosen] = useState<Placeable | null>(null);
  const [selection, setSelection] = useState<Selection>(NOTHING_SELECTED);

  const [terrain, setTerrain] = useState<TerrainAction>(remembered.terrain);
  const [brushSize, setBrushSize] = useState(remembered.brush);
  const [filter, setFilter] = useState<SelectFilter>(remembered.filter);

  /** How hard a terrain stroke presses, and whether it is round or square. */
  const [strength, setStrength] = useState(remembered.strength);
  const [roundBrush, setRoundBrush] = useState(remembered.roundBrush);

  /*
   * What a building comes out as: how tall, which way the door faces, and
   * whether it has windows and anything inside it.
   *
   * Held here rather than in the panel because the same settings are wanted by
   * the stroke that raises it, and a panel that forgot them between two
   * buildings would mean setting them again for every house in a row.
   */
  // Nought for however many storeys the kind goes up: a tower asked for without
  // a number is three, a house is one. A number set here says so instead.
  const [buildStoreys, setBuildStoreys] = useState(0);
  const [buildFacing, setBuildFacing] = useState(0);
  const [buildWindows, setBuildWindows] = useState(true);
  const [buildFurnish, setBuildFurnish] = useState(false);

  /**
   * Which way the last stroke went, and how high a ramp should end up.
   *
   * A ramp faces the way it was drawn, so this is written down by every drag
   * whether or not a ramp is what the drag turned out to be for.
   */
  const [slopeWay, setSlopeWay] = useState<{ x: number; z: number }>({ x: 1, z: 0 });
  const [slopeTop, setSlopeTop] = useState<number | null>(remembered.slopeTop);

  /**
   * Whether a door is built standing open.
   *
   * How a door rests is a fact about the one being built, and the world keeps
   * it: whichever half was put down is what it goes back to once somebody who
   * swung it has walked on.
   */
  const [restsOpen, setRestsOpen] = useState(false);

  /** How a brush of scenery lands: how much of it, and whether each one differs. */
  const [vary, setVary] = useState(remembered.vary);
  const [density, setDensity] = useState(remembered.density);

  /** Cut and copied things, held relative to the corner of what was taken. */
  const [clipboard, setClipboard] = useState<ObjectPick[]>([]);

  /** The spawn zones, which only an editor is ever sent. */
  const [zones, setZones] = useState<readonly ZoneWire[]>([]);
  /** The places in the region that do something to whoever walks into them. */
  const [triggers, setTriggers] = useState<readonly SavedTrigger[]>([]);
  const [spawns, setSpawns] = useState<readonly SpawnWire[]>([]);
  const [zonePaint, setZonePaint] = useState<'add' | 'remove'>('add');
  const [zoneMax, setZoneMax] = useState(remembered.zoneMax);
  const [zoneMin, setZoneMin] = useState(remembered.zoneMin);
  const [zoneSpan, setZoneSpan] = useState(remembered.zoneSpan);

  /** What the server said about the selected spawn, and the last area drawn out. */
  const [facts, setFacts] = useState<Facts | null>(null);

  // The items window: everything there is, whichever one is open, and a count of
  // saves so the form knows when its work has landed.
  const [itemList, setItemList] = useState<ListedItem[]>([]);
  const [itemDetail, setItemDetail] = useState<ItemDetail | null>(null);
  const [itemSaves, setItemSaves] = useState(0);
  const [characterList, setCharacterList] = useState<ListedCharacter[]>([]);
  const [characterDetail, setCharacterDetail] = useState<CharacterDetail | null>(null);
  const [characterSaves, setCharacterSaves] = useState(0);

  /**
   * The quest being followed, and whether the world is marked up for it.
   *
   * Kept in the browser rather than on the character: which quest somebody is
   * watching is a fact about the session, and being handed a marked world on a
   * different machine because of a click made yesterday is a surprise.
   */
  const [pinnedQuest, setPinnedQuest] = useState<string | null>(null);

  /**
   * Starts or stops following a quest, and tells the world.
   *
   * Which quest somebody is following belongs with the rest of what they have
   * done: logging out and back in should not lose it, and neither should
   * playing on another machine.
   */
  const followQuest = useCallback((quest: string | null) => {
    setPinnedQuest(quest);
    connectionRef.current?.sendTrackQuest(quest ?? '');
  }, []);

  /**
   * The panels that are up, when panels float.
   *
   * The bar keeps one at a time in `tab`, which is what a phone wants. Floating,
   * any number can be open, so which ones is a set rather than a choice.
   */
  const [floating, setFloating] = useState<TabName[]>([]);

  /**
   * Which floating panels have been touched, oldest first.
   *
   * The same stacking the editor's windows have, for the same reason: panels
   * that float are windows, and a window somebody clicks into comes to the
   * front. Without it the order was whatever order they happened to be listed
   * in, so a panel could not be got at once another was dropped over it.
   */
  /**
   * What somebody has been asked for, newest last.
   *
   * Held here rather than in the panel that shows it, because a request has to
   * survive that panel being moved, hidden or rebuilt, and because the chat line
   * for a trade request answers the same thing the buttons do.
   */
  /**
   * The trade that is open, and what is on the table.
   *
   * The offers are containers like any other, so they arrive on the ordinary
   * container packets and are kept beside the bag and the bank.
   */
  /**
   * Which part of the log is showing, and which of them have something new.
   *
   * All is the whole of it, colour coded; the rest are one kind each. Private
   * carries a second row of who, because two conversations at once in one list
   * is two conversations nobody can follow.
   */
  const [chatTab, setChatTab] = useState<
    'all' | 'system' | 'nearby' | 'world' | 'guild' | 'private'
  >('all');
  const [talkingWith, setTalkingWith] = useState<string | null>(null);
  const [unread, setUnread] = useState<Record<string, boolean>>({});

  /** Nobody is in a guild yet, because guilds are not built. */
  const inAGuild = false;

  /*
   * Which tab is being read, where a callback can see it.
   *
   * A line arriving on the tab somebody is looking at has been read, and marking
   * it unread would put a dot on it the moment they looked away.
   */
  const chatTabRef = useRef(chatTab);
  useEffect(() => {
    chatTabRef.current = chatTab;
  }, [chatTab]);

  /** What is left of the pool a spell is cast from. Nought until the world says. */
  const [mana, setMana] = useState({ current: 0, max: 0 });

  const [trade, setTrade] = useState<TradeStatePacket | null>(null);
  const [offer, setOffer] = useState<(ItemStack | null)[]>([]);
  const [theirOffer, setTheirOffer] = useState<(ItemStack | null)[]>([]);

  const [asks, setAsks] = useState<Ask[]>([]);
  const askId = useRef(1);

  const [panelStacking, setPanelStacking] = useState<TabName[]>([]);

  /**
   * Saying yes or no to something that was asked.
   *
   * The popup goes at once, whatever the world then says: leaving it up while a
   * packet goes out and comes back invites somebody pressing it twice.
   */
  const answerAsk = useCallback((ask: Ask, yes: boolean): void => {
    setAsks((held) => held.filter((one) => one.id !== ask.id));

    if (ask.kind === AskKind.Friend) {
      connectionRef.current?.sendFriends(
        yes ? FriendAct.AcceptFriend : FriendAct.DenyFriend,
        ask.name,
      );
      return;
    }

    if (ask.kind === AskKind.Party) {
      connectionRef.current?.sendParty(yes ? PartyAct.Accept : PartyAct.Decline, ask.name);
      return;
    }

    connectionRef.current?.sendTrade(yes ? TradeAct.Accept : TradeAct.Decline, ask.name);
  }, []);

  const raisePanel = useCallback((name: TabName) => {
    setPanelStacking((held) =>
      held[held.length - 1] === name ? held : [...held.filter((one) => one !== name), name],
    );
  }, []);

  /** Whether the system menu is up, which is what Escape opens. */
  const [systemOpen, setSystemOpen] = useState(false);

  /** What every key does, kept in the browser and read on the way in. */
  const [bindings, setBindings] = useState<KeyBindings>(() => loadKeys());

  /** The slot waiting for a key, after a middle click on it. */
  const [binding, setBinding] = useState<number | null>(null);

  /** The bag slot the pointer is resting on, which is what a tooltip describes. */
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);

  /**
   * The latest bag, catalogue and station choices, for the game to read.
   *
   * The game holds a callback rather than the state itself, and a callback that
   * closed over the first render would answer with the first render's bag
   * forever. These are what it reads instead.
   */
  const inventoryRef = useRef<readonly (ItemStack | null)[]>([]);
  const catalogueRef = useRef<{ items: ItemDefinitionWire[]; recipes: RecipeWire[] }>({
    items: [],
    recipes: [],
  });
  const stationsRef = useRef<StationDefaults>({});

  /** What is being held to use on something, and where a crafting window is open. */
  const [using, setUsing] = useState<{ slot: number; name: string } | null>(null);
  /** A spell pressed and waiting for what it is to be cast at. */
  const [casting, setCasting] = useState<SpellWire | null>(null);
  /** Every spell and prayer the world knows, as the tools wrote them. */
  const [spellbook, setSpellbook] = useState<SpellbookWire>({ spells: [], prayers: [] });
  /** Faith, prayers held up, the spell set to cast, and what is lasting on them. */
  const [devotion, setDevotion] = useState<DevotionPacket | null>(null);
  const [spellsOpen, setSpellsOpen] = useState(false);
  /** Whether the next click in the world is picking a spot for the tools. */
  const [pickingTile, setPickingTile] = useState(false);
  /** Somebody being reported, while the form is up. */
  const [reporting, setReporting] = useState<string | null>(null);

  /**
   * A spell pressed in the book. One cast on the caster goes at once; anything
   * else waits for the next click to say what it is cast at.
   */
  const castFromBook = useCallback((spell: SpellWire) => {
    if (spell.target === 'self') gameRef.current?.castOnSelf(spell);
    else gameRef.current?.beginCasting(spell);
  }, []);
  const [faithOpen, setFaithOpen] = useState(false);
  const [crafting, setCrafting] = useState<{
    objectId: number;
    name: string;
    tx: number;
    tz: number;
    plane: number;
  } | null>(null);

  /** What each kind of station has been told to make, kept in the browser. */
  const [stations, setStations] = useState<StationDefaults>(() => loadDefaults());

  /** An item hovered anywhere other than the bag, which is named rather than numbered. */
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  /** What each bag slot is bound to, for the corner of the slot itself. */
  const slotKeys = useMemo(() => {
    const held: Record<number, string> = {};
    for (const [action, key] of Object.entries(bindings)) {
      const slot = slotOf(action);
      if (slot !== null && key) held[slot] = readable(key);
    }
    return held;
  }, [bindings]);

  const bind = useCallback((action: string, key: string): void => {
    setBindings((held) => {
      // A key does one thing. Binding it to something else takes it off whatever
      // had it, which is what somebody rebinding a key means by it.
      const next: KeyBindings = {};
      for (const [name, bound] of Object.entries(held)) {
        next[name] = key && bound === key && name !== action ? '' : bound;
      }

      next[action] = key;
      saveKeys(next);
      return next;
    });
  }, []);

  /**
   * Sends a model in, and says what went wrong if anything did.
   *
   * Over HTTP rather than the game connection: a model is megabytes and the game
   * connection is for things that happen in the world.
   */
  const uploadModel = useCallback(
    async (model: {
      name: string;
      title: string;
      contents: string;
      binary: boolean;
      tags: string[];
    }): Promise<string | null> => {
      const token = storedToken();
      if (!token) return 'You are not signed in.';

      try {
        await api.uploadModel(token, model);
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : 'That was refused.';
      }
    },
    [],
  );

  const uploadSound = useCallback(
    async (sound: {
      name: string;
      title: string;
      contents: string;
      kind: 'wav' | 'ogg' | 'mp3';
    }): Promise<string | null> => {
      const token = storedToken();
      if (!token) return 'You are not signed in.';

      try {
        await api.uploadSound(token, sound);
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : 'That was refused.';
      }
    },
    [],
  );

  /** One quest in full, for the panel that writes them. */
  const [questDetail, setQuestDetail] = useState<SavedQuest | null>(null);
  const [questSaves, setQuestSaves] = useState(0);
  const [drops, setDrops] = useState<DropTablePacket | null>(null);
  /** What the last try of a drop table came to. */
  const [dropTest, setDropTest] = useState<DropTest | null>(null);
  /**
   * Every way of gathering the tools have been told about.
   *
   * One packet per way, and a spot can be worked more than one way, so they are
   * collected rather than replaced: netting a riverbank and fishing it with a
   * rod are two entries about the same piece of scenery.
   */
  const [gathering, setGathering] = useState<GatheringPacket[]>([]);
  const [problems, setProblems] = useState<readonly WorldProblem[] | null>(null);
  /** Every problem counted by what it says, since the list itself stops at two hundred. */
  const [problemCounts, setProblemCounts] = useState<WorldCheckPacket['counts']>([]);
  /** Which clip each pose is pointed at, for the panel that points them. */
  const [poseRows, setPoseRows] = useState<readonly PoseRow[]>([]);
  /** What is drawn as each model, for the manager that swaps and removes them. */
  /** Pieces of the world saved to be put down again, as the world last listed them. */
  const [groups, setGroups] = useState<readonly SavedGroup[]>([]);
  const [modelUsers, setModelUsers] = useState<
    Readonly<Record<string, { scenery: string[]; items: string[]; characters: string[] }>>
  >({});
  /** Which skill's guide is open, if one is. */
  const [guideFor_, setGuideFor] = useState<string | null>(null);
  /** What was earned lately, for the numbers that float up beside the character. */
  const [earned, setEarned] = useState<readonly Earned[]>([]);
  /** Every skill earning this sitting, for the tracker. */
  const [tracked, setTracked] = useState<readonly Tracked[]>([]);
  const earnedId = useRef(1);
  /** The item being written, in its own window, and anything wrong with it. */
  const [itemDraft, setItemDraft] = useState<ItemDraft | null>(null);
  const [itemSaying, setItemSaying] = useState('');

  /**
   * Writes the item being edited back to the world.
   *
   * Held in a ref as well, so the settle timer and the window closing can both
   * reach the latest draft without either of them restarting the other.
   */
  const itemRef = useRef<ItemDraft | null>(null);
  useEffect(() => {
    itemRef.current = itemDraft;
  });

  const saveItem = useCallback((draft: ItemDraft | null): void => {
    if (!draft) return;

    const problem = problemWith(draft);
    if (problem) {
      setItemSaying(problem);
      return;
    }

    setItemSaying('');
    connectionRef.current?.sendAdminCommand(
      'saveItem',
      {},
      {
        json: JSON.stringify(itemFrom(draft)),
        recipe: recipeFrom(draft),
        // What it is used in as well as what makes it: the two halves of the
        // same question, and the other one had nowhere to be written.
        uses: usesFrom(draft),
      },
    );
  }, []);

  /**
   * Saved once somebody has stopped changing it, rather than on a button.
   *
   * An item is a dozen fields and a form that only writes itself down when a
   * button is found is a form somebody backs out of having lost the lot. The
   * pause is long enough that typing a name is one save rather than eight.
   */
  useEffect(() => {
    if (!itemDraft) return;
    if (problemWith(itemDraft)) return;

    const timer = window.setTimeout(() => saveItem(itemRef.current), 800);
    return () => window.clearTimeout(timer);
  }, [itemDraft, saveItem]);

  const [windows, setWindows] = useState(() => loadLayout());

  /** Opens the window that writes one item, on the draft handed over. */
  const openItemEditor = useCallback((draft: ItemDraft) => {
    setItemDraft(draft);
    setItemSaying('');

    setWindows((held) => {
      if (held.open.has('item')) return held;

      const open = new Set(held.open).add('item');
      const layout = { ...held.layout, item: clearOf(held.layout, 'item', held.open) };

      saveLayout(layout, open, held.compact);
      return { ...held, layout, open };
    });
  }, []);
  /** The conversation on screen, when somebody is being talked to. */
  const [talking, setTalking] = useState<DialoguePacket | null>(null);
  /** How far along every quest is, for the journal. */
  const [progress, setProgress] = useState<ProgressPacket>({ quests: [], flags: [] });
  /** Set when the server is serving a newer build than this page is running. */
  const [updateReady, setUpdateReady] = useState(false);
  /** Whether scenery is drawn. Off while the ground underneath it is being worked on. */
  const [sceneryShown, setSceneryShown] = useState(true);

  /** Which half of Spawns is being worked in. */
  const [spawnKind, setSpawnKind] = useState<SpawnKind>('people');

  /**
   * Which storey is being built on.
   *
   * Everything put down goes on it and is drawn lifted above the one below.
   * Walking up there is not built yet: what a storey is for now is the shape of
   * a building, a bridge over water, or a dock standing off the shore.
   */
  const [storey, setStorey] = useState(0);

  /**
   * Whether the storeys above the one being built on are drawn.
   *
   * Off by default, because a world with its roofs missing is not the world.
   * On while somebody is laying an upper floor, which cannot be done from
   * underneath a roof.
   */
  const [roofOff, setRoofOff] = useState(false);

  useEffect(() => {
    // The ground floor hides nothing: there is nothing under it to see.
    gameRef.current?.scene.showUpToStorey(roofOff && storey > 0 ? storey : null);
    // And the grid goes up with it, so what is drawn is the floor being built
    // on rather than the ground under it.
    gameRef.current?.scene.buildOn(storey);
  }, [roofOff, storey]);

  /**
   * The dock tool: whether it is on, and the height the dock is being built to.
   *
   * The first piece put down sets that height and every piece after it is laid
   * at the same one, which is the difference between a dock and a line of boards
   * each floating at whatever the water under it happens to be doing.
   */
  const [docking, setDocking] = useState(false);
  const [dockLift, setDockLift] = useState<number | null>(null);
  /** What the thing the tools are looking at does, when it has been asked. */
  const [spotActs, setSpotActs] = useState<{
    tx: number;
    tz: number;
    plane: number;
    own: boolean;
    acts: string;
  } | null>(null);
  /**
   * How far above the ground the next thing is put down, in tiles.
   *
   * A dock pins one height and lays everything at it, which is right for boards
   * over water and wrong for a string of lights along a street: those follow
   * the ground and hang a set distance over it. Nought is standing on it, which
   * is what everything placed before this meant.
   */
  const [hang, setHang] = useState(0);
  /** What a pile left lying about is, and what it does once somebody takes it. */
  const [lootMany, setLootMany] = useState(1);
  const [lootBack, setLootBack] = useState(0);
  const [lootEach, setLootEach] = useState(true);
  const dock = useRef({ on: false, lift: null as number | null });

  useEffect(() => {
    dock.current.on = docking;
    if (!docking) dock.current.lift = null;
  }, [docking]);

  /** Moves or resizes one window, and remembers where it was left. */
  const placeWindow = useCallback((name: WindowName, rect: WindowRect) => {
    setWindows((held) => {
      const layout = { ...held.layout, [name]: rect };
      saveLayout(layout, held.open, held.compact);
      return { ...held, layout };
    });
  }, []);

  /** Opens a window, or puts it away. */
  /**
   * Which windows have been touched, oldest first.
   *
   * A stacking order and nothing more: the last one touched is drawn over the
   * others. Not remembered between sessions, because where a window sits is
   * worth keeping and what somebody clicked last is not.
   */
  const [stacking, setStacking] = useState<WindowName[]>([]);

  /**
   * Where the line saying what the pointer is on has been put.
   *
   * No home given, so until somebody moves it the layout keeps it in the top
   * left and slides it under the editor bar when there is one, which is what it
   * did before it could be moved at all.
   */
  const {
    attach: attachTarget,
    style: targetStyle,
    onPointerDown: dragTarget,
  } = useDraggableWindow('target');

  /**
   * Which tab the Create window opens on, and which quest it opens.
   *
   * Set from the conversation editor, which is the other half of the same job:
   * pressing Edit Quest there should land on that quest rather than on a list
   * of them.
   */
  const [makerTab, setMakerTab] = useState<'item' | 'npc' | 'quest' | 'scenery' | 'model' | null>(
    null,
  );
  /** Whether the search across everything is up. */
  const [finding, setFinding] = useState(false);
  /** Whether the whole island is up over the world. */
  const [worldMap, setWorldMap] = useState(false);

  /** A place the map should open on, for arranging it by dragging. */
  const [arrangeOn, setArrangeOn] = useState<string | null>(null);
  /** The one thing the search asked the Create window to open on. */
  const [makerThing, setMakerThing] = useState<(Wanted & { kind: string }) | null>(null);
  const [makerQuest, setMakerQuest] = useState<string | null>(null);

  /**
   * Whose conversation the Talk window is showing.
   *
   * A character, not a spawn. Writing what somebody says should not require
   * finding one of them standing on a tile first, and a kind nobody has placed
   * yet had no way to be written for at all.
   */
  const [talkingAbout, setTalkingAbout] = useState<number | null>(null);

  /**
   * Which kinds of character are in view, for the dialogue window to offer.
   *
   * Only while that window is open, and on a beat rather than every frame:
   * what it is for is a list somebody reads, and a list that changes sixty
   * times a second is not one.
   */
  const [nearbyCharacters, setNearbyCharacters] = useState<number[]>([]);

  const dialogueOpen = windows.open.has('talk');

  useEffect(() => {
    if (!dialogueOpen) return;

    const beat = window.setInterval(() => {
      const game = gameRef.current;
      if (!game) return;

      const held = new Set<number>();
      for (const entity of game.state.entities.values()) {
        if (entity.kind === EntityKind.Npc && entity.defId > 0) held.add(entity.defId);
      }

      setNearbyCharacters((was) =>
        was.length === held.size && was.every((one) => held.has(one)) ? was : [...held],
      );
    }, 900);

    return () => window.clearInterval(beat);
  }, [dialogueOpen]);
  const [talkLines, setTalkLines] = useState<DialogueLine[]>([]);

  const openConversation = useCallback((npcId: number) => {
    setTalkingAbout(npcId);
    connectionRef.current?.sendAdminCommand('characterDetails', { npc: npcId });

    setWindows((held) => {
      if (held.open.has('talk')) return held;

      const open = new Set(held.open).add('talk');
      const layout = { ...held.layout, talk: clearOf(held.layout, 'talk', held.open) };

      saveLayout(layout, open, held.compact);
      return { ...held, layout, open };
    });
  }, []);

  const raiseWindow = useCallback((name: WindowName) => {
    setStacking((held) =>
      held[held.length - 1] === name ? held : [...held.filter((one) => one !== name), name],
    );
  }, []);

  /** How high one window sits, and what to do when it is touched. */
  const stacked = useCallback(
    (name: WindowName): { depth: number; onRaise: () => void } => {
      const at = stacking.indexOf(name);
      return { depth: at < 0 ? 25 : 26 + at, onRaise: () => raiseWindow(name) };
    },
    [stacking, raiseWindow],
  );

  // Which windows are open, read by the sounds of opening and shutting one.
  const openWindows = useRef(windows.open);
  useEffect(() => {
    openWindows.current = windows.open;
  }, [windows.open]);

  const togglePanel = useCallback((name: WindowName) => {
    sounds.cue(openWindows.current.has(name) ? 'close' : 'open');
    setWindows((held) => {
      const open = new Set(held.open);
      if (open.has(name)) {
        open.delete(name);
        saveLayout(held.layout, open, held.compact);
        return { ...held, open };
      }

      // Opened onto somewhere clear, rather than exactly on top of whatever is
      // already there: a window that lands under another looks like a button
      // that did nothing. And unrolled, for the same reason: pressing Map and
      // getting a title bar with nothing under it is the same disappointment.
      const layout = { ...held.layout, [name]: unrolled(clearOf(held.layout, name, open)) };
      open.add(name);

      saveLayout(layout, open, held.compact);
      return { ...held, layout, open };
    });
  }, []);

  /**
   * Opens a window whether or not it is already open.
   *
   * Different from toggling: something that opens a panel to show what was
   * asked for should never be the press that shuts it.
   */
  const openPanel = useCallback((name: WindowName) => {
    if (!openWindows.current.has(name)) sounds.cue('open');
    setWindows((held) => {
      if (held.open.has(name)) return held;

      const open = new Set(held.open).add(name);
      const layout = { ...held.layout, [name]: unrolled(clearOf(held.layout, name, held.open)) };

      saveLayout(layout, open, held.compact);
      return { ...held, layout, open };
    });
  }, []);

  /** Where what is held stands, for drawing it on the island map. */
  const heldTiles = useMemo(() => tilesOf(selection), [selection]);

  /** The rectangle round what is held, which is where a new trigger goes. */
  const heldArea = useMemo<TriggerArea | null>(() => {
    if (heldTiles.length === 0) return null;

    let leastX = Infinity;
    let leastZ = Infinity;
    let mostX = -Infinity;
    let mostZ = -Infinity;

    for (const tile of heldTiles) {
      leastX = Math.min(leastX, tile.tx);
      leastZ = Math.min(leastZ, tile.tz);
      mostX = Math.max(mostX, tile.tx);
      mostZ = Math.max(mostZ, tile.tz);
    }

    return {
      tx: leastX,
      tz: leastZ,
      width: mostX - leastX + 1,
      height: mostZ - leastZ + 1,
      plane: 0,
    };
  }, [heldTiles]);

  const [census, setCensus] = useState<
    readonly { id: number; name: string; count: number }[] | null
  >(null);
  /** Which one of a kind the list last went to, so pressing again goes to the next. */
  const steppedTo = useRef(new Map<number, number>());
  const [shopStock, setShop2] = useState<ShopStockPacket | null>(null);

  /** Whether the world editor has been opened from the tools window. */
  const [editorOpen, setEditorOpen] = useState(false);

  /** Whether the view is off the character, which the toolbar says and can undo. */
  const [panned, setPanned] = useState(false);

  /**
   * What the map is drawn from, and the two marks on it.
   *
   * Kept as state rather than read from the world while rendering, since the
   * world changes on its own clock and a panel that reads it mid render is a
   * panel that draws something nobody asked it to.
   */

  /** Groups built once and kept to be built again. */
  const [pieces, setPieces] = useState<Piece[]>(() => loadPieces());
  const [regionForMap, setRegionForMap] = useState<LocalRegion | null>(null);
  const [characterTile, setCharacterTile] = useState<{ tx: number; tz: number } | null>(null);
  const [viewTile, setViewTile] = useState<{ tx: number; tz: number } | null>(null);

  /** What the server says about the world: work not written out, and what can be undone. */
  const [world, setWorld] = useState({
    unsaved: false,
    canUndo: false,
    canRedo: false,
    undo: '',
    redo: '',
    zones: 0,
    spawns: 0,
    objects: 0,
    /** Whether a batch is open, and who is holding it. */
    drafting: false,
    draftBy: '',
    draftChanges: 0,
    draftSquares: 0,
    history: [] as readonly EditSummary[],
    future: [] as readonly EditSummary[],
  });
  const [lastArea, setLastArea] = useState<TileArea | null>(null);

  /**
   * Whose roam area the next box dragged on the ground belongs to.
   *
   * Nought for nobody, which is the ordinary case. Armed from the character
   * panel: dragging a box changes what the editor holds, so by the time somebody
   * pressed a button to use it they were editing the ground rather than the
   * character they drew it for. Armed, the drag sets the roam and nothing else.
   */
  const [roamFor, setRoamFor] = useState(0);

  // Read inside the pointer handlers, which are made once and would otherwise
  // hold the value this was when the world started.
  const roamForRef = useRef(0);

  useEffect(() => {
    roamForRef.current = roamFor;
  }, [roamFor]);
  const [saving, setSaving] = useState(false);

  /**
   * What the world contains, kept in state rather than read off the game.
   *
   * The definitions arrive after the first render, and something read during a
   * render has to be something a render will happen again for.
   */
  /** The regions on the server, for going somewhere else to build. */
  const [places, setPlaces] = useState<Place[]>([]);

  /**
   * What the world last said about its own clock, and when we heard it.
   *
   * The day is an hour long, so the stretch it is in is worked out again every
   * half minute rather than every frame: the sky is the game's job, and this is
   * only the word for it. Worked out in an effect rather than while drawing,
   * because reading a clock is not something a render is allowed to do.
   */
  const [clock, setClock] = useState<(TimeOfDayPacket & { heard: number }) | null>(null);
  const [timeOfDay, setTimeOfDay] = useState<{ phase: string; held: boolean } | null>(null);

  useEffect(() => {
    if (!clock) return;

    const settle = (): void => {
      if (clock.forced.length > 0) {
        setTimeOfDay({ phase: clock.forced, held: true });
        return;
      }

      // Their clock rather than ours, so a machine set wrong still agrees with
      // everybody else about whether it is night.
      const drift = clock.now - clock.heard;
      setTimeOfDay({ phase: phaseNow(Date.now() + drift, clock.dayMs), held: false });
    };

    settle();
    const id = window.setInterval(settle, 30_000);
    return () => window.clearInterval(id);
  }, [clock]);

  /** What the character being played looks like, for the panel that changes it. */
  const [myLook, setMyLook] = useState({ skin: 0x6b4a35, eyes: 0xf2efe6 });
  /**
   * A window a character opened, if one is up.
   *
   * Opened by the world rather than from a menu of our own: a barber takes the
   * fee, opens this, and the world listens for a change of appearance only
   * while it is up.
   */
  const [openWindow, setOpenWindow] = useState<string | null>(null);

  const [catalogue, setCatalogue] = useState<{
    items: ItemDefinitionWire[];
    objects: ObjectDefinitionWire[];
    characters: NpcDefinitionWire[];
    quests: QuestWire[];
    models: ModelWire[];
    recipes: RecipeWire[];
    shops: { id: number; name: string }[];
    /** Which scenery gives which item, for pointing a step at the nearest one. */
    yields: { objectId: number; itemId: number }[];
  }>({
    items: [],
    objects: [],
    characters: [],
    quests: [],
    models: [],
    recipes: [],
    shops: [],
    yields: [],
  });
  const [menu, setMenu] = useState<ContextMenu | null>(null);
  /** The item the book is open at, or nothing while it is shut. */
  const [lookingUp, setLookingUp] = useState<number | null>(null);
  const [inventory, setInventory] = useState<(ItemStack | null)[]>(() =>
    new Array<ItemStack | null>(INVENTORY_SIZE).fill(null),
  );
  const [worn, setWorn] = useState<(ItemStack | null)[]>(() =>
    new Array<ItemStack | null>(EQUIPMENT_SLOT_NAMES.length).fill(null),
  );
  /** The tools carried about rather than carried: one for each job. */
  const [toolbelt, setToolbelt] = useState<(ItemStack | null)[]>(() =>
    new Array<ItemStack | null>(TOOLBELT_SLOTS.length).fill(null),
  );
  const [bonuses, setBonuses] = useState<BonusesPacket>(emptyBonuses);
  const [levels, setLevels] = useState<readonly number[]>([]);
  const [experience, setExperience] = useState<readonly number[]>([]);
  /** Which tab the side panel is showing, or null when it is closed. */
  const [tab, setTab] = useState<TabName | null>('inventory');

  // Which panel is showing, read by the sound of opening or shutting one.
  const shownPanels = useRef({ tab, floating, floats: settings.floatingPanels });
  useEffect(() => {
    shownPanels.current = { tab, floating, floats: settings.floatingPanels };
  }, [tab, floating, settings.floatingPanels]);

  /** A panel opening or shutting, heard. */
  const panelSound = useCallback((name: TabName) => {
    const held = shownPanels.current;
    const open = held.floats ? held.floating.includes(name) : held.tab === name;
    sounds.cue(open ? 'close' : 'open');
  }, []);
  /*
   * The tab the dock actually shows. A trade is conducted from the bag, so the
   * bag is what is up for as long as one is open, whichever tab was open before
   * and even if none was; the tab somebody had chosen comes back afterwards.
   */
  const dockTab: TabName | null = trade ? 'inventory' : tab;
  /**
   * Where somebody is headed, and the places they have written down.
   *
   * Kept per character in the browser: a mark on a map is a note about how one
   * person plays rather than a fact the world has to agree with.
   */
  const [marks, setMarks] = useState(() => loadMarks(characterId));
  /** Who is going with them, and where the party is headed. */
  const [party, setParty] = useState<PartyWire>({ leader: '', members: [], point: null });
  /** Their own offers at the exchange, while the window is open. */
  const [exchange, setExchange] = useState<ExchangeWire | null>(null);
  /** The guild they are in, whoever is in it, and anything they have been asked. */
  const [guild, setGuild] = useState<GuildWire>({
    name: '',
    motd: '',
    rank: '',
    members: [],
    asked: null,
  });
  /** This character's own name, for telling their row from the others. */
  const [myName, setMyName] = useState('');

  const changeMarks = useCallback(
    (next: { waypoint: Waypoint | null; markers: MapMarker[] }) => {
      setMarks(next);
      saveMarks(characterId, next);
    },
    [characterId],
  );

  const [gearOpen, setGearOpen] = useState(false);

  /**
   * Whether the kit sits against the bag or floats free.
   *
   * Attached by default, which is the layout the old game taught everybody:
   * the bag in the corner and what you are wearing beside it. Remembered,
   * because somebody who has taken it off did that on purpose.
   */
  const [gearAttached, setGearAttached] = useState(() => {
    try {
      return window.localStorage.getItem('inverse.gear.detached') !== 'yes';
    } catch {
      return true;
    }
  });

  const detachGear = useCallback(() => {
    setGearAttached((held) => {
      try {
        window.localStorage.setItem('inverse.gear.detached', held ? 'yes' : 'no');
      } catch {
        // Private browsing. It still works, it just forgets.
      }

      return !held;
    });
  }, []);
  const [bank, setBank] = useState<(ItemStack | null)[]>([]);
  const [bankOpen, setBankOpen] = useState(false);
  const [shop, setShop] = useState<ShopStatePacket | null>(null);
  /** Whether the touch interface is drawn, from the setting and what the device is. */
  const touch = usingTouch(settings);

  /**
   * The same panel, following the pointer, when it has been asked to.
   *
   * Written straight onto the element rather than through state on purpose. A
   * thing pinned to a cursor is judged entirely on whether it keeps up, and
   * going through React means a render, a reconcile and a paint for every
   * pixel: at speed it visibly trails. This listens for the raw pointer stream,
   * which reports faster than a frame where the browser has it, and sets one
   * transform. Nothing above it re-renders at all.
   *
   * It goes out of the way over anything that is not the world, since it says
   * what the pointer is on and over a panel the answer is the panel.
   */
  const onPointer = settings.targetOnPointer && !touch;
  const followRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const held = followRef.current;
    if (!onPointer || !held) return;

    // Just above and to the right, far enough that the pointer never sits on
    // top of the first letter and near enough that both are one glance.
    const RIGHT = 18;
    const ABOVE = 30;

    const move = (event: PointerEvent): void => {
      const overWorld = (event.target as Element | null)?.tagName === 'CANVAS';
      held.classList.toggle('away', !overWorld);
      if (!overWorld) return;

      // Turned back before the right edge and dropped below the pointer near
      // the top, so it is never half off the screen.
      const width = held.offsetWidth;
      const height = held.offsetHeight;
      const x = Math.min(event.clientX + RIGHT, window.innerWidth - width - 8);
      const y =
        event.clientY - ABOVE < 8 ? event.clientY + ABOVE : event.clientY - ABOVE - height + 24;

      held.style.transform = `translate3d(${Math.max(8, x)}px, ${Math.max(8, y)}px, 0)`;
    };

    const gone = (): void => held.classList.add('away');

    // The raw stream where it exists, which is not coalesced to the frame, and
    // the ordinary one where it does not.
    const raw = 'onpointerrawupdate' in window ? 'pointerrawupdate' : 'pointermove';
    window.addEventListener(raw, move as EventListener);
    window.addEventListener('pointerleave', gone);

    return () => {
      window.removeEventListener(raw, move as EventListener);
      window.removeEventListener('pointerleave', gone);
      held.classList.remove('away');
      held.style.transform = '';
    };
  }, [onPointer]);

  /**
   * Handing the element to both the drag and the follower.
   *
   * Held still with a callback of its own, because a ref callback written
   * inline is a new function every render and React calls a new one again: the
   * drag hook sets state when it is handed an element, so an inline ref here
   * was a render loop, and the whole interface stopped.
   */
  const holdTarget = useCallback(
    (held: HTMLDivElement | null) => {
      followRef.current = held;
      // Only where it stays put. Attaching the drag while it is on the pointer
      // would have the stored position fighting the transform.
      if (!onPointer) attachTarget(held);
    },
    [onPointer, attachTarget],
  );

  /**
   * Whether there is not much screen, which is a different question from touch.
   *
   * Watched rather than read once: a browser window is resized, a phone is
   * turned on its side, and the interface has to keep up with both.
   */
  const [small, setSmall] = useState(() => isSmallScreen());

  /**
   * Whether Alt is being held, which is what puts the interface in the mood to
   * be rearranged: ghosts where the things that come and go sit, and grips on
   * the windows that can be resized.
   */
  const altHeld = useAltHeld();

  /**
   * A moment of black, for something the world moved out of sight.
   *
   * A character sent to another spot while somebody is watching would otherwise
   * be seen to teleport. The screen dips and comes back, and by then they are
   * where they are meant to be.
   */
  const [blinking, setBlinking] = useState(false);

  // Said on the document rather than on one panel, because what it changes is
  // spread across the whole interface: the corner map has its own root, and the
  // grips on it have to know as much as the panels inside the hud do.
  useEffect(() => {
    document.body.classList.toggle('ui-mode', altHeld);
    return () => document.body.classList.remove('ui-mode');
  }, [altHeld]);

  useEffect(() => {
    const look = (): void => setSmall(isSmallScreen());
    window.addEventListener('resize', look);
    return () => window.removeEventListener('resize', look);
  }, []);
  /** Set while the chat box is addressed to one player rather than everybody. */
  const [whisperTo, setWhisperTo] = useState<string | null>(null);

  /**
   * Who is written down, as the world last said.
   *
   * Sent whole whenever it changes, so there is nothing to keep in step here:
   * what arrives is what is true.
   */
  const [friends, setFriends] = useState<FriendsPacket>({
    friends: [],
    ignored: [],
    asking: [],
    asked: [],
  });

  /*
   * The same lists, where the game can read them without a render.
   *
   * The menu over somebody's head is built by the game when the pointer stops,
   * which is not a moment React knows about: a callback that closed over the
   * state would be answering with whatever the lists were when the callbacks
   * were last built.
   */
  const friendsRef = useRef(friends);
  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  /**
   * Everybody a private word has passed with, newest first.
   *
   * Read off the log rather than kept beside it, so a conversation appears the
   * moment the first line does and goes when the log rolls past it.
   */
  const conversations = useMemo(() => {
    const held: string[] = [];
    for (let at = lines.length - 1; at >= 0; at--) {
      const who = lines[at]?.with;
      if (who && !held.includes(who)) held.push(who);
    }
    return held.slice(0, 8);
  }, [lines]);

  /** What the tab that is showing has in it. */
  const showingLines = useMemo(() => {
    if (chatTab === 'all') return lines;

    return lines.filter((line) => {
      if (SCOPE_TAB[line.scope] !== chatTab) return false;
      if (chatTab !== 'private' || talkingWith === null) return true;
      return line.with === talkingWith;
    });
  }, [lines, chatTab, talkingWith]);

  const addLine = useCallback(
    (
      who: string | null,
      text: string,
      act?: ChatLine['act'],
      scope: ChatScopeValue = ChatScope.System,
      withWhom?: string,
    ) => {
      // Anything arriving brings the log back to full strength.
      setChatIdle(false);
      setLines((current) => {
        const next = [
          ...current,
          { id: chatIdRef.current++, who, text, act, scope, with: withWhom },
        ];
        // A transcript this long is nobody's scrollback; the moderation log keeps the rest.
        return next.length > 60 ? next.slice(next.length - 60) : next;
      });

      // Marked on the tab it belongs to, unless that is the one being read.
      const belongs = SCOPE_TAB[scope];
      if (belongs && belongs !== chatTabRef.current && chatTabRef.current !== 'all') {
        setUnread((held) => ({ ...held, [belongs]: true }));
      }
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Connect once, on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    const connection = new Connection();
    connectionRef.current = connection;

    const game = new Game(
      canvas,
      connection,
      {
        onSelect: setSelected,
        onRunEnergy: (value, isRunning) => {
          setEnergy(value);
          setRunning(isRunning);
        },
        onOpenChat: () => {
          setWhisperTo(null);
          setChatIdle(false);
          setChatOpen(true);
        },
        onGameSettings: onSettings,
        onContextMenu: setMenu,
        onSystemMessage: (text) => addLine(null, text),
        onUsing: setUsing,
        onCasting: setCasting,
        onReport: setReporting,
        onCrafting: setCrafting,
        onWhisperTo: (name) => {
          setWhisperTo(name);
          setChatOpen(true);
        },
        // The book, opened at whatever was asked about.
        onLookUp: (itemId) => setLookingUp(itemId),
        onMana: (mana, max) => setMana({ current: mana, max }),
        onAskTrade: (name) => connectionRef.current?.sendTrade(TradeAct.Ask, name),
        onAskParty: (name) => connectionRef.current?.sendParty(PartyAct.Ask, name),
        onAskFriend: (name) => {
          // Accepting and asking are the same act as far as this is concerned:
          // the world works out which of the two it is from what it already
          // holds, so the menu does not have to be right about it.
          const held = friendsRef.current;
          const asking = held.asking.some((one) => one.toLowerCase() === name.toLowerCase());
          connectionRef.current?.sendFriends(
            asking ? FriendAct.AcceptFriend : FriendAct.AskFriend,
            name,
          );
        },
        knownAs: (name) => {
          const held = friendsRef.current;
          const same = (one: string): boolean => one.toLowerCase() === name.toLowerCase();

          if (held.friends.some((one) => same(one.name))) return 'friend';
          if (held.asking.some(same)) return 'asking';
          if (held.asked.some(same)) return 'asked';
          return 'none';
        },
        onContainer: (container, slots) => {
          // Copied rather than kept by reference: the game mutates its own array in
          // place, and React would not see a change it can compare.
          if (container === ContainerId.Inventory) setInventory([...slots]);
          if (container === ContainerId.Equipment) setWorn([...slots]);
          if (container === ContainerId.Toolbelt) setToolbelt([...slots]);
          if (container === ContainerId.Bank) setBank([...slots]);
          if (container === ContainerId.Offer) setOffer([...slots]);
          if (container === ContainerId.TheirOffer) setTheirOffer([...slots]);
        },
        onBonuses: (packet) => {
          setBonuses(packet);
          // How they fight comes back from the server, which is where it is kept
          // between sessions.
          setStance(packet.stance);
          setFightingWay(packet.way);
          setRetaliate(packet.retaliate);
        },
        onSkills: (nextLevels, nextExperience) => {
          setLevels([...nextLevels]);
          setExperience([...nextExperience]);
        },
        onTrained: (held) => {
          // Kept in a short list rather than drawn straight away, so several
          // arriving together stack up the side instead of landing on top of
          // one another.
          setEarned((was) => [
            ...was.slice(-11),
            { ...held, id: earnedId.current++, at: Date.now() },
          ]);
          if (held.gained > 0) {
            const now = Date.now();
            setTracked((was) => {
              const had = was.find((one) => one.skill === held.skill);
              if (!had)
                return [
                  ...was,
                  { skill: held.skill, since: now, gained: held.gained, lastAt: now },
                ];
              return was.map((one) =>
                one.skill === held.skill
                  ? { ...one, gained: one.gained + held.gained, lastAt: now }
                  : one,
              );
            });
          }
        },
        onBank: (open) => {
          setBankOpen(open);
          // Somewhere to put things is only useful next to the bag.
          if (open) setTab('inventory');
        },
        onLook: (look) => setMyLook({ skin: look.skin, eyes: look.eyes }),
        onMenu: (packet) => setOpenWindow(packet.menu),
        onHealth: (current, max) => setHealth({ current, max }),
        onDefinitions: (packet) => {
          const byName = (a: { name: string }, b: { name: string }): number =>
            a.name.localeCompare(b.name);
          setCatalogue({
            items: [...packet.items].sort(byName),
            objects: [...packet.objects].sort(byName),
            characters: [...packet.npcs].sort(byName),
            quests: [...packet.quests].sort(byName),
            models: [...packet.models],
            recipes: [...packet.recipes],
            shops: [...packet.shops].sort(byName),
            yields: [...packet.yields],
          });

          // A way of working something that the world has stopped offering is
          // a way the tools should stop showing. The list here is added to as
          // the tools ask about things, so nothing else would ever take one out.
          setGathering((held) =>
            held.filter((entry) => {
              const object = packet.objects.find((one) => one.id === entry.objectId);
              if (!object) return true;

              return object.actions.some(
                (action) => action.toLowerCase() === entry.action.toLowerCase(),
              );
            }),
          );
        },
        onAdmin: (packet) => {
          setRole(toRole(packet.role));
          setOnline(packet.online);
        },
        onTriggers: (packet) => {
          // Written text on the wire, because what a trigger holds is the same
          // conditions and effects a conversation holds and the editor already
          // knows those shapes. Anything unreadable is left out rather than
          // taking the list down with it.
          const held: SavedTrigger[] = [];
          for (const trigger of packet.triggers) {
            try {
              held.push(JSON.parse(trigger.json) as SavedTrigger);
            } catch {
              continue;
            }
          }

          setTriggers(held);
        },
        onAdminFacts: (packet) => {
          // What there is to make things out of, and one item in full. Both are
          // kept here rather than in the panel: they arrive from the connection,
          // which is the one place state is allowed to come from.
          if (packet.kind === 'items') {
            setItemList(readItemList(packet.text.items ?? ''));
            return;
          }

          if (packet.kind === 'item') {
            setItemDetail(readItemDetail(packet));
            if (packet.numbers.saved === 1) {
              setItemSaves((count) => count + 1);
              connection.sendAdminCommand('listItems');
            }
            return;
          }

          /*
           * What one particular thing standing in the world does.
           *
           * Kept by where it stands rather than by what it is, because that is
           * the whole point of it: two levers of the same kind open two
           * different gates.
           */
          if (packet.kind === 'spotActs') {
            setSpotActs({
              tx: packet.numbers.tx ?? 0,
              tz: packet.numbers.tz ?? 0,
              plane: packet.numbers.plane ?? 0,
              own: packet.numbers.own === 1,
              acts: packet.text.acts ?? '[]',
            });
            return;
          }

          if (packet.kind === 'dropTest') {
            try {
              setDropTest({
                npcId: packet.numbers.npc ?? 0,
                kills: packet.numbers.kills ?? 0,
                nothing: packet.numbers.nothing ?? 0,
                items: JSON.parse(packet.text.json ?? '[]') as DropTest['items'],
              });
            } catch {
              setDropTest(null);
            }
            return;
          }

          if (packet.kind === 'quest') {
            try {
              setQuestDetail(JSON.parse(packet.text.json ?? '') as SavedQuest);
            } catch {
              setQuestDetail(null);
            }
            if (packet.numbers.saved === 1) setQuestSaves((count) => count + 1);
            return;
          }

          if (packet.kind === 'modelUsers') {
            try {
              const held: unknown = JSON.parse(packet.text.json ?? '{}');
              if (held && typeof held === 'object') {
                setModelUsers(
                  held as Record<
                    string,
                    { scenery: string[]; items: string[]; characters: string[] }
                  >,
                );
              }
            } catch {
              // A list the tools cannot read is one the panel leaves alone.
            }
            return;
          }

          if (packet.kind === 'groups') {
            try {
              const held: unknown = JSON.parse(packet.text.json ?? '[]');
              if (Array.isArray(held)) setGroups(held as SavedGroup[]);
            } catch {
              // A list the tools cannot read is one the panel leaves alone.
            }
            return;
          }

          if (packet.kind === 'poses') {
            try {
              const held: unknown = JSON.parse(packet.text.json ?? '[]');
              if (Array.isArray(held)) setPoseRows(held as PoseRow[]);
            } catch {
              // A list the tools cannot read is one the panel leaves alone.
            }
            return;
          }

          if (packet.kind === 'characters') {
            setCharacterList(readCharacterList(packet.text.characters ?? ''));
            return;
          }

          if (packet.kind === 'character') {
            setCharacterDetail(readCharacterDetail(packet));

            // The whole definition comes down, conversation included, which is
            // what the Talk window is drawing.
            try {
              const held = JSON.parse(packet.text.json ?? '{}') as {
                id?: number;
                dialogue?: DialogueLine[];
              };

              if (held.id !== undefined) setTalkLines(held.dialogue ?? []);
            } catch {
              // A definition the tools cannot read is one the canvas leaves alone.
            }

            if (packet.numbers.saved === 1) {
              setCharacterSaves((count) => count + 1);
              connection.sendAdminCommand('listCharacters');
            }
            return;
          }

          // The world's own state comes down the same way anything else the
          // tools asked about does.
          // The places there are, which the map window offers as somewhere to go.
          if (packet.kind === 'regions') {
            setPlaces(placesFrom(packet.text.regions ?? ''));
            return;
          }

          if (packet.kind !== 'world') {
            setFacts(packet);
            return;
          }

          setWorld({
            unsaved: packet.numbers.unsaved === 1,
            canUndo: packet.numbers.canUndo === 1,
            canRedo: packet.numbers.canRedo === 1,
            undo: packet.text.undo ?? '',
            redo: packet.text.redo ?? '',
            zones: packet.numbers.zones ?? 0,
            spawns: packet.numbers.spawns ?? 0,
            objects: packet.numbers.objects ?? 0,
            drafting: packet.numbers.drafting === 1,
            draftBy: packet.text.draftBy ?? '',
            draftChanges: packet.numbers.draftChanges ?? 0,
            draftSquares: packet.numbers.draftSquares ?? 0,
            history: readEdits(packet.text.history),
            future: readEdits(packet.text.future),
          });
        },
        onDropTable: setDrops,
        onGathering: (packet) =>
          setGathering((held) => [
            ...held.filter(
              (entry) => entry.objectId !== packet.objectId || entry.action !== packet.action,
            ),
            packet,
          ]),
        onWorldCheck: (packet) => {
          setProblems(packet.problems);
          setProblemCounts(packet.counts);
        },
        onBlink: () => {
          setBlinking(true);
          window.setTimeout(() => setBlinking(false), 620);
        },
        onDialogue: (packet) => setTalking(packet.name.length > 0 ? packet : null),
        onProgress: (packet) => {
          setProgress(packet);

          // Which quest is being followed comes down with the rest of what a
          // player has done, so it is picked up here rather than remembered by
          // the browser. The world's answer is the one that counts: pressing
          // Follow sets it here at once so the panel appears without waiting,
          // and this puts it right a tick later whatever happened.
          const following = packet.flags.find((flag) => flag.name === 'tracking')?.value ?? '';
          setPinnedQuest(following.length > 0 ? following : null);
        },
        onZones: (packet) => setZones(packet.zones),
        onSpawns: (packet) => setSpawns(packet.spawns),
        onShopStock: setShop2,
        onViewChanged: setPanned,
        // The view used to drag the character after it so that whatever is alive
        // out there would be sent. It does not any more: moving somebody's
        // character because they looked somewhere is a surprise, and the button
        // on the bar is there for when it is actually wanted.
        onViewTravelled: () => undefined,
        onShop: (packet) => {
          setShop(packet.open ? packet : null);
          if (packet.open) setTab('inventory');
        },
      },
      settings,
    );
    gameRef.current = game;

    // What a station makes on a left click, worked out from the bag as it is.
    game.stationDefault = (objectId) => {
      const told = stationsRef.current[objectId];
      if (!told) return null;

      const recipe = recipesAt(catalogueRef.current.recipes, objectId).find(
        (entry) => entry.action === told.action,
      );
      if (!recipe) return null;

      const label = defaultLabel(
        recipe,
        inventoryRef.current,
        catalogueRef.current.items,
        told.all,
      );
      if (!label) return null;

      return {
        label,
        action: told.action,
        count: told.all ? 0 : 1,
      };
    };

    const unsubscribe = [
      connection.on('welcome', (packet) => {
        setStatus('playing');
        addLine(null, `Connected to ${packet.shardName}.`);
      }),
      connection.on('chat', (packet) => {
        // The world saying no is heard as well as read.
        if (packet.scope === ChatScope.System && REFUSAL.test(packet.message))
          sounds.cue('refused');
        addLine(packet.name || null, packet.message, undefined, packet.scope);
      }),
      connection.on('privateMessage', (packet) => {
        if (!packet.outgoing) sounds.cue('message');
        // Filed under the other person either way, so a conversation is one
        // conversation rather than two half lists.
        addLine(
          packet.outgoing ? `To ${packet.name}` : `From ${packet.name}`,
          packet.message,
          undefined,
          ChatScope.Private,
          packet.name,
        );
      }),
      connection.on('friends', setFriends),
      connection.on('spellbook', (json) => {
        try {
          setSpellbook(JSON.parse(json) as SpellbookWire);
        } catch {
          // A book that cannot be read is an empty book, not a broken game.
          setSpellbook({ spells: [], prayers: [] });
        }
      }),
      connection.on('devotion', (packet) => setDevotion(packet)),
      connection.on('playerState', (packet) => setMyName(packet.name)),
      connection.on('exchange', (json) => {
        try {
          const held = JSON.parse(json) as ExchangeWire;
          setExchange(held.open ? held : null);
          if (held.open) setTab('inventory');
        } catch {
          setExchange(null);
        }
      }),
      connection.on('guild', (json) => {
        try {
          setGuild(JSON.parse(json) as GuildWire);
        } catch {
          // A guild that cannot be read leaves the last one standing.
        }
      }),
      connection.on('party', (json) => {
        try {
          setParty(JSON.parse(json) as PartyWire);
        } catch {
          // A party that cannot be read leaves the last one standing.
        }
      }),
      connection.on('collection', (json) => {
        try {
          setCollection(JSON.parse(json) as CollectionWire);
        } catch {
          // A log that cannot be read leaves the last one standing.
        }
      }),
      connection.on('tradeState', (packet) => {
        setTrade(packet.open ? packet : null);

        /*
         * The bag is held open for the length of it and everything else is put
         * away. A trade is a conversation about two lists and what somebody is
         * carrying; anything else on screen is something they can be made to
         * press by mistake.
         */
        if (packet.open) {
          setTab('inventory');
          setFloating((held) => (held.includes('inventory') ? held : [...held, 'inventory']));
          setBankOpen(false);
          setWorldMap(false);
          setSystemOpen(false);
        }

        if (!packet.open) {
          setOffer([]);
          setTheirOffer([]);
          if (packet.ended.length > 0) addLine(null, packet.ended);
        }
      }),
      connection.on('asked', (packet) => {
        // One at a time from any one person. Somebody pressing the option twice
        // should not put two of the same request on the screen.
        setAsks((held) => [
          ...held.filter((one) => !(one.kind === packet.kind && one.name === packet.name)),
          { id: askId.current++, kind: packet.kind, name: packet.name, at: Date.now() },
        ]);

        /*
         * And a line in the log for a trade, which the popup does not replace.
         *
         * The popup fades after five seconds and the log does not, so somebody
         * who looked away can still answer. Pressing the line is the same as
         * pressing accept.
         */
        if (packet.kind === AskKind.Trade) {
          addLine(null, `${packet.name} wishes to trade with you.`, {
            label: `Trade with ${packet.name}`,
            run: () => connectionRef.current?.sendTrade(TradeAct.Accept, packet.name),
          });
        }

        if (packet.kind === AskKind.Party) {
          addLine(null, `${packet.name} has asked you into their party.`, {
            label: `Join ${packet.name}`,
            run: () => connectionRef.current?.sendParty(PartyAct.Accept, packet.name),
          });
        }
      }),
      connection.on('worldPlan', (packet) => gameRef.current?.setWorldPlan(packet)),
      // Kept here as well as in the game: the game runs the sky from it, and
      // the tools show it in words. Reading it off the game during a render
      // would be reading a ref while React is drawing.
      connection.on('timeOfDay', (packet) => setClock({ ...packet, heard: Date.now() })),
      connection.on('latency', setLatency),
      connection.on('systemUpdate', (seconds) => {
        setRestartIn(seconds);
        if (seconds > 0) {
          addLine(
            null,
            `The world restarts in ${seconds} seconds. You will reconnect on your own.`,
          );
        }
      }),
      connection.on('rejected', (reason, message) => {
        setProblem(message);
        setStatus('lost');

        // Turned away for being the wrong version cuts both ways: this page may
        // be old, or the server may be, and reloading a page that is already
        // current fixes nothing and looks like a loop. So it asks first, and
        // only fetches when there is something newer to fetch.
        if (reason === RejectReason.BadProtocol) {
          void updateWaiting().then((waiting) => {
            if (waiting) {
              setProblem('This version is out of date. Fetching the current one.');
              void applyUpdate();
              return;
            }

            setProblem(
              'The world is running a different version of the game. It may be part way ' +
                'through an update: try again in a minute.',
            );
          });
        }
      }),
      connection.on('closed', () => {
        if (disposed) return;
        setStatus('lost');
        setProblem((current) => current ?? 'The connection dropped.');
      }),
    ];

    game.start();

    void (async () => {
      try {
        const { ticket } = await api.play(token, characterId);
        if (disposed) return;
        connection.connect(socketUrl(), ticket);
      } catch (error) {
        if (disposed) return;
        if (error instanceof ApiError && error.status === 401) {
          onSessionExpired();
          return;
        }
        setProblem(error instanceof ApiError ? error.message : 'Could not reach the server.');
        setStatus('lost');
      }
    })();

    return () => {
      disposed = true;
      for (const remove of unsubscribe) remove();
      connection.close();
      game.stop();
      gameRef.current = null;
      connectionRef.current = null;
    };
    // Connecting is a one-time effect. Settings changes are pushed separately below,
    // because reconnecting every time somebody flips the camera would be absurd.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gameRef.current?.applySettings(settings);
  }, [settings]);

  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    game.bankOpen = bankOpen;
    game.shopOpen = shop !== null;
  }, [bankOpen, shop]);

  /**
   * One thing kept in the details panel whatever else is selected.
   *
   * Setting where a character roams means holding the character and then drawing
   * an area, and drawing the area is what used to let go of the character. Pin
   * it and it stays, which is the general answer rather than a special case for
   * that one job.
   */
  const [pinned, setPinned] = useState<Pick | null>(null);

  /** Whether the pinned thing is what the details pane is showing. */
  const [showingPinned, setShowingPinned] = useState(false);

  /** Whether the editor is holding the world rather than the player. */
  const editing = editorOpen && atLeast(role, Role.SuperAdmin);

  /**
   * Whether the editor's own panels are on screen.
   *
   * Play is a mode of the editor rather than a way out of it: the bar stays so
   * it can be left again, and everything else gives way to the game, since the
   * whole point of it is to try what was built as a player would meet it.
   */
  const editorPanels = editing && mode !== 'play';

  /**
   * The other spots the held character stands in, drawn where they are.
   *
   * Only while the world is being edited: they are a note to whoever is writing
   * the conditions, not part of the world.
   */
  useEffect(() => {
    const scene = gameRef.current?.scene;
    if (!scene) return;

    if (!editorPanels) {
      scene.hideSpots();
      return;
    }

    const written = facts?.kind === 'spawn' ? (facts.text.spots ?? '[]') : '[]';

    try {
      const parsed: unknown = JSON.parse(written);
      const spots = Array.isArray(parsed)
        ? (parsed as { tx?: unknown; tz?: unknown }[])
            .map((one) => ({
              tx: Math.round(Number(one.tx ?? 0)),
              tz: Math.round(Number(one.tz ?? 0)),
            }))
            .filter((one) => Number.isFinite(one.tx) && Number.isFinite(one.tz))
        : [];

      scene.showSpots(spots, facts?.kind === 'spawn' ? facts.text.model : undefined);
    } catch {
      scene.hideSpots();
    }
  }, [facts, editorPanels]);

  /**
   * Asks about whatever is selected, however it came to be selected.
   *
   * Clicking something in the world asked; picking the same thing out of the
   * outliner did not, so its details opened with nothing to say about what is
   * gathered there. One question, asked wherever the selection came from.
   */
  useEffect(() => {
    if (!editing) return;

    const asked = new Set<number>();
    for (const pick of selection.picks) {
      if (pick.kind !== 'object' || asked.has(pick.objectId)) continue;

      asked.add(pick.objectId);
      // Where it stands as well as what it is, so the panel shows what this
      // particular spot holds rather than what its kind holds.
      connectionRef.current?.sendAdminCommand('inspectGathering', {
        object: pick.objectId,
        tx: pick.tx,
        tz: pick.tz,
      });
    }
  }, [editing, selection]);

  /** The world draws what is held and where the zones are, while there is an editor. */
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;

    game.scene.showZoneOutlines(editing && mode !== 'play');

    // The character is the player's, not the editor's: off screen while the
    // world is being built, and back the moment it is played again.
    game.showLocalCharacter(!editing || mode === 'play');
    if (editing && mode !== 'play') game.detachView();

    // Everything held, whatever shape it came in: a rectangle dragged out, a
    // shape drawn with the lasso, the ground under things picked one at a time,
    // or the tiles of a zone.
    const zoned = selection.picks.flatMap((pick) => {
      if (pick.kind !== 'zone') return [];

      const zone = zones.find((entry) => entry.id === pick.zoneId);
      return zone ? zone.tiles : [];
    });

    game.scene.showSelection([...tilesOf(selection), ...zoned]);

    // Handles on the middle of what is held, so a drag has somewhere obvious to
    // start and the tool says what it would do before anybody tries it.
    const carried = objectPicks(selection);
    const gizmo =
      editing &&
      mode !== 'play' &&
      carried.length > 0 &&
      (tool === 'move' || tool === 'rotate' || tool === 'scale')
        ? pivotOf(carried)
        : null;

    game.scene.showGizmo(gizmo, tool === 'move' ? 'move' : tool === 'rotate' ? 'rotate' : 'scale');

    // Where a stationed character is allowed to wander, drawn while it is the
    // thing being worked on. Without it, setting a roam area is done blind.
    const roam =
      facts?.kind === 'spawn' && facts.numbers.roamWidth !== undefined
        ? {
            tx: facts.numbers.roamTx ?? 0,
            tz: facts.numbers.roamTz ?? 0,
            width: facts.numbers.roamWidth,
            height: facts.numbers.roamHeight ?? 1,
          }
        : null;

    const showing =
      pinned?.kind === 'character' || selection.picks[selection.active]?.kind === 'character';

    // Where whoever is held may wander, drawn on the ground: painted tiles if
    // somebody painted them, the dragged box otherwise, and nothing at all when
    // nobody is held.
    const region = game.state.region;
    const painted =
      facts?.kind === 'spawn' && region
        ? (facts.text.roamTiles ?? '')
            .split(',')
            .map((held) => Number(held))
            .filter((packed) => Number.isFinite(packed) && packed >= 0)
            .map((packed) => ({
              tx: region.originX + (packed % region.width),
              tz: region.originZ + Math.floor(packed / region.width),
            }))
        : [];

    if (!showing) {
      game.scene.showRoam([]);
    } else if (painted.length > 0) {
      game.scene.showRoam(painted);
    } else if (roam) {
      const tiles = [];
      for (let dz = 0; dz < roam.height; dz++) {
        for (let dx = 0; dx < roam.width; dx++) tiles.push({ tx: roam.tx + dx, tz: roam.tz + dz });
      }
      game.scene.showRoam(tiles);
    } else {
      game.scene.showRoam([]);
    }
  }, [editing, mode, tool, selection, zones, facts, pinned]);

  /**
   * Everything the quest mark is worked out from, kept where a timer can read it.
   *
   * Held in a ref rather than read from the render, because the mark has to be
   * worked out again as the world moves and doing that through state redrew the
   * whole interface twice a second. Nothing here changes what is on the screen,
   * so keeping it up to date costs nothing.
   */
  const markingRef = useRef({
    showMarks: settings.showQuestMarks && !editorPanels,
    following: pinnedQuest,
    quests: catalogue.quests,
    recipes: catalogue.recipes,
    steps: progress.quests,
    yields: catalogue.yields,
    inventory,
  });

  useEffect(() => {
    markingRef.current = {
      // The editor takes the world over, so the guides step out of it with the
      // rest of what belongs to playing.
      showMarks: settings.showQuestMarks && !editorPanels,
      following: pinnedQuest,
      quests: catalogue.quests,
      recipes: catalogue.recipes,
      steps: progress.quests,
      yields: catalogue.yields,
      inventory,
    };
  });

  /**
   * Where the followed quest step points, marked in the world.
   *
   * Worked out here because this is where the quest, the step and everything
   * standing in the world are all known: the scene is told a place and draws an
   * arrow over it, and knows nothing about quests.
   *
   * Run on a timer rather than on a redraw, since what it depends on is mostly
   * the world moving about: whoever the step points at walks, and a mark that
   * only moved when the interface happened to redraw would lag behind them.
   */
  const markQuest = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;

    const held = markingRef.current;
    if (!held.showMarks || !held.following) {
      game.scene.showQuestMark(null);
      return;
    }

    const quest = held.quests.find((one) => one.id === held.following);
    const at = held.steps.find((entry) => entry.id === held.following)?.step ?? 0;
    const step = quest?.steps[at - 1];

    if (!step) {
      game.scene.showQuestMark(null);
      return;
    }

    const region = game.state.region;

    game.scene.showQuestMark(
      questTarget(step, game.state.local.pos, {
        characters: (defId) => {
          const found: { x: number; z: number; plane: number; entity: number }[] = [];
          for (const entity of game.state.entities.values()) {
            if (entity.kind === 1 && entity.defId === defId) {
              found.push({ x: entity.x, z: entity.z, plane: entity.plane, entity: entity.id });
            }
          }
          return found;
        },
        scenery: (objectId) => {
          const found: { x: number; z: number; plane: number }[] = [];
          for (const object of region?.objects ?? []) {
            if (object.id !== objectId) continue;

            found.push({
              x: (region?.originX ?? 0) + object.tx + 0.5,
              z: (region?.originZ ?? 0) + object.tz + 0.5,
              plane: object.plane,
            });
          }
          return found;
        },
        yielding: (itemId) =>
          held.yields.filter((pair) => pair.itemId === itemId).map((pair) => pair.objectId),
        making: (itemId) =>
          held.recipes
            .filter((recipe) => recipe.outputId === itemId && recipe.objectId > 0)
            .map((recipe) => recipe.objectId),
        piles: (itemId) =>
          game
            .pilesOf(itemId)
            .map((pile) => ({ x: pile.tx + 0.5, z: pile.tz + 0.5, plane: pile.plane })),
        carrying: (itemId) =>
          held.inventory.reduce(
            (many, slot) => many + (slot && slot.id === itemId ? slot.quantity : 0),
            0,
          ),
      }),
    );
  }, []);

  // At once when any of it changes, so pressing Follow puts a mark up now
  // rather than in half a second.
  useEffect(markQuest, [
    markQuest,
    settings.showQuestMarks,
    editorPanels,
    pinnedQuest,
    catalogue.quests,
    progress.quests,
    inventory,
  ]);

  // And again on a timer while there is one to draw, for everything that moves
  // without the interface hearing about it.
  useEffect(() => {
    if (!settings.showQuestMarks || !pinnedQuest || editorPanels) return;

    const timer = window.setInterval(markQuest, 500);
    return () => window.clearInterval(timer);
  }, [markQuest, settings.showQuestMarks, editorPanels, pinnedQuest]);

  /** The quest being followed, if it is one the world still knows about. */
  const followed = pinnedQuest
    ? (catalogue.quests.find((held) => held.id === pinnedQuest) ?? null)
    : null;

  /**
   * The things the followed step still wants, for the bag to light up.
   *
   * A quest that says to wield the pickaxe is no help at all if the pickaxe is
   * one of twenty eight squares that all look alike, so the square holding it
   * says so until it is where the step asked for it to be.
   */
  const wantedForStep = useMemo(() => {
    if (!followed) return new Set<number>();

    const at = progress.quests.find((entry) => entry.id === followed.id)?.step ?? 0;
    return wantedItems(wantsOf(followed.steps[at - 1], { inventory, worn, toolbelt }));
  }, [followed, progress.quests, inventory, worn, toolbelt]);

  /**
   * Quests that have just begun or just finished, for the notice in the middle.
   *
   * Worked out by watching what the world sends rather than by being told: the
   * progress packet says how far along every quest is, so a quest that was at
   * nought and is now at one has just been taken on.
   */
  const [questNotices, setQuestNotices] = useState<QuestNoticeEntry[]>([]);
  const questSteps = useRef(new Map<string, number>());

  /**
   * When this session started, so logging in is not mistaken for news.
   *
   * How far along every quest is arrives in pieces: a packet of progress, then
   * the list of quests it refers to, and sometimes a second packet of progress
   * behind them. A quest that is only heard about on the second one looks
   * exactly like a quest that has just been taken on, which is why logging in
   * used to announce every quest already begun and every one already finished.
   * Nothing within the first few seconds of arriving is news.
   */
  const settledAt = useRef(0);

  useEffect(() => {
    const before = questSteps.current;
    const now = Date.now();
    const fresh: QuestNoticeEntry[] = [];

    if (settledAt.current === 0) settledAt.current = now;
    const arriving = now - settledAt.current < 5000;

    for (const entry of progress.quests) {
      const was = before.get(entry.id) ?? 0;
      before.set(entry.id, entry.step);
      if (arriving || entry.step === was) continue;

      const quest = catalogue.quests.find((held) => held.id === entry.id);
      if (!quest) continue;

      const finished = entry.step > quest.steps.length;

      // Finished carries the ending and what it gave; begun carries what it is
      // about. Both are the quest's own words rather than anything derived.
      const ending = {
        id: quest.id,
        name: quest.name,
        about: quest.finishedText,
        rewards: quest.rewards,
        kind: 'finished' as const,
        at: now,
      };

      if (was <= 0 && entry.step > 0) {
        fresh.push(
          finished
            ? ending
            : {
                id: quest.id,
                name: quest.name,
                about: quest.summary,
                kind: 'started' as const,
                at: now,
              },
        );
      } else if (finished && was <= quest.steps.length) {
        fresh.push(ending);
      }
    }

    if (fresh.length === 0) return;

    // Put up on the next turn rather than in the middle of this one: a render
    // that sets state while it is still running is a render that runs again.
    const soon = window.setTimeout(() => {
      setQuestNotices((held) => [...held, ...fresh].slice(-3));

      // Followed at once where the quest itself says so, or where somebody has
      // said they always want that, and only when they are not already watching
      // something else.
      const begun = fresh.find((one) => one.kind === 'started');
      const asks = begun
        ? (catalogue.quests.find((one) => one.id === begun.id)?.followed ?? false)
        : false;

      if (begun && !pinnedQuest && (asks || settings.followNewQuests)) followQuest(begun.id);
    }, 0);

    return () => window.clearTimeout(soon);
  }, [progress.quests, catalogue.quests, settings.followNewQuests, pinnedQuest, followQuest]);

  /**
   * Whether the ground has caught up with somebody who has just arrived.
   *
   * Read on a clock rather than during a render: it changes as squares arrive
   * over the next second or so, and the interface only has to know whether to
   * say so.
   */
  const [arriving, setArriving] = useState<{ have: number; want: number; where: string } | null>(
    null,
  );

  useEffect(() => {
    if (status !== 'playing') return;

    // Held for a moment before it is shown, so stepping through a door does not
    // flash a panel at somebody for two frames.
    let waited = 0;

    const look = (): void => {
      const held = gameRef.current?.arriving();
      if (!held || held.ready) {
        waited = 0;
        setArriving(null);
        return;
      }

      waited += 1;
      // The place being arrived in, which the chart knows before the ground
      // under their feet does.
      const view = gameRef.current?.mapView();
      const where = view?.called || view?.region.name || '';
      setArriving(waited > 2 ? { have: held.have, want: held.want, where } : null);
    };

    look();
    const clock = window.setInterval(look, 120);
    return () => window.clearInterval(clock);
  }, [status]);

  /**
   * The arrow at the edge of the screen, kept pointing at what the step wants.
   *
   * Its own frame loop rather than React state: this moves whenever the camera
   * does, which is every frame, and a component that redrew the whole interface
   * that often would cost more than the world underneath it.
   */
  useEffect(() => {
    if (!settings.showQuestMarks || !pinnedQuest || editorPanels) return;

    let frame = 0;

    const draw = (): void => {
      frame = window.requestAnimationFrame(draw);

      const held = pointerRef.current;
      const game = gameRef.current;
      if (!held || !game) return;

      const at = game.scene.questMarkOnScreen(window.innerWidth, window.innerHeight);

      // In view, or nowhere at all: the arrow above the target says the rest,
      // and a second arrow pointing at something already on the screen is
      // clutter rather than help.
      if (!at || at.inView) {
        held.style.opacity = '0';
        return;
      }

      held.style.opacity = '1';
      held.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px) rotate(${at.angle}rad)`;
    };

    frame = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frame);
  }, [settings.showQuestMarks, editorPanels, pinnedQuest]);

  /**
   * Taking an edit back, and putting it again.
   *
   * Whatever was held is let go of first: undoing can take away the very thing
   * the panel is showing, and a selection pointing at something that is no
   * longer there is worse than no selection at all.
   */
  const takeBack = useCallback(() => {
    setSelection(NOTHING_SELECTED);
    connectionRef.current?.sendAdminCommand('undo');
  }, []);

  const putBack = useCallback(() => {
    setSelection(NOTHING_SELECTED);
    connectionRef.current?.sendAdminCommand('redo');
  }, []);

  /**
   * Which way of editing is on.
   *
   * Live is what it has always been: every stroke goes to everybody watching as
   * it happens. Batch holds the work back, so a hill can be raised, ruined and
   * raised again without anybody standing on it watching the ground move.
   */
  const changeEditMode = useCallback((mode: 'live' | 'batch') => {
    setSelection(NOTHING_SELECTED);
    connectionRef.current?.sendAdminCommand('editMode', {}, { mode });
  }, []);

  const publishEdits = useCallback(() => {
    setSaving(true);
    connectionRef.current?.sendAdminCommand('publishEdits');
    window.setTimeout(() => setSaving(false), 2000);
  }, []);

  const discardEdits = useCallback(() => {
    setSelection(NOTHING_SELECTED);
    connectionRef.current?.sendAdminCommand('discardEdits');
  }, []);

  /** Writes the world out, from the toolbar or from the keyboard. */
  const saveWorld = useCallback(() => {
    setSaving(true);
    connectionRef.current?.sendAdminCommand('saveRegion');
    window.setTimeout(() => setSaving(false), 2000);
  }, []);

  /**
   * The region and every content file at once.
   *
   * A shop, a drop table and a renamed tree are each written by their own button
   * on their own panel, which is one way of losing an afternoon for every button
   * that was not pressed.
   */
  const saveEverything = useCallback(() => {
    setSaving(true);
    connectionRef.current?.sendAdminCommand('saveEverything');
    window.setTimeout(() => setSaving(false), 3000);
  }, []);

  // The way the tools are set up is kept between sessions. What is selected and
  // where the view is are not: those belong to the moment.
  useEffect(() => {
    saveEditorSettings({
      mode: mode === 'play' ? remembered.mode : mode,
      tool,
      terrain,
      brush: brushSize,
      strength,
      roundBrush,
      slopeTop,
      vary,
      density,
      filter,
      zoneMax,
      zoneMin,
      zoneSpan,
    });
  }, [
    mode,
    tool,
    terrain,
    brushSize,
    strength,
    roundBrush,
    slopeTop,
    vary,
    density,
    filter,
    zoneMax,
    zoneMin,
    zoneSpan,
    remembered.mode,
  ]);

  // The map is redrawn on a slow beat while the editor is open: the ground
  // changes as it is worked on, and the two marks move constantly.
  useEffect(() => {
    if (!editing) return;

    const sample = (): void => {
      const game = gameRef.current;
      if (!game) return;

      setRegionForMap(game.state.region);

      const here = game.state.local.pos;
      setCharacterTile({ tx: Math.floor(here.x), tz: Math.floor(here.z) });

      const looking = game.viewingTile();
      setViewTile(looking);
    };

    sample();
    const timer = window.setInterval(sample, 1000);
    return () => window.clearInterval(timer);
  }, [editing]);

  /** Everything the toolbar and the details panel ask the editor to do. */

  /**
   * Opening and closing the world editor.
   *
   * One of these rather than one per way in. The system menu and the tools
   * window both open it, and when the second copy of this drifted from the
   * first, one of them left the panel it opened from sitting on top of the
   * editor and the other asked the server nothing.
   */
  const openEditor = useCallback(
    (open: boolean) => {
      setEditorOpen(open);

      if (open) {
        // The window that opened it steps out of the way: the editor takes the
        // screen over, and a panel about the editor sitting on top of the
        // editor helps nobody.
        setAdminOpen(false);
        setSystemOpen(false);

        // Opening it puts it in Place with the Select tool: nothing is waiting
        // to go down, so a click tells you what is already there.
        setMode(remembered.mode);

        // Zones are only ever sent to somebody editing, so they are asked for
        // when the editor opens rather than at login.
        connectionRef.current?.sendAdminCommand('inspectZones');
        connectionRef.current?.sendAdminCommand('worldFacts');
        return;
      }

      setMode('play');
      setSelection(NOTHING_SELECTED);
      setPinned(null);
      setShowingPinned(false);
      gameRef.current?.clearArea();
    },
    [remembered.mode],
  );

  const changeMode = useCallback((next: EditorMode) => {
    setMode(next);
    gameRef.current?.clearArea();

    // Changing what is being worked on puts down what was held: an outline left
    // over from the last job is an outline nobody asked for, and in Play it is
    // an outline drawn over a game somebody is trying to try out.
    setSelection(NOTHING_SELECTED);
    if (next === 'play') {
      setPinned(null);
      setShowingPinned(false);
    }

    // Terrain has no use for most of the tools, and a tool it does not offer
    // stays in hand with its own options showing under a strip that cannot
    // light any of them up.
    if (next === 'terrain') {
      setTool((held) =>
        held === 'select' || held === 'lasso' || held === 'brush' ? held : 'select',
      );
    }
  }, []);

  /** Points the view at whatever is selected. */
  const focusSelection = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;

    const tiles = selection.picks.flatMap((pick) => {
      if (pick.kind === 'object' || pick.kind === 'tile') return [{ tx: pick.tx, tz: pick.tz }];
      if (pick.kind === 'area') return [{ tx: pick.area.tx, tz: pick.area.tz }];

      if (pick.kind === 'character') {
        const entity = game.state.entities.get(pick.entityId);
        return entity ? [{ tx: Math.floor(entity.x), tz: Math.floor(entity.z) }] : [];
      }

      if (pick.kind === 'tiles') return pick.tiles;

      const zone = zones.find((entry) => entry.id === pick.zoneId);
      return zone?.tiles ?? [];
    });

    if (tiles.length === 0) return;

    // The middle of everything held, so focusing on a building looks at the
    // building rather than at one of its walls.
    const tx = Math.round(tiles.reduce((total, tile) => total + tile.tx, 0) / tiles.length);
    const tz = Math.round(tiles.reduce((total, tile) => total + tile.tz, 0) / tiles.length);

    game.lookAt(tx, tz);
    setPanned(true);
  }, [selection, zones]);

  const setZoneTimers = useCallback((min: number, span: number) => {
    setZoneMin(min);
    setZoneSpan(span);
  }, []);

  const chooseToPlace = useCallback((placeable: Placeable) => {
    // Choosing the same thing again puts it down, which is how a tool is let go
    // of without having to find somewhere else to click.
    let held: Placeable | null = null;
    setChosen((current) => {
      held = current?.kind === placeable.kind && current.id === placeable.id ? null : placeable;
      return held;
    });

    // Holding something means placing it. The tool says so rather than leaving
    // Select lit while a click puts a tree down, which was the old way of it and
    // told anybody reading the toolbar exactly the wrong thing.
    setTool(held === null ? 'select' : 'place');

    if (placeable.kind === 'character') {
      setMode('spawns');
      // Choosing somebody who fights is choosing to seed an area with them, and
      // choosing a shopkeeper is choosing to station one. Said out loud by the
      // tabs rather than guessed at behind them.
      setSpawnKind(placeable.fights === true ? 'enemies' : 'people');
    }
    if (placeable.kind !== 'character') {
      setMode(placeable.category === 'building' ? 'build' : 'place');
    }
  }, []);

  /**
   * Does one landscape stroke to every tile that is held.
   *
   * Sent as rows rather than a tile at a time, and all of it under one stroke,
   * so shaping forty tiles is one thing to take back rather than forty.
   */
  /**
   * Puts what is closed to walking back in step with what is standing there.
   *
   * Sent as one box rather than as rows: the whole point is that the world is
   * asked what it thinks about a region, and asking it about one row at a time
   * would give the same answer several times over.
   */
  const fixWalkable = useCallback(() => {
    const connection = connectionRef.current;
    const tiles = tilesOf(selection);
    if (!connection || tiles.length === 0) return;

    const area = areaAround(tiles);
    if (!area) return;

    connection.sendAdminCommand('fixWalkable', { ...area }, {});
  }, [selection]);

  const applyTerrain = useCallback(
    (action: TerrainAction) => {
      const connection = connectionRef.current;
      const tiles = tilesOf(selection);
      if (!connection || tiles.length === 0) return;

      /*
       * A building goes up over the whole of what is held, in one.
       *
       * Sent as its own command rather than as a stroke because what it wants
       * is not a strength and a brush: it is how many storeys, which side the
       * door is on, and whether it has windows and furniture in it.
       */
      const building = buildingKind(action);
      if (building) {
        const area = areaAround(tiles);
        connection.sendAdminCommand(
          'raiseBuilding',
          { ...area, storeys: buildStoreys, facing: buildFacing },
          {
            kind: building,
            windows: buildWindows ? 'yes' : 'no',
            furnish: buildFurnish ? 'yes' : 'no',
          },
        );
        return;
      }

      const stroke = terrainCommand(action, strength, roundBrush, {
        ...slopeWay,
        ...(slopeTop !== null ? { top: slopeTop } : {}),
      });

      // A shape is a shape of the whole thing, and so is a pour. Sent as rows, a
      // ramp would be worked out afresh for every row and come out as a field of
      // steps, and a pond would come out as a row of ponds at a row of levels.
      if (wantsWholeArea(action)) {
        const area = areaAround(tiles);
        connection.sendAdminCommand('paintArea', { ...area, ...stroke.numbers }, stroke.text);
        return;
      }

      const mark = strokeMark();

      for (const run of runsOf(tiles)) {
        connection.sendAdminCommand(
          'paintArea',
          { tx: run.tx, tz: run.tz, width: run.width, height: 1, stroke: mark, ...stroke.numbers },
          stroke.text,
        );
      }
    },
    [
      selection,
      strength,
      roundBrush,
      slopeWay,
      slopeTop,
      buildStoreys,
      buildFacing,
      buildWindows,
      buildFurnish,
    ],
  );

  /** Sends an action that acts on everything held at once. */
  const transformSelection = useCallback(
    (action: 'move' | 'turn' | 'scale' | 'remove', value: number, dx = 0, dz = 0) => {
      const held = objectPicks(selection);
      if (held.length === 0) return;

      connectionRef.current?.sendEditorAction({
        action,
        tx: dx,
        tz: dz,
        value,
        picks: held.map((pick) => ({
          tx: pick.tx,
          tz: pick.tz,
          id: pick.objectId,
          rotation: pick.rotation,
        })),
      });

      if (action === 'remove') {
        setSelection(NOTHING_SELECTED);
        return;
      }

      if (action === 'move') {
        setSelection(
          selectionOf(
            held.map<Pick>((pick) => ({ ...pick, tx: pick.tx + dx, tz: pick.tz + dz })),
            true,
          ),
        );
        return;
      }

      // Turning moves things about within the selection, so the selection follows
      // them: the same middle and the same quarter turns the server used, which is
      // why that arithmetic is shared rather than written out twice.
      if (action === 'scale') {
        // Sixteen sixteenths is the size it was modelled at, and the server takes
        // that as an instruction to go back to it rather than to multiply by one.
        // Anything else multiplies, and the selection has to follow: a selection
        // still holding the old size is a copy of it that comes back the wrong
        // size, which is what this used to do.
        const factor = value / 16;
        setSelection(
          selectionOf(
            held.map<Pick>((pick) => ({
              ...pick,
              scale: factor === 1 ? 1 : Math.min(4, Math.max(0.25, (pick.scale ?? 1) * factor)),
            })),
            true,
          ),
        );
        return;
      }

      if (action === 'turn') {
        const centre = pivotOf(held);
        setSelection(
          selectionOf(
            held.map<Pick>((pick) => {
              const turned = turnAround(centre, pick.tx, pick.tz, value);
              return {
                ...pick,
                tx: turned.tx,
                tz: turned.tz,
                rotation: (pick.rotation + value) & 3,
              };
            }),
            true,
          ),
        );
      }
    },
    [selection],
  );

  /**
   * Swaps everything held for whatever the palette has chosen.
   *
   * Changing every tree of one kind for another is the sort of thing that is
   * either one press or an afternoon, and there is no middle.
   */
  /**
   * Changing which tier a placed thing is.
   *
   * The same swap as replacing a selection, aimed at one thing and at a
   * particular kind rather than at whatever the browser is holding: a copper
   * vein becomes an iron one and keeps its place, its angle and its size.
   */
  const swapTier = useCallback(
    (objectId: number) => {
      const held = objectPicks(selection);
      const connection = connectionRef.current;
      if (!connection || held.length === 0) return;

      connection.sendEditorAction({
        action: 'replace',
        tx: 0,
        tz: 0,
        value: objectId,
        picks: held.map((pick) => ({
          tx: pick.tx,
          tz: pick.tz,
          id: pick.objectId,
          rotation: pick.rotation,
        })),
      });

      setSelection((current) => ({
        ...current,
        picks: current.picks.map((pick) => (pick.kind === 'object' ? { ...pick, objectId } : pick)),
      }));
    },
    [selection],
  );

  /** The tiers of whatever kind of scenery this is, itself included. */
  const tiersOf = useCallback(
    (objectId: number) => {
      const definition = catalogue.objects.find((entry) => entry.id === objectId);
      if (!definition?.family) return [];

      return catalogue.objects
        .filter((entry) => entry.family === definition.family)
        .map((entry) => ({ id: entry.id, name: entry.name, tier: entry.tier }));
    },
    [catalogue.objects],
  );

  const replaceSelection = useCallback(() => {
    const held = objectPicks(selection);
    const connection = connectionRef.current;
    if (!connection || held.length === 0 || chosen?.kind !== 'object') return;

    connection.sendEditorAction({
      action: 'replace',
      tx: 0,
      tz: 0,
      value: chosen.id,
      picks: held.map((pick) => ({
        tx: pick.tx,
        tz: pick.tz,
        id: pick.objectId,
        rotation: pick.rotation,
      })),
    });

    setSelection(
      selectionOf(
        held.map<Pick>((pick) => ({ ...pick, objectId: chosen.id })),
        true,
      ),
    );
  }, [selection, chosen]);

  /**
   * Counts what is standing in the world, by kind.
   *
   * A hand-built world is mostly scenery, and scenery is the one thing the lists
   * never showed: there is no way to ask how many of something went down or where
   * the last one ended up. Counted when asked rather than kept up to date, since
   * it is a question somebody asks, not a number anybody watches.
   */
  const takeCensus = useCallback(() => {
    const region = gameRef.current?.state.region;
    if (!region) return;

    const counts = new Map<number, number>();
    for (const object of region.objects) counts.set(object.id, (counts.get(object.id) ?? 0) + 1);

    setCensus(
      [...counts]
        .map(([id, count]) => ({
          id,
          name: catalogue.objects.find((entry) => entry.id === id)?.name ?? `Scenery ${id}`,
          count,
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    );
  }, [catalogue.objects]);

  /**
   * Looks at the next one of a kind, and holds it.
   *
   * Pressing the same row again goes on to the next rather than back to the same
   * one, which is what makes a list of counts into a way of walking the world.
   */
  const stepToScenery = useCallback((objectId: number) => {
    const game = gameRef.current;
    const region = game?.state.region;
    if (!game || !region) return;

    const standing = region.objects.filter((object) => object.id === objectId);
    if (standing.length === 0) return;

    const at = ((steppedTo.current.get(objectId) ?? -1) + 1) % standing.length;
    steppedTo.current.set(objectId, at);

    const object = standing[at];
    if (!object) return;

    const tx = region.originX + object.tx;
    const tz = region.originZ + object.tz;

    setSelection(
      selectionOf([
        { kind: 'object', tx, tz, objectId, rotation: object.rotation, scale: object.scale },
      ]),
    );
    game.lookAt(tx, tz);
  }, []);

  /**
   * Turns and sizes everything held a little differently.
   *
   * The editor picks the number the arrangement comes out of and sends it, so
   * both sides work out the same result and the selection stays held. Pressing
   * it again picks another number and gives another arrangement.
   */
  const varySelection = useCallback(() => {
    const held = objectPicks(selection);
    const connection = connectionRef.current;
    if (!connection || held.length === 0) return;

    const seed = Math.floor(Math.random() * 0xffff) + 1;

    connection.sendEditorAction({
      action: 'vary',
      tx: 0,
      tz: 0,
      value: seed,
      picks: held.map((pick) => ({
        tx: pick.tx,
        tz: pick.tz,
        id: pick.objectId,
        rotation: pick.rotation,
      })),
    });

    setSelection(
      selectionOf(
        held.map<Pick>((pick) => {
          const varied = variationOf(seed, pick.tx, pick.tz);
          return { ...pick, rotation: varied.turns, scale: varied.scale };
        }),
        true,
      ),
    );
  }, [selection]);

  /** Keeps whatever is held as a piece that can be put down again. */
  const keepAsPiece = useCallback(
    (name: string) => {
      const held = objectPicks(selection);
      if (held.length === 0 || name.trim().length === 0) return;

      const kept = [...pieces, pieceFrom(name.trim().slice(0, 32), held)];
      setPieces(kept);
      savePieces(kept);
    },
    [selection, pieces],
  );

  const forgetPiece = useCallback(
    (name: string) => {
      const kept = pieces.filter((piece) => piece.name !== name);
      setPieces(kept);
      savePieces(kept);
    },
    [pieces],
  );

  /** Puts a copy of everything held down one tile along, and holds the copy. */
  const duplicateSelection = useCallback(() => {
    const held = objectPicks(selection);
    const connection = connectionRef.current;
    if (!connection || held.length === 0) return;

    const cornerX = Math.min(...held.map((pick) => pick.tx));
    const cornerZ = Math.min(...held.map((pick) => pick.tz));

    connection.sendEditorAction({
      action: 'paste',
      tx: cornerX + 1,
      tz: cornerZ + 1,
      value: 0,
      picks: held.map((pick) => ({
        tx: pick.tx - cornerX,
        tz: pick.tz - cornerZ,
        id: pick.objectId,
        rotation: pick.rotation,
      })),
    });

    setSelection(
      selectionOf(
        held.map<Pick>((pick) => ({ ...pick, tx: pick.tx + 1, tz: pick.tz + 1 })),
        true,
      ),
    );
  }, [selection]);

  const copySelection = useCallback(() => {
    const held = objectPicks(selection);
    if (held.length === 0) return;

    // Held relative to the corner of what was taken, so a paste lands as one
    // piece wherever the pointer is rather than back where it came from.
    const cornerX = Math.min(...held.map((pick) => pick.tx));
    const cornerZ = Math.min(...held.map((pick) => pick.tz));

    setClipboard(held.map((pick) => ({ ...pick, tx: pick.tx - cornerX, tz: pick.tz - cornerZ })));
    // Anything the picks carry beyond a tile and a turn goes with them: what a
    // copy is worth is what was done to the thing, not the thing itself.
  }, [selection]);

  /**
   * Takes whatever is held out of the world.
   *
   * Scenery, characters and zones all answer to the same key, since asking
   * somebody to remember which of the three needs which button is asking them
   * to think about the tools rather than the world.
   */
  const deleteSelection = useCallback(() => {
    const connection = connectionRef.current;

    for (const pick of selection.picks) {
      if (pick.kind === 'character') {
        connection?.sendAdminCommand('removeSpawn', {
          ...(pick.entityId !== 0 ? { entity: pick.entityId } : {}),
          ...(pick.tx !== undefined ? { tx: pick.tx, tz: pick.tz ?? 0 } : {}),
        });
      }

      if (pick.kind === 'zone') connection?.sendAdminCommand('removeZone', { zone: pick.zoneId });
    }

    if (objectPicks(selection).length > 0) {
      transformSelection('remove', 0);
      return;
    }

    setSelection(NOTHING_SELECTED);
  }, [selection, transformSelection]);

  const cutSelection = useCallback(() => {
    copySelection();
    deleteSelection();
  }, [copySelection, deleteSelection]);

  const pasteClipboard = useCallback(() => {
    const game = gameRef.current;
    // Wherever the pointer is, or failing that the last thing that was clicked:
    // pressing paste with the pointer resting over a panel is not a reason for
    // nothing at all to happen.
    const tile = game?.hoveredTile() ?? lastPickRef.current;
    if (!game || !tile || clipboard.length === 0) return;

    connectionRef.current?.sendEditorAction({
      action: 'paste',
      tx: tile.tx,
      tz: tile.tz,
      value: 0,
      picks: clipboard.map((pick) => ({
        tx: pick.tx,
        tz: pick.tz,
        id: pick.objectId,
        rotation: pick.rotation,
        scale: pick.scale,
        lift: pick.lift,
      })),
    });
  }, [clipboard]);

  /**
   * Grows the selection along whatever it is joined to.
   *
   * Walls, floors and fences that touch are one thing as far as anybody looking
   * at them is concerned, so selecting one and asking for the rest is how a
   * building gets picked up rather than a wall at a time.
   */
  const selectConnected = useCallback(() => {
    const game = gameRef.current;
    const region = game?.state.region;
    if (!game || !region) return;

    const pieces = new Set(
      catalogue.objects.filter((object) => object.piece).map((object) => object.id),
    );

    const key = (tx: number, tz: number): string => `${tx},${tz}`;
    const byTile = new Map<string, Pick[]>();
    for (const object of region.objects) {
      if (!pieces.has(object.id)) continue;

      const tx = region.originX + object.tx;
      const tz = region.originZ + object.tz;
      const bucket = byTile.get(key(tx, tz)) ?? [];
      bucket.push({ kind: 'object', tx, tz, objectId: object.id, rotation: object.rotation });
      byTile.set(key(tx, tz), bucket);
    }

    const queue = objectPicks(selection).filter((pick) => pieces.has(pick.objectId));
    if (queue.length === 0) return;

    const seen = new Set<string>();
    const found: Pick[] = [];

    while (queue.length > 0) {
      const pick = queue.pop();
      if (!pick) break;

      const at = key(pick.tx, pick.tz);
      if (seen.has(at)) continue;
      seen.add(at);

      for (const held of byTile.get(at) ?? []) {
        found.push(held);
        if (held.kind !== 'object') continue;

        for (const [dx, dz] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const beyond = { tx: held.tx + dx, tz: held.tz + dz };
          if (byTile.has(key(beyond.tx, beyond.tz)) && !seen.has(key(beyond.tx, beyond.tz))) {
            queue.push({ ...held, tx: beyond.tx, tz: beyond.tz });
          }
        }
      }
    }

    if (found.length > 0) setSelection(selectionOf(found, true));
  }, [selection, catalogue.objects]);

  // Escape closes the menu, the same key that leaves every other panel.
  useEffect(() => {
    if (!menu) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenu(null);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  /**
   * What the keys do.
   *
   * One listener for everything bound: panels, stances, hitting back, running,
   * chat, and any bag slot somebody has given a key to. Nothing here fires while
   * a line is being typed or while the editor holds the world, because in both
   * of those a key means what that thing says it means.
   */
  useEffect(() => {
    if (status !== 'playing' || editing) return;

    const onKey = (event: KeyboardEvent): void => {
      const typing = document.activeElement;
      if (typing instanceof HTMLInputElement || typing instanceof HTMLTextAreaElement) return;

      const action = actionFor(bindings, keyOf(event), placeOf(event));
      if (!action) return;

      event.preventDefault();

      const slot = slotOf(action);
      if (slot !== null) {
        // Whatever a left click on that slot would do.
        gameRef.current?.useItem(ContainerId.Inventory, slot);
        return;
      }

      switch (action) {
        case 'panel.equipment':
          setGearOpen((open) => !open);
          return;

        case 'panel.spells':
          setSpellsOpen((open) => !open);
          return;

        case 'panel.faith':
          setFaithOpen((open) => !open);
          return;

        case 'panel.collection':
          setCollectionOpen((open) => !open);
          return;

        case 'panel.boards':
          setBoardsFor((held) => (held === null ? myName : null));
          return;

        case 'panel.exchange':
          if (exchange) {
            setExchange(null);
            connectionRef.current?.sendExchange({
              act: ExchangeAct.Close,
              slot: 0,
              itemId: 0,
              selling: false,
              price: 0,
              quantity: 0,
            });
          } else {
            connectionRef.current?.sendExchange({
              act: ExchangeAct.Open,
              slot: 0,
              itemId: 0,
              selling: false,
              price: 0,
              quantity: 0,
            });
          }
          return;

        case 'world.map':
          setWorldMap((open) => !open);
          return;

        case 'panel.inventory':
        case 'panel.combat':
        case 'panel.skills':
        case 'panel.quests':
        case 'panel.friends':
        case 'panel.settings': {
          const name = action.slice('panel.'.length) as TabName;
          panelSound(name);
          if (settings.floatingPanels) {
            raisePanel(name);
            setFloating((held) =>
              held.includes(name) ? held.filter((entry) => entry !== name) : [...held, name],
            );
          } else {
            setTab((current) => (current === name ? null : name));
          }
          return;
        }

        case 'system.menu':
          setSystemOpen((open) => !open);
          return;

        case 'system.chat':
          setWhisperTo(null);
          setChatIdle(false);
          setChatOpen(true);
          return;

        case 'move.run':
          setRunning((on) => {
            gameRef.current?.setRunning(!on);
            return !on;
          });
          return;

        case 'fight.retaliate':
          setRetaliate((on) => {
            connectionRef.current?.sendRetaliate(!on);
            return !on;
          });
          return;

        case 'stance.one':
        case 'stance.two':
        case 'stance.three':
        case 'stance.four': {
          // The keys are the four buttons rather than four particular stances:
          // which stance the second button is depends on what is in your hands.
          const order = ['one', 'two', 'three', 'four'];
          const wanted = order.indexOf(action.slice('stance.'.length));
          if (wanted < 0) return;

          setStance(wanted);
          connectionRef.current?.sendStance(wanted);
          return;
        }

        default:
          return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    status,
    editing,
    bindings,
    settings.floatingPanels,
    raisePanel,
    panelSound,
    exchange,
    myName,
  ]);

  /*
   * Telling the world which requests this player will hear.
   *
   * Sent whenever it changes and once the moment there is a connection, because
   * the setting lives on this machine and the server is what has to act on it.
   */
  useEffect(() => {
    if (status !== 'playing') return;
    connectionRef.current?.sendAllowRequests(
      settings.takeFriendRequests,
      settings.takeTradeRequests,
    );
  }, [status, settings.takeFriendRequests, settings.takeTradeRequests]);

  /**
   * The key a middle clicked slot is waiting for.
   *
   * The next key pressed belongs to that slot. Escape gives up on it and
   * Backspace takes the binding away, the same as in the settings.
   */
  useEffect(() => {
    if (binding === null) return;

    const onKey = (event: KeyboardEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'Escape') {
        setBinding(null);
        return;
      }

      if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;

      bind(`slot.${binding}`, event.key === 'Backspace' ? '' : keyOf(event));
      setBinding(null);
    };

    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [binding, bind]);

  /**
   * Escape opens the system menu.
   *
   * Only when it has nothing else to close first: a right click menu, a shop, a
   * conversation. Escape means "out of this", and the system menu is what is
   * left when there is no this to be out of.
   */
  useEffect(() => {
    if (status !== 'playing' || editing) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (using) {
        gameRef.current?.stopUsing();
        return;
      }
      if (casting) {
        gameRef.current?.stopCasting();
        return;
      }

      if (menu || bankOpen || shop || talking || crafting || allSettingsOpen) return;

      const typing = document.activeElement;
      if (typing instanceof HTMLInputElement || typing instanceof HTMLTextAreaElement) return;

      setSystemOpen((open) => !open);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [status, editing, menu, bankOpen, shop, talking, crafting, allSettingsOpen, using, casting]);

  // How the tools pick a spot in the world and send the camera to one.
  useEffect(() => {
    provideTilePicking(
      (done) => {
        setPickingTile(true);
        gameRef.current?.pickTileOnce((at) => {
          setPickingTile(false);
          done(at);
        });
      },
      (tx, tz) => gameRef.current?.lookAt(tx, tz),
    );
    return () => provideTilePicking(null, null);
  }, []);

  useEffect(() => {
    if (!pickingTile) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') gameRef.current?.cancelTilePick();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickingTile]);

  /**
   * The editor's own keys, in the places every editor puts them.
   *
   * Modes on the number row, tools on Q W E R, the clipboard on the usual
   * letters, Delete to take something out and Escape to put everything down.
   */
  useEffect(() => {
    if (!editing) return;

    const onKey = (event: KeyboardEvent): void => {
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement;
      if (typing) return;

      const numbered = MODES.find((entry) => entry.key === event.key);
      if (numbered && !event.ctrlKey && !event.metaKey) {
        changeMode(numbered.mode);
        return;
      }

      const lettered = TRANSFORM_TOOLS.find(
        (entry) => entry.key.toLowerCase() === event.key.toLowerCase(),
      );
      if (lettered && !event.ctrlKey && !event.metaKey) {
        setTool(lettered.tool);
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        // Find, which is what this key does in every editor there has ever
        // been. Taken from the browser, which would otherwise open its own.
        if (event.key === 'f') {
          event.preventDefault();
          setFinding(true);
          return;
        }

        if (event.key === 'c') copySelection();
        if (event.key === 'x') cutSelection();
        if (event.key === 'v') pasteClipboard();
        if (event.key === 'z') takeBack();
        if (event.key === 'y') putBack();
        if (event.key === 's') {
          event.preventDefault();
          saveWorld();
        }
        if (event.key === 'd') {
          event.preventDefault();
          duplicateSelection();
        }
        return;
      }

      // The arrows nudge whatever is held, a tile at a time, which is the
      // steadiest way to line something up.
      const nudges: Record<string, [number, number]> = {
        ArrowUp: [0, 1],
        ArrowDown: [0, -1],
        ArrowLeft: [1, 0],
        ArrowRight: [-1, 0],
      };

      const nudge = nudges[event.key];
      if (nudge) {
        event.preventDefault();
        transformSelection('move', 0, nudge[0], nudge[1]);
        return;
      }

      if (event.key === 'f') focusSelection();
      if (event.key === 'g') {
        gameRef.current?.followCharacter();
        setPanned(false);
      }
      if (event.key === 'Delete' || event.key === 'Backspace') deleteSelection();
      if (event.key === 'Escape') {
        // Puts down both what is held and what was about to be placed, which is
        // what somebody pressing it wants either way.
        setSelection(NOTHING_SELECTED);
        setChosen(null);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    editing,
    changeMode,
    copySelection,
    cutSelection,
    pasteClipboard,
    deleteSelection,
    focusSelection,
    transformSelection,
    duplicateSelection,
    saveWorld,
    takeBack,
    putBack,
  ]);

  // Escape closes the menu, and while the editor holds the world the keyboard
  // reaches for the same places every editor puts these.
  /** What the toolbar says the current mode is for. */
  const editorHint = chosen
    ? mode === 'build'
      ? `Click a side of a tile, or drag out an area of ${chosen.name}. Escape puts it down.`
      : mode === 'spawns'
        ? `Click to place ${chosen.name}${chosen.fights ? ', which draws a zone' : ''}. Escape puts it down.`
        : `Click to place ${chosen.name}. Escape puts it down.`
    : tool === 'erase'
      ? 'Click anything to take it out of the world'
      : (MODES.find((entry) => entry.mode === mode)?.hint ?? '');

  /**
   * Hands the world to the editor, or gives it back.
   *
   * What a click means is decided here rather than in the world, which only knows
   * that somebody clicked a tile and what was standing on it. Every mode reads
   * the same two events: something was picked, or an area was drawn out.
   */
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;

    if (!editing || mode === 'play') {
      game.editor = null;
      game.clearArea();
      // The world says whether the view moved, so putting it back reports itself
      // rather than being announced from here in the middle of an effect.
      game.followCharacter();
      return;
    }

    const connection = connectionRef.current;

    /** Whether a tile has a floor laid on it, which is what walls line up against. */
    const floors = new Set(
      catalogue.objects.filter((object) => object.floor).map((object) => object.id),
    );
    const hasFloor = (tx: number, tz: number): boolean => {
      const region = game.state.region;
      if (!region) return false;

      return region.objects.some(
        (object) =>
          object.tx === tx - region.originX &&
          object.tz === tz - region.originZ &&
          floors.has(object.id),
      );
    };

    /**
     * Everything the editor could mean by a tile, in the order it was probably
     * meant: whatever the pointer actually landed on, then everything else
     * standing there, then the zones over it, then the ground itself.
     *
     * A wall sits on the edge of a tile and is easy to click past. Listing what
     * is there rather than only what was hit is what makes the tabs across the
     * panel worth having.
     */
    const picksOn = (
      tx: number,
      tz: number,
      objectId: number | null,
      rotation: number,
      npcId: number | null,
    ): Pick[] => {
      const picks: Pick[] = [];
      if (npcId !== null) picks.push({ kind: 'character', entityId: npcId });

      const region = game.state.region;
      const standing = (region?.objects ?? []).filter(
        (object) =>
          region !== null &&
          region !== undefined &&
          region.originX + object.tx === tx &&
          region.originZ + object.tz === tz,
      );

      // The one that was clicked comes first, and everything about it comes
      // from the region rather than from the click: what was done to a thing is
      // written down in the world, and a pick that leaves it behind is a pick
      // that loses it the moment somebody copies or moves the thing.
      const clicked = standing.find(
        (object) => object.id === objectId && object.rotation === rotation,
      );

      const asPick = (object: (typeof standing)[number]): Pick => ({
        kind: 'object',
        tx,
        tz,
        objectId: object.id,
        rotation: object.rotation,
        scale: object.scale,
        lift: object.lift,
        // What it is drawn as, when it wears something other than its kind.
        looks: object.looks,
        // What this one offers, when it has been told: each fishing spot is its
        // own spot, and the panel is about the one being looked at.
        ways: object.ways?.map((way) => ({ action: way.action })),
      });

      if (clicked) picks.push(asPick(clicked));
      else if (objectId !== null) picks.push({ kind: 'object', tx, tz, objectId, rotation });

      for (const object of standing) {
        if (object === clicked) continue;
        picks.push(asPick(object));
      }

      for (const zone of zonesOn(zones, tx, tz)) picks.push({ kind: 'zone', zoneId: zone.id });
      picks.push({ kind: 'tile', tx, tz });
      return picks;
    };

    /** Asks the server about whatever was picked, so the panel has something to show. */
    const askAbout = (picks: readonly Pick[]): void => {
      for (const pick of picks) {
        if (pick.kind === 'object') {
          connection?.sendAdminCommand('inspectGathering', {
            object: pick.objectId,
            tx: pick.tx,
            tz: pick.tz,
          });
          continue;
        }

        if (pick.kind !== 'character') continue;

        const npc = game.state.entities.get(pick.entityId);
        connection?.sendAdminCommand('inspect', { entity: pick.entityId });

        if (npc) {
          connection?.sendAdminCommand('inspectDrops', { npc: npc.defId });
          connection?.sendAdminCommand('inspectShop', { npc: npc.defId });
        }
      }
    };

    /** Takes whatever is under the pointer out of the world. */
    const eraseAt = (
      tx: number,
      tz: number,
      objectId: number | null,
      rotation: number,
      npcId: number | null,
    ): void => {
      if (objectId !== null) {
        connection?.sendAdminCommand('removeObject', { tx, tz, object: objectId, rotation });
        return;
      }

      // Nothing was clicked directly, so whatever is standing there will do. A
      // wall is a small thing to hit and is usually what was meant.
      const region = game.state.region;
      const standing = (region?.objects ?? []).find(
        (object) =>
          region !== null && region.originX + object.tx === tx && region.originZ + object.tz === tz,
      );

      if (standing) {
        connection?.sendAdminCommand('removeObject', {
          tx,
          tz,
          object: standing.id,
          rotation: standing.rotation,
        });
        return;
      }

      if (npcId !== null) connection?.sendAdminCommand('removeSpawn', { entity: npcId });
    };

    game.editor = {
      // A drag only draws a box when nothing is being dragged about instead.
      dragsArea: () => true,

      describe: (objectName, tx, tz) => {
        const where = `${tx}, ${tz}`;

        if (tool === 'erase')
          return objectName ? `Erase ${objectName}` : `Nothing to erase at ${where}`;
        if (chosen) return `Place ${chosen.name}`;

        if (mode === 'terrain') {
          if (tool !== 'brush') return `Mark out ground at ${where}`;

          const named = TERRAIN_ACTIONS.find((entry) => entry.action === terrain)?.label ?? 'Paint';
          return `${named} at ${where}`;
        }

        if (tool === 'lasso') return `Draw over the ground to take in what it covers`;
        if (tool === 'move' && objectPicks(selection).length > 0)
          return 'Drag to move what is held';
        if (tool === 'rotate' && objectPicks(selection).length > 0)
          return 'Drag to turn what is held';
        if (tool === 'scale' && objectPicks(selection).length > 0)
          return 'Drag to resize what is held';

        const zone = zonesOn(zones, tx, tz)[0];
        if (objectName) return `Select ${objectName}`;
        if (zone) return `Select the zone at ${where}`;
        return `Ground at ${where}`;
      },

      onPick: ({ tx, tz, x, z, objectId, objectRotation, npcId, adding, removing }) => {
        lastPickRef.current = { tx, tz };
        const how = howFrom(adding, removing);

        const pickHere = (): void => {
          const picks = picksOn(tx, tz, objectId, objectRotation, npcId);
          const first = picks[0];
          if (!first) return;

          // Ctrl adds to what is held and Shift takes away, which is how a wall
          // at a time becomes a building and how one wall of it is let go of
          // again. On its own a click starts afresh with everything under the
          // pointer, and the tabs across the panel choose between them.
          setSelection((held) =>
            how === 'replace' ? selectionOf(picks) : withPicks(held, [first], how),
          );
          askAbout(picks);
        };

        // The eraser is the whole of what a click means while it is held.
        if (tool === 'erase') {
          eraseAt(tx, tz, objectId, objectRotation, npcId);
          return;
        }

        // The Brush works the ground as it goes. Every other tool in Terrain
        // marks ground out for the Landscape panel, so looking at a hillside
        // cannot reshape it.
        if (mode === 'terrain') {
          const area = brushArea(tx, tz, brushSize);

          if (tool === 'brush') {
            const stroke = terrainCommand(terrain, strength, roundBrush);
            connection?.sendAdminCommand('paintArea', { ...area, ...stroke.numbers }, stroke.text);
          }

          setSelection((held) => withPicks(held, [{ kind: 'area', area }], how));
          setLastArea(area);
          game.markArea(
            { tx: area.tx, tz: area.tz },
            { tx: area.tx + area.width - 1, tz: area.tz + area.height - 1 },
          );
          return;
        }

        // Something chosen in the browser is put down by the Place tool and by
        // nothing else. Holding a tree while the Select tool is lit used to plant
        // one on every click, which is the toolbar saying one thing and the
        // pointer doing another.
        if (tool === 'place' && putDown(chosen, tx, tz, x, z)) return;

        // With a zone held and nothing waiting to be placed, the brush works on
        // the shape of the zone rather than on what is standing there.
        const zoneHeld = zoneUnderEdit();
        if (zoneHeld && mode === 'spawns') {
          const area = brushArea(tx, tz, brushSize);
          connection?.sendAdminCommand(
            'editZone',
            { zone: zoneHeld.id, ...area },
            { mode: zonePaint },
          );
          return;
        }

        // A character held instead of a zone: the brush paints the ground that
        // one is allowed to wander.
        const walker = characterUnderEdit();
        if (walker !== null && mode === 'spawns' && tool === 'brush') {
          const area = brushArea(tx, tz, brushSize);
          connection?.sendAdminCommand(
            'editRoam',
            { entity: walker, ...area },
            { mode: zonePaint, brush: roundBrush ? 'round' : 'square' },
          );
          return;
        }

        pickHere();
      },

      onArea: (from, to, modifiers = { adding: false, removing: false }) => {
        const area = areaBetween(from, to);
        const how = howFrom(modifiers.adding, modifiers.removing);

        setLastArea(area);

        // Drawing where somebody may wander. Nothing else happens: what is held
        // stays held, so the panel that armed this is still the panel in front
        // of whoever drew it.
        if (roamForRef.current > 0) {
          connectionRef.current?.sendAdminCommand('setSpawn', {
            entity: roamForRef.current,
            roamTx: area.tx,
            roamTz: area.tz,
            roamWidth: area.width,
            roamHeight: area.height,
          });

          setRoamFor(0);
          return;
        }

        // Which way it was dragged, kept so a ramp can face that way without
        // anybody having to say so a second time.
        if (to.tx !== from.tx || to.tz !== from.tz) {
          setSlopeWay({ x: to.tx - from.tx, z: to.tz - from.tz });
        }

        const zone = zoneUnderEdit();
        const holding = objectPicks(selection).length > 0;
        const meaning = dragMeans(tool, mode, holding, chosen !== null);

        if (meaning === 'paint') {
          setSelection((held) => withPicks(held, [{ kind: 'area', area }], how));

          const stroke = terrainCommand(terrain, strength, roundBrush, {
            x: to.tx - from.tx || 1,
            z: to.tz - from.tz,
            ...(slopeTop !== null ? { top: slopeTop } : {}),
          });
          connection?.sendAdminCommand('paintArea', { ...area, ...stroke.numbers }, stroke.text);
          return;
        }

        if (meaning === 'build' && chosen?.kind === 'object') {
          const building =
            restsOpen && (chosen.opensTo ?? 0) > 0 ? (chosen.opensTo as number) : chosen.id;

          // One tile thick builds a line, anything wider builds the outside of
          // what was drawn, and a floor fills it. The server decides which,
          // because it is the one that knows what the piece is.
          connection?.sendAdminCommand('buildArea', { ...area, object: building });
          return;
        }

        if (meaning === 'zone' && zone) {
          connection?.sendAdminCommand('editZone', { zone: zone.id, ...area }, { mode: zonePaint });
          return;
        }

        // Dragging out a box with a character held does to its ground what the
        // brush does a tile at a time.
        const walking = characterUnderEdit();
        if (walking !== null && mode === 'spawns' && (tool === 'brush' || tool === 'lasso')) {
          connection?.sendAdminCommand(
            'editRoam',
            { entity: walking, ...area },
            { mode: zonePaint, brush: 'square' },
          );
          return;
        }

        // In Terrain a drag marks out ground and nothing else. What is standing
        // on it is not what a landscape stroke works on, and holding a fence
        // while pressing Raise is the confusion this mode is meant to be free of.
        if (mode === 'terrain') {
          setSelection((held) => withPicks(held, [{ kind: 'area', area }], how));
          return;
        }

        // A marquee takes in everything inside it that the filter allows.
        const region = game.state.region;
        if (!region) return;

        const wanted = region.objects
          .filter((object) => {
            const tx = region.originX + object.tx;
            const tz = region.originZ + object.tz;
            if (!areaHolds(area, tx, tz)) return false;

            const definition = catalogue.objects.find((entry) => entry.id === object.id);
            if (filter === 'building') return definition?.piece === true;
            if (filter === 'scenery') return definition?.piece !== true;
            return true;
          })
          .map<Pick>((object) => ({
            kind: 'object',
            tx: region.originX + object.tx,
            tz: region.originZ + object.tz,
            objectId: object.id,
            rotation: object.rotation,
            scale: object.scale,
          }));

        const marked: Pick[] = wanted.length > 0 ? wanted : [{ kind: 'area', area }];
        setSelection((held) =>
          how === 'replace' ? selectionOf(marked, true) : withPicks(held, marked, how),
        );
      },

      // A brush paints along where it went, the same way a lasso keeps where it
      // went: neither of them is a box, and drawing one round a stroke was the
      // thing that made the Brush feel like a selection tool.
      dragsTrail: () => tool === 'lasso' || (tool === 'brush' && mode === 'terrain'),

      // The brush works as it is drawn. Waiting for the pointer to come up
      // means drawing blind and finding out afterwards.
      paintsAsDrawn: () => tool === 'brush' && mode === 'terrain',

      onTrailStep: (tiles) => {
        if (tool !== 'brush' || mode !== 'terrain') return;

        const stroke = (strokeRef.current ??= { mark: strokeMark(), done: new Set<string>() });

        // Where the stroke started and where it has got to, for a ramp drawn
        // rather than dragged out.
        const started = tiles[0];
        const ended = tiles[tiles.length - 1];
        if (started && ended) {
          wayRef.current = { from: wayRef.current?.from ?? started, to: ended };
        }

        const reach = Math.floor(Math.max(1, brushSize) / 2);
        const fresh: { tx: number; tz: number }[] = [];

        for (const tile of tiles) {
          for (let dz = -reach; dz <= reach; dz++) {
            for (let dx = -reach; dx <= reach; dx++) {
              if (roundBrush && Math.hypot(dx, dz) > reach + 0.35) continue;

              const at = { tx: tile.tx + dx, tz: tile.tz + dz };
              const key = `${at.tx},${at.tz}`;
              if (stroke.done.has(key)) continue;

              stroke.done.add(key);
              fresh.push(at);
            }
          }
        }

        if (fresh.length === 0) return;

        // A ramp, a dish, a mound, a flattening and every pour are worked out
        // from a whole piece of ground. Drawn a row at a time a ramp comes out
        // as a field of steps and a pond as a row of ponds at a row of levels,
        // so the stroke is gathered here and done once when it ends.
        if (wantsWholeArea(terrain) && terrain !== 'smooth') return;

        const what = terrainCommand(terrain, strength, false);
        for (const run of runsOf(fresh)) {
          connection?.sendAdminCommand(
            'paintArea',
            {
              tx: run.tx,
              tz: run.tz,
              width: run.width,
              height: 1,
              stroke: stroke.mark,
              ...what.numbers,
            },
            what.text,
          );
        }
      },

      onTrailDone: () => {
        const stroke = strokeRef.current;
        const way = wayRef.current;
        strokeRef.current = null;
        wayRef.current = null;

        if (way && (way.from.tx !== way.to.tx || way.from.tz !== way.to.tz)) {
          setSlopeWay({ x: way.to.tx - way.from.tx, z: way.to.tz - way.from.tz });
        }

        if (!stroke) return;

        const painted = [...stroke.done].map((key) => {
          const [tx, tz] = key.split(',');
          return { tx: Number(tx), tz: Number(tz) };
        });

        const area = areaAround(painted);
        setLastArea(area);

        // The shape the stroke was gathering, done once over everything it
        // covered and facing the way it was drawn.
        if (wantsWholeArea(terrain) && terrain !== 'smooth' && painted.length > 0) {
          const what = terrainCommand(terrain, strength, roundBrush, {
            x: way ? way.to.tx - way.from.tx || 1 : 1,
            z: way ? way.to.tz - way.from.tz : 0,
            ...(slopeTop !== null ? { top: slopeTop } : {}),
          });

          connection?.sendAdminCommand('paintArea', { ...area, ...what.numbers }, what.text);
        }
      },

      // Only the Move tool carries anything, and only when the drag is not the
      // one that draws a box: holding Ctrl with Move means marking out what to
      // move rather than moving what is marked.
      dragsHeld: () =>
        DRAGGED.includes(tool) && !gameRef.current?.holdingAdd()
          ? objectPicks(selection).map((pick) => ({
              id: pick.objectId,
              tx: pick.tx,
              tz: pick.tz,
              rotation: pick.rotation,
              scale: pick.scale,
            }))
          : [],

      // What the drag would leave behind, so turning and resizing show what they
      // are about to do rather than doing it and letting somebody judge after.
      dragPreview: (from, to) => {
        const held = objectPicks(selection);
        if (held.length === 0) return null;

        if (tool === 'rotate') {
          const way = to.tx - from.tx >= 0 ? 1 : -1;
          const turns = (way + 4) & 3;
          const centre = pivotOf(held);

          return held.map((pick) => {
            const turned = turnAround(centre, pick.tx, pick.tz, turns);

            // A wall turns in eighths, because it can stand corner to corner as
            // well as along a side. Everything else turns in quarters: a chest
            // at forty five degrees to the tile it is on looks dropped.
            const part = catalogue.objects.find((object) => object.id === pick.objectId);

            return {
              id: pick.objectId,
              tx: turned.tx,
              tz: turned.tz,
              rotation: turnedBy(pick.rotation, way, part?.barrier === 'wall'),
              scale: pick.scale,
            };
          });
        }

        if (tool === 'scale') {
          const factor = to.tz - from.tz >= 0 ? 20 / 16 : 13 / 16;
          return held.map((pick) => ({
            id: pick.objectId,
            tx: pick.tx,
            tz: pick.tz,
            rotation: pick.rotation,
            scale: Math.min(4, Math.max(0.25, (pick.scale ?? 1) * factor)),
          }));
        }

        return null;
      },

      // Select and the tools that act on what is selected point at things. The
      // brush, the eraser and Place point at ground, except that the eraser is
      // aimed at a particular thing and wants to say which.
      outlines: () => tool !== 'brush' && !(tool === 'place' && chosen !== null),

      ghostAt: (tx, tz) => {
        if (tool !== 'place' || !chosen || chosen.kind !== 'object') return [];

        return [{ id: chosen.id, tx, tz, rotation: 0 }];
      },

      // A brush only means something where a stroke covers ground: sculpting, or
      // planting a wood. Everything else works on one thing at a time.
      erasing: () => tool === 'erase',

      brushWidth: () =>
        mode === 'terrain' || (mode === 'place' && chosen !== null) ? brushSize : 1,

      onTrail: (trail, modifiers = { adding: false, removing: false }) => {
        const region = game.state.region;
        if (!region) return;

        const how = howFrom(modifiers.adding, modifiers.removing);

        // The Brush works the ground it was drawn over, widened by whatever the
        // brush is set to, and the whole stroke is one thing to take back.
        if (tool === 'brush' && mode === 'terrain') {
          const covered = new Map<string, { tx: number; tz: number }>();
          const reach = Math.floor(Math.max(1, brushSize) / 2);

          for (const tile of trail) {
            for (let dz = -reach; dz <= reach; dz++) {
              for (let dx = -reach; dx <= reach; dx++) {
                if (roundBrush && Math.hypot(dx, dz) > reach + 0.35) continue;
                covered.set(`${tile.tx + dx},${tile.tz + dz}`, {
                  tx: tile.tx + dx,
                  tz: tile.tz + dz,
                });
              }
            }
          }

          const stroke = terrainCommand(terrain, strength, false);

          // The same rule the panel follows: what is worked out from the ground
          // as a whole is sent as a whole, however it was drawn.
          if (wantsWholeArea(terrain)) {
            const area = areaAround([...covered.values()]);
            connection?.sendAdminCommand('paintArea', { ...area, ...stroke.numbers }, stroke.text);
            return;
          }

          const mark = strokeMark();

          for (const run of runsOf([...covered.values()])) {
            connection?.sendAdminCommand(
              'paintArea',
              {
                tx: run.tx,
                tz: run.tz,
                width: run.width,
                height: 1,
                stroke: mark,
                ...stroke.numbers,
              },
              stroke.text,
            );
          }

          return;
        }

        // What was drawn is a loop, not a line: the ends are joined and the
        // inside is filled in, which is the difference between a lasso and a
        // brush and the reason for having both.
        const tiles = insideLoop(trail);
        const covered = new Set(tiles.map((tile) => `${tile.tx},${tile.tz}`));

        // In Terrain a drawn shape is ground to work on rather than ground to
        // work: the Landscape panel does the working, so a stray loop shapes
        // nothing on its own.
        if (mode === 'terrain') {
          setSelection((held) => withPicks(held, [{ kind: 'tiles', tiles }], how));
          setLastArea(areaAround(tiles));

          const first = trail[0];
          const last = trail[trail.length - 1];
          if (first && last && (first.tx !== last.tx || first.tz !== last.tz)) {
            setSlopeWay({ x: last.tx - first.tx, z: last.tz - first.tz });
          }

          return;
        }

        // With a zone being worked on, the shape drawn is the shape of the zone,
        // which is the whole reason for drawing one by hand. Sent as rows and
        // under one stroke, so it is one thing to take back.
        const zone = zoneUnderEdit();
        if (mode === 'spawns' && zone) {
          const mark = strokeMark();
          for (const run of runsOf(tiles)) {
            connection?.sendAdminCommand(
              'editZone',
              {
                zone: zone.id,
                tx: run.tx,
                tz: run.tz,
                width: run.width,
                height: 1,
                stroke: mark,
              },
              { mode: zonePaint },
            );
          }
          return;
        }

        const wanted = region.objects
          .filter((object) => {
            const tx = region.originX + object.tx;
            const tz = region.originZ + object.tz;
            if (!covered.has(`${tx},${tz}`)) return false;

            const definition = catalogue.objects.find((entry) => entry.id === object.id);
            if (filter === 'building') return definition?.piece === true;
            if (filter === 'scenery') return definition?.piece !== true;
            return true;
          })
          .map<Pick>((object) => ({
            kind: 'object',
            tx: region.originX + object.tx,
            tz: region.originZ + object.tz,
            objectId: object.id,
            rotation: object.rotation,
            scale: object.scale,
          }));

        // Nothing standing inside the loop still means the loop: the ground it
        // covers is what was asked for.
        if (wanted.length === 0) {
          setSelection((held) => withPicks(held, [{ kind: 'tiles', tiles }], how));
          setLastArea(areaAround(tiles));
          return;
        }

        setSelection((held) =>
          how === 'replace' ? selectionOf(wanted, true) : withPicks(held, wanted, how),
        );
      },

      onDrag: (from, to) => {
        // With Ctrl held, a drag marks out rather than moves, so a selection can
        // be gathered without putting the Move tool down first.
        if (tool === 'move' && game.holdingAdd()) return false;

        const held = objectPicks(selection);
        if (held.length === 0) return false;

        const picks = held.map((pick) => ({
          tx: pick.tx,
          tz: pick.tz,
          id: pick.objectId,
          rotation: pick.rotation,
        }));

        if (tool === 'move') {
          const dx = to.tx - from.tx;
          const dz = to.tz - from.tz;
          if (dx === 0 && dz === 0) return true;

          connection?.sendEditorAction({ action: 'move', tx: dx, tz: dz, value: 0, picks });
          setSelection(
            selectionOf(
              held.map<Pick>((pick) => ({ ...pick, tx: pick.tx + dx, tz: pick.tz + dz })),
            ),
          );
          return true;
        }

        // Turning and resizing read the direction of the drag rather than how far
        // it went: a quarter turn is a quarter turn however long the arm.
        if (tool === 'rotate') {
          const turns = to.tx - from.tx >= 0 ? 1 : 3;
          transformSelection('turn', turns);
          return true;
        }

        if (tool === 'scale') {
          transformSelection('scale', to.tz - from.tz >= 0 ? 20 : 13);
          return true;
        }

        return false;
      },
    };

    /**
     * Puts whatever the palette is holding into the world.
     *
     * Shared by a click and by something dragged in from the browser, which are
     * the same act with a different pointer behind them: the rules for where a
     * wall faces or how wide a brush plants do not change because the mouse
     * button was already down when it started.
     */
    function putDown(
      what: Placeable | null,
      tx: number,
      tz: number,
      x: number,
      z: number,
    ): boolean {
      if (!what) return false;

      // A door goes down the way it should rest. The browser holds the shut
      // half, since that is the one thing a door is called: which way it sits
      // is a choice about this door, not a different piece of scenery.
      const chosen =
        what.kind === 'object' && restsOpen && (what.opensTo ?? 0) > 0
          ? { ...what, id: what.opensTo as number }
          : what;

      if (chosen.kind === 'piece') {
        const piece = pieces.find((entry) => entry.name === chosen.name);
        if (!piece || !connection) return true;

        connection.sendEditorAction({
          action: 'paste',
          tx,
          tz,
          value: 0,
          picks: piece.parts.map((part) => ({
            tx: part.dx,
            tz: part.dz,
            id: part.id,
            rotation: part.rotation,
          })),
        });
        return true;
      }

      if (mode === 'spawns' && chosen.kind === 'character') {
        if (spawnKind === 'enemies') {
          connection?.sendAdminCommand('createZone', {
            tx,
            tz,
            npc: chosen.id,
            radius: Math.max(1, Math.floor(brushSize / 2) + 1),
            max: zoneMax,
            minTicks: zoneMin,
            maxTicks: zoneMin + zoneSpan,
          });
        } else {
          connection?.sendAdminCommand('placeSpawn', { tx, tz, npc: chosen.id });
        }
        return true;
      }

      /**
       * How high off the ground the next piece goes, or nothing to stand on it.
       *
       * Two ways to be off the ground and they mean different things. A dock is
       * one height for the whole run, set by the first board and kept, so a
       * jetty is level however deep the water under it gets. A hang follows the
       * ground: lights strung down a street stay the same distance over the
       * road as it rises.
       */
      function dockHeight(atX: number, atZ: number): number | undefined {
        if (dock.current.on) {
          const held = dock.current.lift ?? gameRef.current?.buildHeightAt(atX, atZ) ?? 0;
          dock.current.lift = held;
          setDockLift(held);
          return held;
        }

        if (hang <= 0) return undefined;
        return (gameRef.current?.buildHeightAt(atX, atZ) ?? 0) + hang;
      }

      /*
       * Loot left lying where it was clicked.
       *
       * Before the scenery, because it is not scenery: it goes on the region as
       * a pile rather than as a thing standing on a tile, and the hang above
       * puts it on a table rather than on the floor under one.
       */
      if (chosen.kind === 'loot') {
        connection?.sendAdminCommand('placeLoot', {
          tx,
          tz,
          plane: storey,
          item: chosen.id,
          quantity: lootMany,
          emptyFor: lootBack,
          each: lootEach ? 1 : 0,
          ...(hang > 0 ? { lift: hang } : {}),
        });
        return true;
      }

      if (chosen.kind === 'object' && mode === 'build') {
        // A floor takes the tile it was clicked on. Anything that stands on an
        // edge takes the side the pointer was nearest, unless there are floors
        // about, in which case it lines up with them.
        const part = catalogue.objects.find((object) => object.id === chosen.id);
        const rotation = part?.floor
          ? 0
          : wallSide(tx, tz, x - tx, z - tz, (atX, atZ) => hasFloor(atX, atZ));

        const lift = dockHeight(tx, tz);
        connection?.sendAdminCommand('placeObject', {
          tx,
          tz,
          object: chosen.id,
          rotation,
          plane: storey,
          ...(lift === undefined ? {} : { lift }),
        });
        setSelection(selectionOf([{ kind: 'object', tx, tz, objectId: chosen.id, rotation }]));
        return true;
      }

      if (chosen.kind === 'object') {
        const area = brushArea(tx, tz, brushSize);
        if (brushSize === 1) {
          const lift = dockHeight(tx, tz);
          connection?.sendAdminCommand('placeObject', {
            tx,
            tz,
            object: chosen.id,
            rotation: 0,
            plane: storey,
            ...(lift === undefined ? {} : { lift }),
          });
        } else {
          connection?.sendAdminCommand(
            'paintArea',
            { ...area, object: chosen.id, rotation: 0, density, plane: storey },
            { ...(vary ? { vary: 'yes' } : {}), ...(roundBrush ? { brush: 'round' } : {}) },
          );
        }

        setSelection(selectionOf([{ kind: 'object', tx, tz, objectId: chosen.id, rotation: 0 }]));
        return true;
      }

      return false;
    }

    // What a drop from the content browser lands on. Kept where the JSX can
    // reach it, since the canvas is drawn a long way from here and the rules for
    // putting something down live in this effect.
    // A drag out of the browser is its own instruction, so it puts things down
    // whatever tool is lit.
    dropRef.current = (screenX: number, screenY: number, dropped: Placeable | null): void => {
      const at = game.tileAt(screenX, screenY);
      if (at) putDown(dropped ?? chosen, at.tx, at.tz, at.x, at.z);
    };

    /** The zone the panel is showing, when it is showing one. */
    function zoneUnderEdit(): ZoneWire | undefined {
      const pick = selection.picks[selection.active];
      if (!pick || pick.kind !== 'zone') return undefined;
      return zones.find((zone) => zone.id === pick.zoneId);
    }

    /**
     * The character whose ground is being painted, if one is held.
     *
     * The same rule as a zone: what is selected decides what the brush works on,
     * so painting where a goblin may wander is the same gesture as painting the
     * shape of a den.
     */
    function characterUnderEdit(): number | null {
      const pick = selection.picks[selection.active];
      if (!pick || pick.kind !== 'character' || pick.entityId <= 0) return null;
      return pick.entityId;
    }

    return () => {
      game.editor = null;
    };
  }, [
    editing,
    mode,
    tool,
    chosen,
    brushSize,
    terrain,
    strength,
    roundBrush,
    slopeTop,
    restsOpen,
    vary,
    density,
    filter,
    role,
    zones,
    zonePaint,
    zoneMax,
    zoneMin,
    zoneSpan,
    pieces,
    selection,
    transformSelection,
    catalogue.objects,
    spawnKind,
    storey,
    hang,
    lootMany,
    lootBack,
    lootEach,
  ]);

  /**
   * Keeps the camera framed on whatever the interface is not covering.
   *
   * Measured from the panels that are actually on screen rather than from which
   * ones are meant to be open, so it stays right through a layout change, a
   * rotation, or a window being resized.
   */
  useEffect(() => {
    if (status !== 'playing') return;

    const measure = (): void =>
      gameRef.current?.setPlayableArea(playableArea(settings.centreOnPlayer));

    // After the panels have been laid out, not while React is still committing.
    const frame = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
    // Deliberately not keyed on chat: it is not counted as covering the world.
  }, [
    status,
    touch,
    tab,
    gearOpen,
    bankOpen,
    shop,
    restartIn,
    editing,
    mode,
    settings.centreOnPlayer,
    settings.floatingPanels,
    floating,
  ]);

  /**
   * Puts the scenery back when the editor is put down.
   *
   * Hiding it is a way of working, not a setting: nobody wants to play in a
   * world with the trees taken out because of something they did an hour ago.
   */
  useEffect(() => {
    gameRef.current?.showScenery(editing ? sceneryShown : true);
  }, [editing, sceneryShown]);

  /**
   * Asks before a reload throws away world edits.
   *
   * Editing is server side, so an unsaved change is not lost by closing the tab,
   * but it is lost by the next restart, and the person who made it is the only
   * one who knows it is there. The browser will only show its own wording, which
   * is fine: what matters is the pause.
   */
  useEffect(() => {
    if (!editing || !world.unsaved) return;

    const ask = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [editing, world.unsaved]);

  /**
   * Watches for a newer build while the game is open.
   *
   * Offered rather than taken, since dropping somebody out of the world mid
   * fight to install an update is its own kind of rude. The exception is a
   * version the server refuses outright, which is handled where that arrives.
   */
  useEffect(() => watchForUpdates(() => setUpdateReady(true)), []);

  useEffect(() => {
    gameRef.current?.setTyping(chatOpen);
    if (chatOpen) chatInputRef.current?.focus();
  }, [chatOpen]);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [lines]);

  /**
   * Fades the chat log out when it has nothing new to say.
   *
   * It sits over the world and is read in glances rather than watched, so leaving
   * it at full strength costs visibility for nothing. Anything arriving, or the
   * box being opened, brings it back.
   */
  useEffect(() => {
    if (chatOpen) return;

    if (settings.chatFadeSeconds <= 0) return;

    const timer = window.setTimeout(() => setChatIdle(true), settings.chatFadeSeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [lines, chatOpen, settings.chatFadeSeconds]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const sendChat = (event: React.FormEvent): void => {
    event.preventDefault();
    const input = chatInputRef.current;
    const message = input?.value.trim() ?? '';

    if (message) {
      /*
       * Said where the log is open.
       *
       * Standing on the World tab and typing says it to the world; standing on
       * Nearby says it out loud. All and System are neither, so they say it out
       * loud, which is what somebody who has not chosen means.
       */
      const speakingIn =
        chatTab === 'world'
          ? ChatScope.World
          : chatTab === 'guild'
            ? ChatScope.Guild
            : ChatScope.Nearby;

      if (whisperTo) gameRef.current?.whisper(whisperTo, message.slice(0, 120));
      else connectionRef.current?.sendChat(message.slice(0, 120), speakingIn);
    }

    if (input) input.value = '';
    setChatOpen(false);
    setWhisperTo(null);
  };

  const toggleRun = (): void => {
    const next = !running;
    setRunning(next);
    gameRef.current?.setRunning(next);
  };

  const update = (patch: Partial<GameSettings>): void => onSettings({ ...settings, ...patch });

  /**
   * What the tooltip should say, worked out when the hover changes.
   *
   * Held as state rather than read at draw time, because what an item is called
   * comes from the game and reaching into that while React is rendering is
   * reaching into something that is allowed to be mid change.
   */
  const [hoverTip, setHoverTip] = useState<TooltipContent | null>(null);

  useEffect(() => {
    const definitions = gameRef.current?.definitions;

    if (hoveredItem !== null) {
      setHoverTip(itemTooltip(definitions?.item(hoveredItem), `Item ${hoveredItem}`));
      return;
    }

    if (hoveredSlot === null) {
      setHoverTip(null);
      return;
    }

    const stack = inventory[hoveredSlot];
    if (!stack) {
      setHoverTip(null);
      return;
    }

    const many = stack.quantity > 1 ? `${stack.quantity} of them.` : undefined;
    const held = definitions?.item(stack.id);

    // What is in that slot now, so a sword in the bag is read against the sword
    // in hand rather than on its own. Nothing to compare with leaves the tooltip
    // exactly as it was.
    const inSlot =
      held && held.equipSlot >= 0 ? definitions?.item(worn[held.equipSlot]?.id ?? -1) : undefined;

    setHoverTip(itemTooltip(held, `Item ${stack.id}`, many, inSlot));
  }, [hoveredItem, hoveredSlot, inventory, worn]);

  /**
   * What the line in the corner says the pointer is on.
   *
   * The world when the pointer is out there, and the item when it is over a
   * slot in the bag or the bank: the panel is about what is under the pointer,
   * and a bag is as much under it as a tree is.
   */
  const pointingAt = useMemo((): string | null => {
    /**
     * What an item is, and what pressing it would do.
     *
     * The panel says both for everything in the world and used to say only the
     * name for anything in the bag, which made the bag the one place it stopped
     * answering the question it is there to answer.
     */
    const named = (itemId: number): string => {
      const held = catalogue.items.find((item) => item.id === itemId);
      if (!held) return `Item ${itemId}`;

      // While something is held for use, the name on its own: what a click
      // would ordinarily do is not what the next click is going to do, and
      // "Use a hatchet on a log, light it" is two answers at once.
      if (using) return held.name;

      const action = held.actions[0];
      return action ? `${held.name} \u2013 ${action}` : held.name;
    };

    if (hoveredItem !== null) return named(hoveredItem);

    if (hoveredSlot !== null) {
      const stack = inventory[hoveredSlot];
      if (stack) return named(stack.id);
    }

    return selected;
  }, [catalogue.items, hoveredItem, hoveredSlot, inventory, selected, using]);

  /**
   * Levels by the names the recipes use.
   *
   * The skills travel as an array in a fixed order and a recipe names its skill,
   * so this is the one place the two are put side by side.
   */
  /**
   * What the character fights at.
   *
   * The same arithmetic the server uses, run on the levels the client already
   * has, so the number on the panel is the number in the world rather than one
   * that has to be asked for and waited on.
   */
  const combatLevel = useMemo(() => {
    const at = (skill: Skill): number => levels[skill] ?? 1;

    return combatLevelOf({
      attack: at(Skill.Attack),
      strength: at(Skill.Strength),
      defence: at(Skill.Defence),
      constitution: at(Skill.Constitution),
      ranged: at(Skill.Ranged),
      magic: at(Skill.Magic),
      faith: at(Skill.Faith),
    });
  }, [levels]);

  const skillLevels = useMemo(() => {
    const held: Record<string, number> = {};
    for (const [at, name] of SKILL_NAMES.entries()) held[name.toLowerCase()] = levels[at] ?? 1;
    return held;
  }, [levels]);

  useEffect(() => {
    inventoryRef.current = inventory;
  }, [inventory]);

  useEffect(() => {
    catalogueRef.current = { items: catalogue.items, recipes: catalogue.recipes };
  }, [catalogue]);

  useEffect(() => {
    stationsRef.current = stations;
  }, [stations]);

  /** What one panel holds. The bar and a floating window both draw this. */
  const panelBody = (name: TabName): JSX.Element | null => {
    switch (name) {
      case 'inventory':
        return (
          <Inventory
            slots={inventory}
            wanted={wantedForStep}
            using={using?.slot ?? null}
            describe={(itemId) => gameRef.current?.definitions.item(itemId)}
            onUse={(slot) => {
              // With a trade open, a press puts the thing up instead. Doing what
              // it ordinarily does would eat it or wield it, and either of those
              // ends the trade: pressing a slot in a trade window means one
              // thing, and it is not that.
              if (trade) {
                connectionRef.current?.sendTrade(TradeAct.Offer, '', slot, 0);
                return;
              }

              // Something held for use goes on this slot instead of doing whatever
              // clicking this slot would ordinarily do.
              if (gameRef.current?.castOnSlot(slot)) return;
              if (gameRef.current?.useOnSlot(slot)) return;
              gameRef.current?.useItem(ContainerId.Inventory, slot);
            }}
            onMenu={(slot, x, y) =>
              gameRef.current?.openItemMenu(x, y, ContainerId.Inventory, slot)
            }
            onMove={(from, to) => gameRef.current?.moveItem(ContainerId.Inventory, from, to)}
            onBind={touch ? undefined : (slot) => setBinding(slot)}
            onHover={touch ? undefined : setHoveredSlot}
            keys={binding === null ? slotKeys : { ...slotKeys, [binding]: '...' }}
          />
        );

      case 'combat':
        return (
          <Fighting
            way={fightingWay}
            stance={stance}
            onStance={(next) => {
              setStance(next);
              connectionRef.current?.sendStance(next);
            }}
            retaliate={retaliate}
            onRetaliate={(on) => {
              setRetaliate(on);
              connectionRef.current?.sendRetaliate(on);
            }}
            onHoverText={setHoverTip}
          />
        );

      case 'skills':
        return (
          <Skills
            levels={levels}
            experience={experience}
            combatLevel={combatLevel}
            onHoverText={setHoverTip}
            onGuide={setGuideFor}
          />
        );

      case 'quests':
        return (
          <Journal
            quests={catalogue.quests}
            progress={progress.quests}
            flags={progress.flags}
            levels={levels}
            pinned={pinnedQuest}
            onPin={followQuest}
          />
        );

      case 'friends':
        return (
          <Friends
            friends={friends.friends}
            ignored={friends.ignored}
            asking={friends.asking}
            asked={friends.asked}
            onAct={(act, name) => connectionRef.current?.sendFriends(act, name)}
            onMessage={(name) => {
              setWhisperTo(name);
              setChatOpen(true);
            }}
            guild={
              <Guild
                guild={guild}
                me={myName}
                onAct={(act, text) => connectionRef.current?.sendGuild(act, text)}
              />
            }
            party={
              <Party
                party={party}
                me={myName}
                waypoint={marks.waypoint}
                onLeave={() => connectionRef.current?.sendParty(PartyAct.Leave)}
                onShare={(at) => connectionRef.current?.sendParty(PartyAct.Point, '', at)}
                onClearPoint={() => connectionRef.current?.sendParty(PartyAct.Unpoint)}
                onGoTo={(at) => setMarks((held) => ({ ...held, waypoint: at }))}
              />
            }
          />
        );

      case 'settings':
        return (
          <QuickSettings
            settings={settings}
            update={update}
            onOpenAll={() => setAllSettingsOpen(true)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* The world itself, either filling the page or in a window of its own.
          The canvas never changes hands: what moves is the frame round it. */}
      <WorldFrame panelled={settings.gameViewPanel && status === 'playing'}>
        <canvas
          className="world"
          ref={canvasRef}
          onDragOver={(event) => {
            if (!editing) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(event) => {
            if (!editing) return;
            event.preventDefault();
            dropRef.current?.(event.clientX, event.clientY, draggedPlaceable(event.dataTransfer));
          }}
        />
      </WorldFrame>

      {/* One search across everything the world is made of, over whatever is
          open rather than in a window of its own: it is a way in, not a place
          to work. */}
      {editorPanels && finding ? (
        <div
          className="editor-find-over"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) setFinding(false);
          }}
        >
          <EditorFind
            world={{
              items: catalogue.items,
              characters: catalogue.characters,
              objects: catalogue.objects,
              quests: catalogue.quests,
              models: catalogue.models,
              // The ground somebody has named, and the shops there are: both
              // are things a designer looks for by name like anything else.
              places: triggers
                .filter((one) => one.names === true && one.name.trim().length > 0)
                .map((one) => ({
                  id: one.id,
                  name: one.name,
                  width: one.width,
                  height: one.height,
                })),
              shops: catalogue.shops,
            }}
            onClose={() => setFinding(false)}
            onOpen={(one) => {
              setFinding(false);

              // A quest has a window of its own, since it is read as a shape.
              if (one.kind === 'quest') {
                openPanel('quests');
                setMakerQuest(one.id);
                return;
              }

              openPanel('make');

              // The tab it lives on, and the thing itself, so the panel opens
              // on it rather than on the list it is somewhere in.

              if (one.kind === 'scenery') {
                setMakerTab('scenery');
                return;
              }

              // A place is ground rather than a thing made, so it opens where
              // ground is edited and walks the camera to it.
              if (one.kind === 'place') {
                const place = triggers.find((held) => String(held.id) === one.id);
                setFinding(false);
                if (place) {
                  gameRef.current?.walkToTile(
                    place.tx + Math.floor(place.width / 2),
                    place.tz + Math.floor(place.height / 2),
                  );
                }
                return;
              }

              if (one.kind === 'shop') {
                setMakerTab('npc');
                return;
              }

              setMakerTab(one.kind === 'model' ? 'model' : one.kind === 'item' ? 'item' : 'npc');
              setMakerThing({ kind: one.kind, id: one.id, at: Date.now() });
            }}
          />
        </div>
      ) : null}

      {/* Where you are. Not while editing, where the editor has a map of its
          own with the zones and spawns on it. */}
      {status === 'playing' && !editorPanels && settings.showMinimap ? (
        <Minimap
          game={gameRef}
          northUp={settings.minimapNorthUp}
          marks={marks}
          party={party}
          onOpenWorld={() => setWorldMap(true)}
        />
      ) : null}

      {/* The map of everywhere, in play and while editing. Editing is where an
          island is picked up and put somewhere else, which is the whole of
          arranging a world for as long as an island is one region. */}
      {status === 'playing' && worldMap ? (
        <WorldMap
          game={gameRef}
          arranging={editorPanels}
          openOn={arrangeOn}
          openOnLayer={
            arrangeOn ? (places.find((one) => one.id === arrangeOn)?.layer ?? null) : null
          }
          marks={marks}
          party={party}
          onMarks={changeMarks}
          onMove={(region, tx, tz) =>
            connectionRef.current?.sendAdminCommand('moveRegion', { tx, tz }, { region })
          }
          onClose={() => {
            setWorldMap(false);
            setArrangeOn(null);
          }}
        />
      ) : null}

      {/* Somebody asking for something. Always while playing: this is not a
          flourish somebody turns off, it is how a request is answered, and the
          setting that governs it is whether they arrive at all. */}
      {status === 'playing' && !editorPanels ? (
        <LoosePanel
          kind="asks"
          label="Requests"
          showing={asks.length > 0}
          altHeld={altHeld}
          home={(size) => ({
            x: (window.innerWidth - size.width) / 2,
            y: window.innerHeight * 0.24,
          })}
        >
          <Requests
            asks={asks}
            onAnswer={answerAsk}
            onDone={(before) => setAsks((was) => was.filter((one) => one.at >= before))}
          />
        </LoosePanel>
      ) : null}

      {status === 'playing' && !editorPanels && settings.xpTracker ? (
        <LoosePanel
          kind="xp-tracker"
          label="Experience tracker"
          showing={tracked.length > 0}
          altHeld={altHeld}
          home={(size) => ({ x: 16, y: Math.max(80, window.innerHeight * 0.3 - size.height / 2) })}
        >
          <XpTracker
            tracked={tracked}
            experience={experience}
            onForget={(skill) => setTracked((was) => was.filter((one) => one.skill !== skill))}
            onReset={() => setTracked([])}
          />
        </LoosePanel>
      ) : null}

      {/* What was just earned. Only while playing, and only where somebody has
          asked to see it. */}
      {status === 'playing' && !editorPanels && settings.showXpDrops ? (
        <LoosePanel
          kind="xp-drops"
          label="Experience"
          showing={earned.length > 0}
          altHeld={altHeld}
          home={(size) => ({
            x: (window.innerWidth - size.width) / 2,
            y: window.innerHeight * 0.62,
          })}
        >
          <Trained
            earned={earned}
            onDone={(before) => setEarned((was) => was.filter((one) => one.at >= before))}
            onGuide={setGuideFor}
          />
        </LoosePanel>
      ) : null}

      {/* Where a thing comes from and what it is for, opened from an item's own
          menu. The same definitions the guide is built from, asked the other way
          round. */}
      {lookingUp !== null && status === 'playing' ? (
        <Lookup
          itemId={lookingUp}
          shelf={{
            items: catalogue.items,
            objects: catalogue.objects,
            recipes: catalogue.recipes,
            yields: catalogue.yields,
            quests: catalogue.quests,
          }}
          onLookUp={setLookingUp}
          onClose={() => setLookingUp(null)}
        />
      ) : null}

      {/* What a skill is for, opened by pressing it. Built from the definitions
          this client already holds, so a recipe written in the tools turns up
          the moment it is saved. */}
      {guideFor_ && status === 'playing' ? (
        <SkillGuide
          skill={guideFor_}
          level={skillLevels[guideFor_] ?? 1}
          entries={guideFor(guideFor_, skillLevels[guideFor_] ?? 1, {
            recipes: catalogue.recipes,
            items: catalogue.items,
            objects: catalogue.objects,
            quests: catalogue.quests,
            yields: catalogue.yields,
          })}
          onClose={() => setGuideFor(null)}
        />
      ) : null}

      {/* A window a character opened. The world took whatever it cost before
          this arrived, and is listening for an answer only while it is up. */}
      {openWindow === 'appearance' && status === 'playing' && !editorPanels ? (
        <Appearance
          skin={myLook.skin}
          eyes={myLook.eyes}
          onKeep={(skin, eyes) => {
            connectionRef.current?.sendSetLook(skin, eyes);
            setOpenWindow(null);
          }}
          onClose={() => setOpenWindow(null)}
        />
      ) : null}

      {/* The quest being followed, in the corner of the eye. Only while one is
          being followed, since a panel about nothing is a panel in the way. */}
      {status === 'playing' && followed && !editorPanels ? (
        <QuestTracker
          quest={followed}
          step={progress.quests.find((entry) => entry.id === followed.id)?.step ?? 0}
          inventory={inventory}
          worn={worn}
          toolbelt={toolbelt}
          items={catalogue.items}
          marks={settings.showQuestMarks}
          onMarks={(showQuestMarks) => onSettings({ ...settings, showQuestMarks })}
          onStop={() => followQuest(null)}
        />
      ) : null}

      {/* Which way to look for what the step wants, when it is out of view.
          Moved by hand every frame rather than through state: a marker that
          re-rendered the whole interface sixty times a second would cost more
          than the world it is drawn over. */}
      {settings.showQuestMarks && pinnedQuest && status === 'playing' && !editorPanels ? (
        <div className="quest-pointer" ref={pointerRef} aria-hidden>
          <span className="quest-pointer-arrow" />
        </div>
      ) : null}

      {status !== 'playing' ? (
        <div className="screen" style={{ background: 'rgba(10, 11, 13, 0.82)' }}>
          <div className="panel">
            <h1 className="title">Inverse</h1>
            <p className="subtitle">
              {status === 'connecting' ? 'Entering the world' : 'Disconnected'}
            </p>
            {problem ? <p className="error">{problem}</p> : null}
            {status === 'lost' ? (
              <div className="row">
                <button className="primary" type="button" onClick={() => void applyUpdate()}>
                  Reconnect
                </button>
                <button type="button" onClick={onLeave}>
                  Characters
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {status === 'playing' ? (
        <div
          className={`hud${touch ? ' touch' : ''}${small ? ' small' : ''}${
            altHeld ? ' ui-mode' : ''
          }${editing ? ' editing' : ''}${editorPanels ? ' building' : ''}`}
        >
          <LoosePanel
            kind="notices"
            label="Notices"
            showing={questNotices.length > 0}
            altHeld={altHeld}
            home={(size) => ({
              x: (window.innerWidth - size.width) / 2,
              y: window.innerHeight * 0.16,
            })}
          >
            <QuestNotice
              notices={questNotices}
              following={pinnedQuest}
              onFollow={followQuest}
              onOpen={() => {
                if (settings.floatingPanels) {
                  raisePanel('quests');
                  setFloating((held) => (held.includes('quests') ? held : [...held, 'quests']));
                } else {
                  setTab('quests');
                }
              }}
              onDone={(before) => setQuestNotices((held) => held.filter((one) => one.at >= before))}
              onDismiss={(at) => setQuestNotices((held) => held.filter((one) => one.at !== at))}
            />
          </LoosePanel>

          {/* A moment of black, while the world moves somebody out of sight. */}
          {blinking ? <div className="blink" aria-hidden /> : null}

          {/* Arriving somewhere. The ground is asked for and comes a square at
              a time, so for a second there is nothing under somebody's feet:
              better to say the world is coming than to show them a hole. */}
          {arriving ? (
            <div className="arriving" role="status">
              <strong>Arriving</strong>
              <span>{arriving.where || 'Somewhere new'}</span>
              <div className="arriving-bar">
                <div style={{ width: `${Math.round((arriving.have / arriving.want) * 100)}%` }} />
              </div>
            </div>
          ) : null}

          {reporting ? (
            <ReportForm
              name={reporting}
              onSend={(reason, note) => connectionRef.current?.sendReport(reporting, reason, note)}
              onClose={() => setReporting(null)}
            />
          ) : null}

          {pickingTile ? (
            <div className="banner">Click the spot in the world. Escape to stop.</div>
          ) : null}

          {updateReady ? (
            <div className="banner">
              A newer version of the game is ready.{' '}
              <button type="button" className="banner-action" onClick={() => void applyUpdate()}>
                Load it now
              </button>
            </div>
          ) : null}

          {restartIn > 0 ? (
            <div className="banner">
              World restarting in {restartIn}s. You will reconnect automatically.
            </div>
          ) : null}

          {menu ? (
            <ContextMenuView
              menu={menu}
              onClose={() => setMenu(null)}
              onReopen={(x, y) => gameRef.current?.openMenuAt(x, y)}
            />
          ) : null}

          {/* Waiting for the key that will belong to a bag slot. It says so
              rather than leaving somebody pressing keys at a game that has
              quietly stopped listening to them. */}
          {binding !== null ? (
            <div className="binding-note">
              <span>Press a key to assign it to this inventory slot...</span>
              <button type="button" onClick={() => setBinding(null)}>
                Cancel
              </button>
            </div>
          ) : null}

          {/* What the pointer is on. Moved wherever somebody wants it, because
              where it sits is a matter of which corner of their screen they
              look at, and the top left is only a default. */}
          <div
            className={onPointer ? 'hud-top-left on-pointer away' : 'hud-top-left'}
            ref={holdTarget}
            style={onPointer ? undefined : targetStyle}
          >
            <div className="badge" onPointerDown={onPointer ? undefined : dragTarget}>
              {/* The sentence, finished. Holding something and pointing at
                  something else is a whole thought, and stopping at "on..."
                  while the pointer is plainly over a chisel says less than it
                  knows. */}
              <strong>
                {using
                  ? pointingAt
                    ? `Use ${using.name} on ${pointingAt}.`
                    : `Use ${using.name} on...`
                  : casting
                    ? pointingAt
                      ? `Cast ${casting.name} on ${pointingAt}.`
                      : `Cast ${casting.name} on...`
                    : (pointingAt ?? 'Nothing')}
              </strong>
            </div>
            {using ? (
              <button type="button" className="badge" onClick={() => gameRef.current?.stopUsing()}>
                Put it down
              </button>
            ) : null}
            {casting ? (
              <button
                type="button"
                className="badge"
                onClick={() => gameRef.current?.stopCasting()}
              >
                Do not cast it
              </button>
            ) : null}
          </div>

          <div className="hud-top-right">
            <div className="badge">
              <strong>{latency}</strong> ms
            </div>
            {/* Everything that is about the session rather than about the world
                is behind Escape now: the tools, the settings and the way out. */}
            <button
              type="button"
              className={systemOpen ? 'badge on' : 'badge'}
              title="Escape"
              onClick={() => setSystemOpen(!systemOpen)}
            >
              Menu
            </button>
          </div>

          {adminOpen && atLeast(role, Role.Moderator) ? (
            <AdminWindow
              role={role}
              online={online}
              items={catalogue.items}
              editing={editing}
              unsaved={world.unsaved}
              time={timeOfDay}
              myLook={myLook}
              facts={facts}
              openOn={adminSection}
              onEditing={openEditor}
              onCommand={(command, numbers, text) =>
                connectionRef.current?.sendAdminCommand(command, numbers, text)
              }
              onClose={() => setAdminOpen(false)}
            />
          ) : null}

          {editing ? (
            <>
              <EditorToolbar
                mode={mode}
                onMode={changeMode}
                selection={selection}
                hint={editorHint}
                onFind={() => setFinding(true)}
                blocked={settings.showBlocked}
                onBlocked={(showBlocked) => update({ showBlocked })}
                saving={saving}
                world={world}
                panned={panned}
                grid={settings.showGrid}
                scenery={sceneryShown}
                onScenery={setSceneryShown}
                storey={storey}
                onStorey={(next) => {
                  setStorey(next);

                  // Going upstairs takes the roof off, and coming back down puts
                  // it on. Building an upper floor with the storeys above still
                  // drawn is building blind, and turning that off by hand every
                  // time is a step nobody would choose.
                  setRoofOff(next > 0);
                }}
                roofOff={roofOff}
                onRoofOff={setRoofOff}
                docking={docking}
                dockLift={dockLift}
                hang={hang}
                onHang={setHang}
                onDocking={setDocking}
                onNewDock={() => {
                  dock.current.lift = null;
                  setDockLift(null);
                }}
                canTeleport={heldTiles.length > 0}
                onStandHere={() => {
                  // Wherever was marked out, rather than wherever the camera
                  // happens to be pointing: a click on a tile is a clearer way of
                  // saying where than a camera is.
                  const at = heldTiles[0];
                  if (!at) return;

                  connectionRef.current?.sendAdminCommand('teleport', { tx: at.tx, tz: at.tz });
                }}
                onGrid={(grid) => onSettings({ ...settings, showGrid: grid })}
                panels={windows.open}
                onPanel={togglePanel}
                onClose={() => {
                  setEditorOpen(false);
                  setMode('play');
                  setSelection(NOTHING_SELECTED);
                  setPinned(null);
                  setShowingPinned(false);
                  gameRef.current?.clearArea();
                }}
                onFocus={focusSelection}
                onFollow={() => {
                  gameRef.current?.followCharacter();
                  setPanned(false);
                }}
                onUndo={takeBack}
                onRedo={putBack}
                onUndoTo={(steps) => {
                  setSelection(NOTHING_SELECTED);
                  connectionRef.current?.sendAdminCommand('undoTo', { steps });
                }}
                onRedoTo={(steps) => {
                  setSelection(NOTHING_SELECTED);
                  connectionRef.current?.sendAdminCommand('redoTo', { steps });
                }}
                onEditMode={changeEditMode}
                onPublish={publishEdits}
                onDiscard={discardEdits}
                onSave={saveWorld}
                onSaveEverything={saveEverything}
              />

              {editorPanels ? (
                <EditorWindow
                  title="Tools"
                  rect={windows.layout.tools}
                  onRect={(rect) => placeWindow('tools', rect)}
                  className="tools"
                  {...stacked('tools')}
                  fits
                  resizable={false}
                  // No bar over it. A title saying Tools over a row of tools
                  // says nothing, and there is nothing to put away: the ribs
                  // down the side are what it is dragged by.
                  bare
                >
                  <EditorTools
                    mode={mode}
                    tool={tool}
                    onTool={setTool}
                    holding={chosen?.name ?? null}
                    filter={filter}
                    onFilter={setFilter}
                    onSelectConnected={selectConnected}
                    selected={selection.picks.length}
                    brush={brushSize}
                    onBrush={setBrushSize}
                    round={roundBrush}
                    onRound={setRoundBrush}
                    density={density}
                    onDensity={setDensity}
                    vary={vary}
                    onVary={setVary}
                    clipboard={clipboard.length}
                    onCopy={copySelection}
                    onCut={cutSelection}
                    onPaste={pasteClipboard}
                    onDelete={deleteSelection}
                  />
                </EditorWindow>
              ) : null}

              {/* The browser belongs to the mode rather than to the tool. It
                  used to come and go with Place, which meant reaching for the
                  eraser or the select box put away the list of what was being
                  built with, and choosing the next thing meant picking the tool
                  up again first. */}
              {editorPanels ? (
                <EditorWindow
                  title={
                    mode === 'terrain'
                      ? 'Landscape'
                      : mode === 'spawns'
                        ? 'Characters'
                        : mode === 'build'
                          ? 'Building'
                          : 'Content'
                  }
                  note={chosen?.name ?? null}
                  rect={windows.layout.content}
                  onRect={(rect) => placeWindow('content', rect)}
                  onClose={() => (mode === 'terrain' ? changeMode('place') : setTool('select'))}
                  className="content"
                  {...stacked('content')}
                >
                  <EditorPalette
                    mode={mode}
                    objects={catalogue.objects}
                    characters={catalogue.characters}
                    items={catalogue.items}
                    lootMany={lootMany}
                    onLootMany={setLootMany}
                    lootBack={lootBack}
                    onLootBack={setLootBack}
                    lootEach={lootEach}
                    onLootEach={setLootEach}
                    chosen={chosen}
                    onChoose={chooseToPlace}
                    buildStoreys={buildStoreys}
                    onBuildStoreys={setBuildStoreys}
                    buildFacing={buildFacing}
                    onBuildFacing={setBuildFacing}
                    buildWindows={buildWindows}
                    onBuildWindows={setBuildWindows}
                    buildFurnish={buildFurnish}
                    onBuildFurnish={setBuildFurnish}
                    spawnKind={spawnKind}
                    onSpawnKind={setSpawnKind}
                    terrain={terrain}
                    onTerrain={setTerrain}
                    onApply={applyTerrain}
                    onFixWalkable={fixWalkable}
                    slopeTop={slopeTop}
                    onSlopeTop={setSlopeTop}
                    restsOpen={restsOpen}
                    onRestsOpen={setRestsOpen}
                    held={heldTiles.length}
                    brush={brushSize}
                    onBrush={setBrushSize}
                    strength={strength}
                    onStrength={setStrength}
                    roundBrush={roundBrush}
                    onRoundBrush={setRoundBrush}
                    vary={vary}
                    onVary={setVary}
                    density={density}
                    onDensity={setDensity}
                    zoneMax={zoneMax}
                    onZoneMax={setZoneMax}
                    zoneMin={zoneMin}
                    zoneSpan={zoneSpan}
                    onZoneTimers={setZoneTimers}
                    pieces={pieces}
                    onForgetPiece={forgetPiece}
                  />
                </EditorWindow>
              ) : null}

              {editorPanels && selection.picks.length > 0 ? (
                <EditorWindow
                  title="Details"
                  note={selection.picks.length > 0 ? `${selection.picks.length} held` : 'nothing'}
                  rect={windows.layout.details}
                  onRect={(rect) => placeWindow('details', rect)}
                  onClose={() => setSelection(NOTHING_SELECTED)}
                  className="details"
                  {...stacked('details')}
                >
                  <EditorDetails
                    waysAt={(tx, tz, objectId) => {
                      // Read from the world rather than from what was picked:
                      // turning a way on or off arrives as a patch, and a panel
                      // drawn from a snapshot would go on showing what the spot
                      // offered when it was chosen.
                      const region = gameRef.current?.state.region;
                      const found = (region?.objects ?? []).find(
                        (object) =>
                          object.id === objectId &&
                          object.tx === tx - (region?.originX ?? 0) &&
                          object.tz === tz - (region?.originZ ?? 0),
                      );

                      return found?.ways?.map((way) => ({ action: way.action }));
                    }}
                    selection={selection}
                    onActive={(active) => {
                      // Below zero is the pinned tab, which is not one of the
                      // picks and keeps the pane until another tab is chosen.
                      setShowingPinned(active < 0);
                      if (active >= 0) setSelection((held) => ({ ...held, active }));
                    }}
                    pinned={pinned}
                    showingPinned={showingPinned && pinned !== null}
                    tiersOf={tiersOf}
                    onSwapTier={swapTier}
                    shapes={catalogue.objects}
                    onPin={(pick) => {
                      setPinned(pick);
                      setShowingPinned(pick !== null);
                    }}
                    mode={mode}
                    tool={tool}
                    facts={facts}
                    drops={drops}
                    dropTest={dropTest}
                    shop={shopStock}
                    gathering={gathering}
                    spotActs={spotActs}
                    shops={catalogue.shops}
                    items={catalogue.items}
                    characters={catalogue.characters}
                    quests={catalogue.quests}
                    zones={zones}
                    describeObject={(id) => catalogue.objects.find((object) => object.id === id)}
                    places={places}
                    groundAt={(tx, tz) => {
                      const region = gameRef.current?.state.region;
                      if (!region) return 0;
                      return (
                        region.ground?.[
                          (tz - region.originZ) * region.width + (tx - region.originX)
                        ] ?? 0
                      );
                    }}
                    cornersAt={(tx, tz) => {
                      const region = gameRef.current?.state.region;
                      if (!region) return [0, 0, 0, 0];

                      const stride = region.width + 1;
                      const localX = tx - region.originX;
                      const localZ = tz - region.originZ;

                      return [
                        region.heights[localZ * stride + localX] ?? 0,
                        region.heights[localZ * stride + localX + 1] ?? 0,
                        region.heights[(localZ + 1) * stride + localX] ?? 0,
                        region.heights[(localZ + 1) * stride + localX + 1] ?? 0,
                      ];
                    }}
                    areasNow={() => gameRef.current?.state.region?.chart.areas ?? []}
                    areaAt={(tx, tz) => {
                      const region = gameRef.current?.state.region;
                      if (!region) return 0;
                      const localX = tx - region.originX;
                      const localZ = tz - region.originZ;
                      if (localX < 0 || localZ < 0) return 0;
                      if (localX >= region.width || localZ >= region.height) return 0;
                      return region.area[localZ * region.width + localX] ?? 0;
                    }}
                    describeTile={(tx, tz) => {
                      const collision = gameRef.current?.state.collision;
                      if (!collision) return 'unknown';

                      const flags = collision.flagsAt(tx, tz, 0);
                      if ((flags & 64) !== 0) {
                        // Said with its level, because water is the one thing on
                        // a tile whose height is not the ground's: matching a
                        // pool to the river beside it means knowing what the
                        // river is at, and the only other way to find out is to
                        // guess.
                        const region = gameRef.current?.state.region;
                        const level =
                          region === undefined || region === null
                            ? undefined
                            : region.water?.[
                                (tz - region.originZ) * region.width + (tx - region.originX)
                              ];
                        return level === undefined || level === 0
                          ? 'water, at sea level'
                          : `water, surface ${Math.round(level * 100) / 100}`;
                      }
                      if ((flags & 1) !== 0) return 'blocked';
                      if ((flags & 30) !== 0) return 'walled on a side';
                      return 'clear';
                    }}
                    onCommand={(command, numbers, text) =>
                      connectionRef.current?.sendAdminCommand(command, numbers, text)
                    }
                    onTransform={transformSelection}
                    onReplace={replaceSelection}
                    onDuplicate={duplicateSelection}
                    onVary={varySelection}
                    onKeepPiece={keepAsPiece}
                    replacement={chosen?.kind === 'object' ? chosen.name : null}
                    onClear={() => setSelection(NOTHING_SELECTED)}
                    brush={brushSize}
                    lastArea={lastArea}
                    drawingRoam={roamFor > 0}
                    onDrawRoam={(on: boolean) => {
                      const held = activePick(selection);
                      setRoamFor(on && held?.kind === 'character' ? held.entityId : 0);
                    }}
                    zonePaint={zonePaint}
                    onZonePaint={setZonePaint}
                    onTool={setTool}
                    onTalk={openConversation}
                  />
                </EditorWindow>
              ) : null}

              {editorPanels && windows.open.has('world') ? (
                <EditorWindow
                  title="World"
                  note={`${zones.length} zones, ${spawns.length} placed`}
                  rect={windows.layout.world}
                  onRect={(rect) => placeWindow('world', rect)}
                  onClose={() => togglePanel('world')}
                  className="outliner"
                  {...stacked('world')}
                >
                  <EditorOutliner
                    zones={zones}
                    spawns={spawns}
                    characters={catalogue.characters}
                    // Where the camera is looking, which in the tools is often
                    // nowhere near the character. Near is measured from there.
                    at={viewTile ?? characterTile ?? { tx: 0, tz: 0 }}
                    problems={problems}
                    problemCounts={problemCounts}
                    census={census}
                    onCount={takeCensus}
                    onStepTo={stepToScenery}
                    onCheck={() => connectionRef.current?.sendAdminCommand('checkWorld', {})}
                    onTidy={() => connectionRef.current?.sendAdminCommand('tidyWorld', {})}
                    onGo={(tx, tz) => {
                      // Looking at it is half of it: what somebody wants next is
                      // whatever is there, in the pane, ready to be changed.
                      gameRef.current?.lookAt(tx, tz);
                      const region = gameRef.current?.state.region;
                      const standing = (region?.objects ?? [])
                        .filter(
                          (object) =>
                            region !== null &&
                            region !== undefined &&
                            region.originX + object.tx === tx &&
                            region.originZ + object.tz === tz,
                        )
                        .map<Pick>((object) => ({
                          kind: 'object',
                          tx,
                          tz,
                          objectId: object.id,
                          rotation: object.rotation,
                          scale: object.scale,
                        }));

                      const covering = zonesOn(zones, tx, tz).map<Pick>((zone) => ({
                        kind: 'zone',
                        zoneId: zone.id,
                      }));

                      setSelection(
                        selectionOf([...standing, ...covering, { kind: 'tile', tx, tz }]),
                      );
                      connectionRef.current?.sendAdminCommand('inspect', { tx, tz });
                    }}
                    selectedZone={
                      selection.picks[selection.active]?.kind === 'zone'
                        ? (selection.picks[selection.active] as { zoneId: number }).zoneId
                        : null
                    }
                    onSelectZone={(zone) => {
                      setSelection(selectionOf([{ kind: 'zone', zoneId: zone.id }]));

                      const first = zone.tiles[0];
                      if (first) {
                        const tx = Math.round(
                          zone.tiles.reduce((total, tile) => total + tile.tx, 0) /
                            zone.tiles.length,
                        );
                        const tz = Math.round(
                          zone.tiles.reduce((total, tile) => total + tile.tz, 0) /
                            zone.tiles.length,
                        );
                        gameRef.current?.lookAt(tx, tz);
                      }

                      setMode('spawns');
                    }}
                    onSelectSpawn={(spawn) => {
                      setSelection(
                        selectionOf([
                          { kind: 'character', entityId: 0, tx: spawn.tx, tz: spawn.tz },
                        ]),
                      );
                      connectionRef.current?.sendAdminCommand('inspect', {
                        tx: spawn.tx,
                        tz: spawn.tz,
                      });
                      connectionRef.current?.sendAdminCommand('inspectDrops', { npc: spawn.npcId });
                      connectionRef.current?.sendAdminCommand('inspectShop', { npc: spawn.npcId });
                      gameRef.current?.lookAt(spawn.tx, spawn.tz);
                    }}
                  />
                </EditorWindow>
              ) : null}

              {editorPanels && windows.open.has('map') ? (
                <EditorWindow
                  title="Map"
                  note="click to go"
                  rect={windows.layout.map}
                  onRect={(rect) => placeWindow('map', rect)}
                  onClose={() => togglePanel('map')}
                  className="map"
                  {...stacked('map')}
                >
                  <EditorMap
                    region={regionForMap}
                    zones={zones}
                    spawns={spawns}
                    character={characterTile}
                    focus={panned ? viewTile : null}
                    held={heldTiles}
                    // The ground is painted again when something is built or
                    // taken away, and otherwise kept: painting a continent is
                    // four million pixels, and it was being done once a second
                    // because the character had moved.
                    repaint={world.objects}
                    onGo={(tx, tz) => {
                      gameRef.current?.lookAt(tx, tz);
                    }}
                  />

                  <Places
                    here={regionForMap?.id ?? ''}
                    places={places}
                    onGo={(region) =>
                      connectionRef.current?.sendAdminCommand('goToRegion', {}, { region })
                    }
                    onMake={(made) =>
                      connectionRef.current?.sendAdminCommand(
                        'makeRegion',
                        { width: made.width, height: made.height },
                        {
                          region: made.region,
                          name: made.name,
                          underground: made.underground ? 'yes' : 'no',
                        },
                      )
                    }
                    // Where a place sits is arranged by dragging it on the map
                    // of everywhere, which is a thing somebody can see, rather
                    // than by typing two numbers that mean nothing to anybody.
                    onArrange={(region) => {
                      setArrangeOn(region ?? null);
                      setWorldMap(true);
                    }}
                    onRefresh={() => connectionRef.current?.sendAdminCommand('listRegions')}
                  />
                </EditorWindow>
              ) : null}

              {editorPanels && windows.open.has('quests') ? (
                <EditorWindow
                  title="Quests"
                  rect={windows.layout.quests}
                  onRect={(rect) => placeWindow('quests', rect)}
                  onClose={() => togglePanel('quests')}
                  className="quests"
                  {...stacked('quests')}
                >
                  <EditorQuests
                    quests={catalogue.quests}
                    progress={progress.quests}
                    items={catalogue.items}
                    characters={catalogue.characters}
                    objects={catalogue.objects}
                    named={triggers
                      .filter((one) => one.names === true && one.name.trim().length > 0)
                      .map((one) => ({ id: one.id, name: one.name }))}
                    detail={questDetail}
                    saves={questSaves}
                    openQuest={makerQuest}
                    onEditTalk={openConversation}
                    onCommand={(command, numbers, text) =>
                      connectionRef.current?.sendAdminCommand(command, numbers, text)
                    }
                  />
                </EditorWindow>
              ) : null}

              {editorPanels && windows.open.has('make') ? (
                <EditorWindow
                  title="Create"
                  rect={windows.layout.make}
                  onRect={(rect) => placeWindow('make', rect)}
                  onClose={() => togglePanel('make')}
                  className="make items"
                  {...stacked('make')}
                >
                  <EditorMake
                    modelUsers={modelUsers}
                    groups={groups}
                    recipes={catalogue.recipes}
                    yields={catalogue.yields}
                    groupArea={
                      selection.picks.find((pick) => pick.kind === 'area')?.area ?? lastArea
                    }
                    onEditItem={openItemEditor}
                    poses={poseRows}
                    clips={clipsLoaded()}
                    objects={catalogue.objects}
                    items={catalogue.items}
                    itemList={itemList}
                    itemDetail={itemDetail}
                    itemSaves={itemSaves}
                    characterList={characterList}
                    characterDetail={characterDetail}
                    characterSaves={characterSaves}
                    models={catalogue.models}
                    onUploadModel={uploadModel}
                    onUploadSound={uploadSound}
                    onFitModel={(model) => {
                      const game = gameRef.current;
                      if (!game) return;

                      const at = game.state.local.pos;
                      game.scene.showFittedModel(
                        model ? { x: at.x, z: at.z, plane: at.plane } : null,
                        model,
                      );
                    }}
                    quests={catalogue.quests}
                    characters={catalogue.characters}
                    shops={catalogue.shops}
                    triggers={triggers}
                    triggerArea={heldArea}
                    onGo={(tx, tz) => gameRef.current?.lookAt(tx, tz)}
                    openOn={makerTab}
                    openThing={makerThing}
                    spellbook={spellbook}
                    onTalk={openConversation}
                    onCommand={(command, numbers, text) =>
                      connectionRef.current?.sendAdminCommand(command, numbers, text)
                    }
                  />
                </EditorWindow>
              ) : null}

              {/* Writing one item, in a window of its own. Beside the list
                  rather than instead of it, so a better sword can be written
                  while the worse one is being read. */}
              {editorPanels && windows.open.has('item') ? (
                <EditorWindow
                  title="Edit Item"
                  note={itemDraft ? itemDraft.name || 'unnamed' : 'nothing'}
                  rect={windows.layout.item}
                  onRect={(rect) => placeWindow('item', rect)}
                  onClose={() => {
                    saveItem(itemRef.current);
                    togglePanel('item');
                  }}
                  className="item"
                  {...stacked('item')}
                >
                  {itemDraft === null ? (
                    <p className="hint">
                      Nothing open. Choose an item in the Items list and press Edit, or make a new
                      one.
                    </p>
                  ) : (
                    <ItemForm
                      draft={itemDraft}
                      items={catalogue.items}
                      objects={catalogue.objects}
                      quests={catalogue.quests}
                      characters={catalogue.characters}
                      shops={catalogue.shops}
                      recipes={catalogue.recipes}
                      saying={itemSaying}
                      onChange={setItemDraft}
                      onSave={() => saveItem(itemDraft)}
                      onCancel={() => {
                        // Closing writes it down rather than throwing it away:
                        // backing out of a form is not the same as meaning to
                        // lose what was typed into it.
                        saveItem(itemDraft);
                        setItemDraft(null);
                        setItemSaying('');
                        togglePanel('item');
                      }}
                    />
                  )}
                </EditorWindow>
              ) : null}

              {editorPanels && windows.open.has('talk') ? (
                <EditorWindow
                  title="Dialogue"
                  // Whose conversation is open, or nothing at all: a title bar
                  // reading "Dialogue nobody" is a sentence about no one.
                  note={
                    catalogue.characters.find((one) => one.id === talkingAbout)?.name ?? undefined
                  }
                  rect={windows.layout.talk}
                  onRect={(rect) => placeWindow('talk', rect)}
                  onClose={() => togglePanel('talk')}
                  className="talk"
                  {...stacked('talk')}
                >
                  {talkingAbout === null ? (
                    /* Whoever is in front of you, rather than an instruction to
                       go and find a list. Somebody who opens this has almost
                       always just been looking at the character they want. */
                    <DialogueNearby
                      characters={catalogue.characters}
                      nearby={nearbyCharacters}
                      /* Through the same door everything else opens a
                         conversation by. Picking one out of this list used to
                         only say who was chosen, and never asked the world for
                         what they say, so the canvas came up empty and the only
                         way to see anybody's conversation was to find them in
                         the world instead. */
                      onOpen={openConversation}
                    />
                  ) : (
                    <Conversation
                      npcId={talkingAbout}
                      npcName={
                        catalogue.characters.find((one) => one.id === talkingAbout)?.name ??
                        `Character ${talkingAbout}`
                      }
                      lines={talkLines}
                      items={catalogue.items}
                      quests={catalogue.quests}
                      shops={catalogue.shops}
                      characters={catalogue.characters}
                      objects={catalogue.objects}
                      onLeave={() => setTalkingAbout(null)}
                      onEditQuest={(quest) => {
                        setWindows((held) => {
                          if (held.open.has('quests')) return held;

                          const open = new Set(held.open).add('quests');
                          const layout = {
                            ...held.layout,
                            quests: clearOf(held.layout, 'quests', held.open),
                          };

                          saveLayout(layout, open, held.compact);
                          return { ...held, layout, open };
                        });

                        setMakerQuest(quest);
                      }}
                      onCommand={(command, numbers, text) => {
                        connectionRef.current?.sendAdminCommand(command, numbers, text);

                        // Saving a conversation sends the whole definition back,
                        // so the canvas is asked for it again rather than left
                        // drawing what it had before the save.
                        if (command === 'setDialogue' && talkingAbout !== null) {
                          window.setTimeout(
                            () =>
                              connectionRef.current?.sendAdminCommand('characterDetails', {
                                npc: talkingAbout,
                              }),
                            120,
                          );
                        }
                      }}
                    />
                  )}
                </EditorWindow>
              ) : null}

              {editorPanels && windows.open.has('keys') ? (
                <EditorWindow
                  title="Keys"
                  rect={windows.layout.keys}
                  onRect={(rect) => placeWindow('keys', rect)}
                  onClose={() => togglePanel('keys')}
                  className="keys"
                  {...stacked('keys')}
                >
                  <EditorKeys />
                </EditorWindow>
              ) : null}
            </>
          ) : null}

          {exchange ? (
            <Exchange
              state={exchange}
              items={catalogue.items}
              describe={(itemId) => gameRef.current?.definitions.item(itemId)}
              onOffer={(slot, itemId, selling, price, quantity) =>
                connectionRef.current?.sendExchange({
                  act: ExchangeAct.Offer,
                  slot,
                  itemId,
                  selling,
                  price,
                  quantity,
                })
              }
              onAsk={(itemId) =>
                connectionRef.current?.sendExchange({
                  act: ExchangeAct.Open,
                  slot: 0,
                  itemId,
                  selling: false,
                  price: 0,
                  quantity: 0,
                })
              }
              onCancel={(slot) =>
                connectionRef.current?.sendExchange({
                  act: ExchangeAct.Cancel,
                  slot,
                  itemId: 0,
                  selling: false,
                  price: 0,
                  quantity: 0,
                })
              }
              onCollect={(slot) =>
                connectionRef.current?.sendExchange({
                  act: ExchangeAct.Collect,
                  slot,
                  itemId: 0,
                  selling: false,
                  price: 0,
                  quantity: 0,
                })
              }
              onClose={() => {
                setExchange(null);
                connectionRef.current?.sendExchange({
                  act: ExchangeAct.Close,
                  slot: 0,
                  itemId: 0,
                  selling: false,
                  price: 0,
                  quantity: 0,
                });
              }}
            />
          ) : null}

          {collectionOpen ? (
            <CollectionLog
              log={collection}
              describe={(itemId) => gameRef.current?.definitions.item(itemId)}
              onClose={() => setCollectionOpen(false)}
            />
          ) : null}

          {boardsFor !== null ? (
            <Leaderboards me={boardsFor} onClose={() => setBoardsFor(null)} />
          ) : null}

          {allSettingsOpen ? (
            <AllSettings
              settings={settings}
              update={update}
              bindings={bindings}
              onBind={bind}
              onClose={() => setAllSettingsOpen(false)}
            />
          ) : null}

          {/* Trading. While it is up, everything else that is not the bag or the
              chat is hidden: a trade is a conversation about two lists, and a
              screen with six other windows on it is a screen somebody can be
              made to press the wrong thing on. */}
          {trade ? (
            <TradeWindow
              state={trade}
              mine={offer}
              theirs={theirOffer}
              describe={(itemId) => gameRef.current?.definitions.item(itemId)}
              onAct={(act, slot) => connectionRef.current?.sendTrade(act, '', slot ?? 0, 0)}
            />
          ) : null}

          {/* The spell book and the faith panel, windows of their own like the kit. */}
          {spellsOpen && !editorPanels ? (
            <SpellsPanel
              spells={spellbook.spells}
              magicLevel={levels[Skill.Magic] ?? 1}
              mana={mana.current}
              autocast={devotion?.autocast ?? 0}
              carried={(itemId) =>
                inventory.reduce(
                  (many, stack) => many + (stack?.id === itemId ? stack.quantity : 0),
                  0,
                )
              }
              itemName={(itemId) => gameRef.current?.definitions.item(itemId)?.name ?? 'Something'}
              onCast={castFromBook}
              onAutocast={(spellId) => connectionRef.current?.sendAutocast(spellId)}
              element={fightingWay === 2 ? stance : 0}
              onMenu={(spell, x, y) =>
                setMenu({
                  screenX: x,
                  screenY: y,
                  title: spell.name,
                  entries: [
                    { label: `Cast ${spell.name}`, run: () => castFromBook(spell) },
                    ...(spell.autocast
                      ? [
                          devotion?.autocast === spell.id
                            ? {
                                label: 'Stop autocasting',
                                run: () => connectionRef.current?.sendAutocast(0),
                              }
                            : {
                                label: 'Autocast',
                                run: () => connectionRef.current?.sendAutocast(spell.id),
                              },
                        ]
                      : []),
                  ],
                })
              }
              onHover={touch ? undefined : setHoverTip}
              onClose={() => setSpellsOpen(false)}
            />
          ) : null}

          {faithOpen && !editorPanels ? (
            <FaithPanel
              prayers={spellbook.prayers}
              faithLevel={levels[Skill.Faith] ?? 1}
              faith={(devotion?.faith ?? 0) / 10}
              maxFaith={Math.round((devotion?.maxFaith ?? 0) / 10)}
              held={new Set(devotion?.prayers ?? [])}
              onToggle={(prayer, on) => connectionRef.current?.sendPrayer(prayer.id, on)}
              onHover={touch ? undefined : setHoverTip}
              onClose={() => setFaithOpen(false)}
            />
          ) : null}

          {bankOpen ? (
            <Bank
              slots={bank}
              size={BANK_SIZE}
              onHover={touch ? undefined : setHoveredItem}
              onDepositBag={() => {
                // Every slot, back to front: taking from the front would shuffle
                // what has not been taken yet under the slot being read.
                for (let slot = inventory.length - 1; slot >= 0; slot--) {
                  if (inventory[slot]) connectionRef.current?.sendBankMove(true, slot, 0);
                }
              }}
              onDepositWorn={() => {
                for (let slot = worn.length - 1; slot >= 0; slot--) {
                  if (worn[slot]) connectionRef.current?.sendBankMove(true, slot, 0, { kit: true });
                }
              }}
              describe={(itemId) => gameRef.current?.definitions.item(itemId)}
              onWithdraw={(slot) => connectionRef.current?.sendBankMove(false, slot, 1)}
              onMenu={(slot, x, y) => gameRef.current?.openItemMenu(x, y, ContainerId.Bank, slot)}
              onClose={() => connectionRef.current?.sendCloseInterface()}
            />
          ) : null}

          {shop ? (
            <Shop
              name={shop.name}
              entries={shop.entries}
              describe={(itemId) => gameRef.current?.definitions.item(itemId)}
              onBuy={(slot, quantity) => connectionRef.current?.sendShopTrade(true, slot, quantity)}
              onClose={() => connectionRef.current?.sendCloseInterface()}
            />
          ) : null}

          {/* The kit stays up beside the bank: swapping armour is exactly what
              somebody at a bank is doing, and closing it to do that is a rule
              with nothing behind it. A shop still takes the room, since a shop
              window and a kit window would be fighting for the same corner. */}
          {gearOpen &&
          !editorPanels &&
          !(touch && tab) &&
          !shop &&
          (!gearAttached || settings.floatingPanels) ? (
            <Equipment
              slots={worn}
              toolbelt={toolbelt}
              onBeltMenu={(slot, x, y) =>
                gameRef.current?.openItemMenu(x, y, ContainerId.Toolbelt, slot)
              }
              bonuses={bonuses}
              combatLevel={combatLevel}
              onHoverText={setHoverTip}
              describe={(itemId) => gameRef.current?.definitions.item(itemId)}
              onRemove={(slot) => {
                // At a bank, taking something off puts it in the bank rather
                // than in the bag: that is what somebody standing at one means.
                if (bankOpen) connectionRef.current?.sendBankMove(true, slot, 0, { kit: true });
                else connectionRef.current?.sendUnequip(slot);
              }}
              onMenu={(slot, x, y) =>
                gameRef.current?.openItemMenu(x, y, ContainerId.Equipment, slot)
              }
              onHover={touch ? undefined : setHoveredItem}
              onClose={() => setGearOpen(false)}
              onDetach={settings.floatingPanels ? undefined : detachGear}
            />
          ) : null}

          {/* Watched while moving rather than while stopped, so they live by the
              interface buttons and can be dragged wherever they are wanted. */}
          {editorPanels ? null : (
            <>
              <RunMeter
                energy={energy}
                max={MAX_RUN_ENERGY}
                running={running}
                onToggle={toggleRun}
                onMenu={setMenu}
              />
              {health.max > 0 ? (
                <HealthMeter health={health.current} max={health.max} onMenu={setMenu} />
              ) : null}
              {/* Only once there is any. Somebody who has never cast anything
                  does not need a gauge telling them so. */}
              {mana.max > 0 ? (
                <ManaMeter mana={mana.current} max={mana.max} onMenu={setMenu} />
              ) : null}
              {devotion && devotion.maxFaith > 0 ? (
                <FaithMeter
                  faith={devotion.faith / 10}
                  max={Math.round(devotion.maxFaith / 10)}
                  onMenu={setMenu}
                />
              ) : null}
            </>
          )}

          {/* Panels that float: as many as somebody wants, wherever they put them.
              Put away while the editor holds the world, the same as the docked
              ones are: a bag and a skill list over a landscape being built are
              in the way, and floating them was making them the one layout that
              did not know that. Which ones were open is remembered, so they come
              back where they were when the editor closes. */}
          {settings.floatingPanels && !editorPanels
            ? (trade ? (['inventory'] as const) : floating).map((name) => (
                <FloatingPanel
                  key={name}
                  kind={name}
                  title={TAB_LABELS[name]}
                  depth={40 + Math.max(0, panelStacking.indexOf(name))}
                  onRaise={() => raisePanel(name)}
                  note={
                    name === 'inventory'
                      ? `${inventory.filter((slot) => slot !== null).length}/${inventory.length}`
                      : undefined
                  }
                  // Each one opened steps up and to the left of the one before,
                  // so opening five panels shows five title bars rather than a
                  // pile with only the top one visible.
                  home={(size) => {
                    const step = Math.max(0, floating.indexOf(name)) * 28;
                    return {
                      x: Math.max(12, window.innerWidth - size.width - 24 - step),
                      y: Math.max(12, window.innerHeight - size.height - 90 - step),
                    };
                  }}
                  // The bag cannot be shut in the middle of a trade: it is where
                  // the goods come from.
                  onClose={
                    trade
                      ? undefined
                      : () => setFloating((held) => held.filter((entry) => entry !== name))
                  }
                >
                  {panelBody(name)}
                </FloatingPanel>
              ))
            : null}

          <div className="dock">
            {touch && gearOpen && (settings.floatingPanels ? floating.length > 0 : tab) ? (
              <div className="panel-card">
                <div className="panel-head">
                  <span>Equipment</span>
                </div>
                <p className="hint">Close the open tab to see your equipment.</p>
              </div>
            ) : null}

            {!settings.floatingPanels && dockTab ? (
              <div className="dock-row">
                {/* The kit slides out beside the bag rather than covering the
                    screen, which is the layout everybody already knows. It can
                    be taken off and put anywhere, and put back again. */}
                {gearOpen && gearAttached && !shop && !(touch && tab) ? (
                  <Equipment
                    attached
                    slots={worn}
                    toolbelt={toolbelt}
                    onBeltMenu={(slot, x, y) =>
                      gameRef.current?.openItemMenu(x, y, ContainerId.Toolbelt, slot)
                    }
                    bonuses={bonuses}
                    combatLevel={combatLevel}
                    onHoverText={setHoverTip}
                    describe={(itemId) => gameRef.current?.definitions.item(itemId)}
                    onRemove={(slot) => {
                      if (bankOpen)
                        connectionRef.current?.sendBankMove(true, slot, 0, { kit: true });
                      else connectionRef.current?.sendUnequip(slot);
                    }}
                    onMenu={(slot, x, y) =>
                      gameRef.current?.openItemMenu(x, y, ContainerId.Equipment, slot)
                    }
                    onHover={touch ? undefined : setHoveredItem}
                    onClose={() => setGearOpen(false)}
                    onDetach={detachGear}
                  />
                ) : null}

                <div className="panel-card">
                  <div className="panel-head">
                    <span>{TAB_LABELS[dockTab]}</span>
                    {dockTab === 'inventory' ? (
                      <span className="panel-count">
                        {inventory.filter((slot) => slot !== null).length}/{inventory.length}
                      </span>
                    ) : null}
                  </div>
                  {panelBody(dockTab)}
                </div>
              </div>
            ) : null}

            <TabStrip
              active={settings.floatingPanels ? null : dockTab}
              open={settings.floatingPanels ? floating : undefined}
              gearOpen={gearOpen}
              onSelect={(next) => {
                panelSound(next);
                if (!settings.floatingPanels) {
                  setTab((current) => (current === next ? null : next));
                  return;
                }

                raisePanel(next);
                setFloating((held) =>
                  held.includes(next) ? held.filter((name) => name !== next) : [...held, next],
                );
              }}
              onToggleGear={() => setGearOpen(!gearOpen)}
              spellsOpen={spellsOpen}
              faithOpen={faithOpen}
              onToggleSpells={() => setSpellsOpen(!spellsOpen)}
              onToggleFaith={() => setFaithOpen(!faithOpen)}
            />
          </div>

          {systemOpen ? (
            <SystemMenu
              canUseTools={atLeast(role, Role.Moderator)}
              canEditWorld={atLeast(role, Role.SuperAdmin)}
              toolsOpen={adminOpen}
              editing={editing}
              onTools={(named) => {
                // Named means somebody asked for that part of the tools, so it
                // opens there. Unnamed is the way out again.
                setAdminOpen(named !== undefined ? true : !adminOpen);
                if (named !== undefined) setAdminSection(named);
                setSystemOpen(false);
              }}
              onEditing={openEditor}
              onExchange={() => {
                connectionRef.current?.sendExchange({
                  act: ExchangeAct.Open,
                  slot: 0,
                  itemId: 0,
                  selling: false,
                  price: 0,
                  quantity: 0,
                });
                setSystemOpen(false);
              }}
              onCollection={() => {
                setCollectionOpen(true);
                setSystemOpen(false);
              }}
              onBoards={() => {
                setBoardsFor(myName);
                setSystemOpen(false);
              }}
              onSettings={() => {
                setAllSettingsOpen(true);
                setSystemOpen(false);
              }}
              onLogOut={() => {
                setSystemOpen(false);
                onLeave();
              }}
              onClose={() => setSystemOpen(false)}
            />
          ) : null}

          {/* What the pointer is resting on, said in our own words rather than the
              browser's, and only after somebody has rested on it. */}
          <Tooltip content={hoverTip} delay={settings.tooltipDelay} />

          {crafting ? (
            <Crafting
              station={crafting}
              recipes={recipesAt(catalogue.recipes, crafting.objectId)}
              bag={inventory}
              items={catalogue.items}
              levels={skillLevels}
              progress={progress}
              chosen={stations[crafting.objectId]}
              onCraft={(action, count, outputId) =>
                connectionRef.current?.sendCraft(
                  crafting.objectId,
                  { tx: crafting.tx, tz: crafting.tz, plane: crafting.plane },
                  action,
                  count,
                  outputId,
                )
              }
              onDefault={(held) => {
                setStations((current) => {
                  const next = { ...current };
                  if (held) next[crafting.objectId] = held;
                  else delete next[crafting.objectId];

                  saveDefaults(next);
                  return next;
                });
              }}
              onClose={() => setCrafting(null)}
            />
          ) : null}

          {talking ? (
            <Dialogue
              packet={talking}
              onChoose={(option) => connectionRef.current?.sendDialogueChoice(option)}
              onClose={() => {
                connectionRef.current?.sendDialogueChoice(255);
                setTalking(null);
              }}
            />
          ) : null}

          {touch ? (
            <TouchButtons
              running={running}
              chatOpen={chatOpen}
              onToggleRun={toggleRun}
              onOpenChat={() => {
                setWhisperTo(null);
                setChatIdle(false);
                setChatOpen(true);
              }}
            />
          ) : null}

          <div
            className={chatIdle ? 'chat idle' : 'chat'}
            onPointerEnter={() => setChatIdle(false)}
          >
            {/* One kind of talk at a time, or all of it colour coded. A tab with
                something unread on it says so, and stops saying so the moment it
                is looked at. */}
            <div className="chat-tabs">
              {CHAT_TABS.map((one) => {
                // Nothing about guilds is shown until there is a guild to show:
                // a tab that can never have anything in it is a tab that teaches
                // people to stop reading the row.
                if (one.name === 'guild' && !inAGuild) return null;

                return (
                  <button
                    type="button"
                    key={one.name}
                    className={chatTab === one.name ? 'on' : ''}
                    onClick={() => {
                      setChatTab(one.name);
                      setUnread((held) => ({ ...held, [one.name]: false }));
                      setChatIdle(false);
                    }}
                  >
                    {one.label}
                    {unread[one.name] && chatTab !== one.name ? (
                      <span className="chat-unread" aria-label="unread" />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* And which conversation, when there is more than one. */}
            {chatTab === 'private' && conversations.length > 0 ? (
              <div className="chat-tabs chat-with">
                <button
                  type="button"
                  className={talkingWith === null ? 'on' : ''}
                  onClick={() => setTalkingWith(null)}
                >
                  Everyone
                </button>
                {conversations.map((who) => (
                  <button
                    type="button"
                    key={who}
                    className={talkingWith === who ? 'on' : ''}
                    onClick={() => {
                      setTalkingWith(who);
                      setWhisperTo(who);
                    }}
                  >
                    {who}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="chat-log" ref={logRef}>
              {showingLines.map((line) => (
                <div key={line.id} className={SCOPE_CLASS[line.scope]}>
                  {line.act ? (
                    /* A line that does something when it is pressed: a trade
                       request, so far. Its own colour, because a line that can
                       be clicked has to look unlike the ones that cannot. */
                    <button
                      type="button"
                      className="chat-act"
                      title={line.act.label}
                      onClick={line.act.run}
                    >
                      {line.text}
                    </button>
                  ) : line.who ? (
                    <>
                      <span className="who">{line.who}:</span> {line.text}
                    </>
                  ) : (
                    <span className="system">{line.text}</span>
                  )}
                </div>
              ))}
            </div>
            {chatOpen ? (
              <form onSubmit={sendChat}>
                <input
                  ref={chatInputRef}
                  className="chat-input"
                  type="text"
                  maxLength={120}
                  placeholder={whisperTo ? `Message ${whisperTo}` : 'Say something'}
                  onBlur={() => {
                    setChatOpen(false);
                    setWhisperTo(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      setChatOpen(false);
                      setWhisperTo(null);
                    }
                  }}
                />
              </form>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
